import axios from "axios";
import debugData from "../data/debugData.json";
import { unifiedLogout, isUnauthorizedLogoutSuppressed } from "../services/logoutService";
import { translateApiPayload } from "../utils/translateApiError";
import { getUserFacingApiMessage } from "../utils/getUserFacingApiMessage";
import i18n from "../i18n/config";
import {
  getSelectedCompanyIds,
  readActiveProjectId,
  normalizeWorkspaceSlug,
  getActiveWorkspaceSlug,
  setActiveWorkspaceDatabase,
  readWorkspaceSlugFromUrl,
} from "../utils/workspaceStorage";
import { getPartnerDashboardAiContext, isPartnerAssistantContext } from "../utils/partnerPortal";
import { getApiBaseURL } from "./apiBaseUrl";
import { isLikelyJwt, readJwtCompanyClaim, readJwtScope } from "./clientJwt";
import { shouldRejoinVirtualOfficePresence } from "../utils/virtualOfficePresenceRejoin";

import {
  readTabBearerToken,
  writeTabBearerToken,
  clearTabBearerToken,
  readTabUserDataRaw,
  readTabAuthWorkspaceSlug,
  clearTabAuthStorage,
} from "../utils/tabAuthStorage";

export { isLikelyJwt } from "./clientJwt";

const DEBUG_MODE = false;

const apiClient = axios.create({
  baseURL: getApiBaseURL(),
  timeout: 50000,
  withCredentials: false,
});

function readStoredBearerToken() {
  return readTabBearerToken();
}

let bearerToken = readStoredBearerToken();

/** Espelho do slug do workspace (schema MySQL); usado se `company` foi sobrescrito por id_projeto por engano. */
const WORKSPACE_DATABASE_MIRROR_KEY = "xproWorkspaceDatabase";

/**
 * Header HTTP Company = nome do banco/schema do tenant (slug), nunca id_projeto.
 * Prioridade: `?ws=` na URL desta aba → pin sessionStorage → localStorage.
 */
function getWorkspaceDatabaseHeaderValue() {
  try {
    const fromUrl = readWorkspaceSlugFromUrl();
    if (fromUrl) {
      // Sync pin (+ espelho local desta aba) para requests e UI estáveis após F5.
      setActiveWorkspaceDatabase(fromUrl);
      return fromUrl;
    }
    const tabSlug = getActiveWorkspaceSlug();
    if (tabSlug) {
      try {
        if (typeof localStorage !== "undefined") {
          localStorage.setItem(WORKSPACE_DATABASE_MIRROR_KEY, tabSlug);
        }
      } catch {
        /* ignore */
      }
      return tabSlug;
    }
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem("company");
    const s = raw != null ? String(raw).trim() : "";
    if (!s) return null;
    if (/^\d+$/.test(s)) {
      const fb = localStorage.getItem(WORKSPACE_DATABASE_MIRROR_KEY);
      return normalizeWorkspaceSlug(fb);
    }
    return normalizeWorkspaceSlug(s);
  } catch {
    return null;
  }
}

/**
 * Lê os projetos selecionados em cada requisição (escopo por workspace no sessionStorage).
 */
function getSelectedProjectIdsForRequest() {
  // Em escopos "partner" / "platform" (console/portal), não anexar ids_projetos,
  // pois isso dispara validação de projetos no tenant e gera 403 em massa.
  try {
    const udRaw = readTabUserDataRaw();
    if (udRaw) {
      const ud = JSON.parse(udRaw);
      const scope = ud?.scope;
      if (scope === "partner" || scope === "platform") return null;
    }
  } catch {
    /* ignore */
  }

  try {
    const parsed = getSelectedCompanyIds();
    if (Array.isArray(parsed) && parsed.length > 0) {
      return parsed;
    }
    if (Array.isArray(parsed) && parsed.length === 0) {
      console.warn("IDs de empresas inválidos ou vazios.");
    }
  } catch (error) {
    console.error("Erro ao processar IDs de empresas do workspace:", error);
  }
  try {
    const ap = readActiveProjectId();
    if (ap) return [ap];
  } catch {
    /* ignore */
  }
  try {
    const company =
      typeof localStorage !== "undefined" ? localStorage.getItem("company") : null;
    if (company && /^\d+$/.test(String(company).trim())) {
      const n = parseInt(String(company), 10);
      if (Number.isFinite(n) && n > 0) return [n];
    }
  } catch {
    /* ignore */
  }
  return null;
}

// Interceptador de request
apiClient.interceptors.request.use(
  (config) => {
    config.headers["X-Xpro-Client"] = "browser";

    const urlStr = config.url != null ? String(config.url) : "";
    const isPublicSignupFlow =
      urlStr.includes("public/signup") ||
      urlStr.includes("public/verify-email") ||
      urlStr.includes("public/resend-signup-verification") ||
      urlStr.includes("public/plan-catalog") ||
      urlStr.includes("public/dashboard/presentations");
    const isPartnerBillingApi = urlStr.includes("/billing/partner/");
    // Console plataforma (JWT scope=platform): não enviar Company de tenant residual —
    // senão a API autentica no schema errado e devolve 403 em workspaces já liberados.
    const isPlatformBillingAdminApi =
      urlStr.includes("/billing/admin/") &&
      isLikelyJwt(bearerToken) &&
      readJwtScope(bearerToken) === "platform";
    const skipCompanyHeader =
      Boolean(config.skipCompanyHeader) || isPartnerBillingApi || isPlatformBillingAdminApi;
    if (skipCompanyHeader && config.headers) {
      if (typeof config.headers.delete === "function") {
        config.headers.delete("Company");
        config.headers.delete("company");
      } else {
        delete config.headers.Company;
        delete config.headers.company;
      }
    }
    const presetCompany = config.headers?.Company;
    const companyHeader = skipCompanyHeader
      ? null
      : presetCompany || getWorkspaceDatabaseHeaderValue();
    if (companyHeader && !isPublicSignupFlow) {
      config.headers.Company = companyHeader;
    }
    if (isPublicSignupFlow) {
      config.headers.Company = "__signup__";
    }

    let attachBearer = isLikelyJwt(bearerToken) && !config.skipAuthHeader;
    if (attachBearer && companyHeader && !isPublicSignupFlow) {
      const scope = readJwtScope(bearerToken);
      if (scope === "workspace" || scope == null) {
        const claimCompany = readJwtCompanyClaim(bearerToken);
        const boundSlug = readTabAuthWorkspaceSlug();
        const expected = normalizeWorkspaceSlug(claimCompany || boundSlug);
        const active = normalizeWorkspaceSlug(companyHeader);
        if (expected && active && expected !== active) {
          // Token de outro tenant — não enviar Bearer (evita colisão de id no servidor).
          attachBearer = false;
          try {
            clearTabAuthStorage();
            bearerToken = null;
          } catch {
            /* ignore */
          }
          if (typeof window !== "undefined" && !config.skipWorkspaceMismatchRedirect) {
            const loginQs = active ? `?c=${encodeURIComponent(active)}` : "";
            const path = String(window.location?.pathname || "");
            if (!path.startsWith("/login")) {
              window.location.assign(`/login${loginQs}`);
            }
          }
        }
      }
    }
    if (attachBearer) {
      config.headers.Authorization = `Bearer ${bearerToken}`;
    }

    const companyIds = getSelectedProjectIdsForRequest();
    if (companyIds && companyIds.length > 0) {
      if (config.method?.toUpperCase() === "GET") {
        const urlAlreadyHasIdsProjetos =
          config.url && String(config.url).includes("ids_projetos");
        // Não sobrescreve se a requisição já enviar ids_projetos na URL ou em params
        // (ex.: busca de produtos relacionados do mesmo projeto do produto em edição)
        if (!urlAlreadyHasIdsProjetos) {
          const params = new URLSearchParams(config.params || {});
          if (!params.has("ids_projetos")) {
            params.set("ids_projetos", companyIds.join(","));
          }
          config.params = params;
        }
      } else if (["POST", "PUT"].includes(config.method?.toUpperCase())) {
        const body = config.data ? { ...config.data } : {};
        // Telefonia (PABX) não usa ids_projetos; manter payload igual ao esperado pelo backend
        const isTelephony = config.url && String(config.url).includes("/telephony/");
        // Produtos: PUT /products/:id não deve receber ids_projetos do contexto automaticamente,
        // pois isso pode carregar múltiplos projetos e o backend rejeita (produto é 1-por-1).
        // Se alguém precisar alterar `ids_projetos` num update de produto, deve enviar explicitamente no body.
        const urlPath = config.url != null ? String(config.url) : "";
        const isProductUpdateById =
          config.method?.toUpperCase() === "PUT" && /^\/products\/\d+\/?$/.test(urlPath);

        if (!isTelephony && !isProductUpdateById && body.ids_projetos == null) {
          body.ids_projetos = companyIds;
        }
        config.data = body;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Endpoints de autenticação: 401 não deve disparar logout no próprio login (a tela mostra o erro).
// /refresh-token fica a cargo do AuthContext (timeouts durante ops longas não devem deslogar).
const isAuthEndpoint = (config) => {
  const url = config?.url ?? "";
  return (
    /^\/login\/?$/.test(url) ||
    /^\/biometric-login\/?$/.test(url) ||
    /^\/refresh-token\/?$/.test(url) ||
    /public\/signup/.test(url) ||
    /public\/verify-email/.test(url) ||
    /public\/resend-signup-verification/.test(url) ||
    /\/users\/(platform|partner)\/send-reset/.test(url) ||
    /\/users\/send-reset/.test(url) ||
    /\/users\/(platform|partner)\/(validate-password-token|complete-password)/.test(url) ||
    /\/users\/(validate-password-token|complete-password)/.test(url) ||
    /\/users\/me\/password/.test(url)
  );
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const isLoginAttempt = isAuthEndpoint(error.config || {});

    const authErrorCode =
      typeof error.response?.data?.code === "string" ? error.response.data.code : null;
    const sessionInvalid =
      status === 401 ||
      (status === 403 &&
        (authErrorCode === "errors.auth.token_invalid" ||
          authErrorCode === "errors.auth.company_mismatch" ||
          authErrorCode === "errors.auth.identity_mismatch" ||
          authErrorCode === "errors.auth.session_revoked"));
    // 403 genérico costuma ser “proibido nesta ação” com sessão válida — não deslogar nesses casos.
    if (sessionInvalid && !isLoginAttempt && !isUnauthorizedLogoutSuppressed()) {
      console.error("Sessão expirada/não autorizada. Efetuando logout unificado…");
      await unifiedLogout("unauthorized");
    }

    if (status === 402) {
      const body = error.response?.data;
      const code = typeof body?.code === "string" ? body.code : null;
      const fromBilling = translateApiPayload({ code, params: body?.params });
      const fallbackMsg =
        (typeof body?.message === "string" && body.message) ||
        i18n.t("billingErrors.generic", { ns: "app" });
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("xpro:billing-limit-hit", {
            detail: {
              code,
              message: fromBilling || fallbackMsg,
              raw: body || null,
            },
          })
        );
      }
      return Promise.reject({
        message: fromBilling || fallbackMsg,
        status: 402,
        code,
        response: error.response,
      });
    }

    // 403 (proibido) e demais erros agora são propagados para a UI,
    // permitindo exibir mensagens específicas (ex.: sem permissão no projeto).
    try {
      error.userFacing = getUserFacingApiMessage(error);
    } catch {
      /* ignore */
    }
    return Promise.reject(error);
  }
);

export const setBearerToken = (token) => {
  if (!isLikelyJwt(token)) {
    bearerToken = null;
    clearTabBearerToken();
    return;
  }
  bearerToken = writeTabBearerToken(token);
};

export const clearBearerToken = () => {
  bearerToken = null;
  clearTabBearerToken();
};

export const fetchData = async (endpoint, method = "GET", body = null, options = {}) => {
  if (DEBUG_MODE) {
    console.log(`[DEBUG MODE] ${method} em ${endpoint}`);
    if (method === "GET") {
      const data = debugData[endpoint];
      if (!data) {
        throw new Error(`Endpoint ${endpoint} não encontrado em debugData.json`);
      }
      return data;
    } else {
      console.log("Simulando operação no DEBUG MODE:", {
        method,
        endpoint,
        body,
      });
      return { success: true };
    }
  }

  try {
    const config = {
      method,
      url: endpoint,
      headers: { "Content-Type": "application/json" },
    };

    if (options.timeout) {
      config.timeout = options.timeout;
    }

    if (options.company) {
      config.headers.Company = String(options.company).trim();
    }
    if (options.skipCompanyHeader) {
      config.skipCompanyHeader = true;
    }
    if (options.skipAuthHeader) {
      config.skipAuthHeader = true;
    }

    if (options.params != null && typeof options.params === "object") {
      config.params = Object.fromEntries(
        Object.entries(options.params).filter(([, v]) => v !== "" && v !== undefined && v !== null)
      );
    }

    if (body) {
      const sanitizedBody = JSON.parse(
        JSON.stringify(body, (key, value) => (value === undefined ? null : value))
      );
      config.data = sanitizedBody;
    }

    const response = await apiClient(config);
    const responseBody = response.data || {};
    // 1) Desembrulha { message, data: T }; se T vier no topo, usa responseBody.
    const envelope =
      responseBody != null &&
      responseBody.data != null &&
      typeof responseBody.data === "object"
        ? responseBody.data
        : responseBody;
    // 2) Listas (ex.: channels) vêm como { data: [], pagination }; demais como { request, client }.
    const payload = envelope;

    if (
      process.env.NODE_ENV === "development" &&
      String(endpoint).includes("field-definitions")
    ) {
      console.log("[CustomFields] fetchData unwrap", {
        endpoint,
        responseBodyKeys: Object.keys(responseBody || {}),
        envelopeKeys:
          envelope && typeof envelope === "object" && !Array.isArray(envelope)
            ? Object.keys(envelope)
            : Array.isArray(envelope)
            ? { arrayLen: envelope.length }
            : typeof envelope,
        resolvedDataIsArray: Array.isArray(payload?.data ?? payload),
        requestLen: Array.isArray(payload?.request)
          ? payload.request.length
          : Array.isArray(payload?.data ?? payload)
          ? 0
          : (payload?.request?.length ?? 0),
      });
    }

    return {
      data: payload?.data ?? payload ?? [],
      pagination: payload?.pagination ?? envelope?.pagination,
      totalInDatabase: payload?.totalInDatabase ?? envelope?.totalInDatabase,
    };
  } catch (error) {
    console.error(
      `Erro na API ${method} ${endpoint}:`,
      error.response?.data || error
    );
    const status = error.response?.status;
    const body = error.response?.data;
    if (status === 402 && body && typeof body === "object") {
      const code = typeof body.code === "string" ? body.code : null;
      const fromBilling = translateApiPayload({ code, params: body.params });
      const fallbackMsg =
        (typeof body.message === "string" && body.message) ||
        i18n.t("billingErrors.generic", { ns: "app" });
      const billingError = new Error(fromBilling || fallbackMsg);
      billingError.status = 402;
      billingError.code = code;
      throw billingError;
    }
    if (body && typeof body === "object") {
      const u = getUserFacingApiMessage(error);
      const apiError = new Error(u.message);
      Object.assign(apiError, {
        ...body,
        message: u.message,
        status: status ?? null,
        userFacingAction: u.action,
        ...(u.integrationDebug ? { integrationDebug: u.integrationDebug } : {}),
      });
      throw apiError;
    }
    throw error.response?.data || error;
  }
};

function isDashboardJobOrDeferredEnvelope(envelope) {
  if (!envelope || typeof envelope !== "object" || Array.isArray(envelope)) return false;
  const status = String(envelope.status || "");
  if (!status) return false;
  if (envelope.jobId || envelope.route) return true;
  return status === "computing" || status === "ready" || status === "failed" || status === "running";
}

/** GET dashboard com preservação de HTTP 202 (carregamento deferido). */
export const fetchDashboardDeferred = async (endpoint) => {
  const response = await apiClient({
    method: "GET",
    url: endpoint,
    headers: { "Content-Type": "application/json" },
    validateStatus: (status) => (status >= 200 && status < 300) || status === 202,
  });
  const responseBody = response.data || {};
  const envelope =
    responseBody != null &&
    responseBody.data != null &&
    typeof responseBody.data === "object"
      ? responseBody.data
      : responseBody;
  const preserveEnvelope = isDashboardJobOrDeferredEnvelope(envelope);
  const inner = preserveEnvelope ? envelope : envelope?.data ?? envelope ?? [];
  return {
    httpStatus: response.status,
    data: inner,
    pagination: preserveEnvelope ? null : envelope?.pagination ?? responseBody.pagination,
    totalInDatabase: preserveEnvelope ? null : envelope?.totalInDatabase ?? responseBody.totalInDatabase,
    message: responseBody.message,
  };
};

/** Lista jobs de relatório do dashboard em processamento para o usuário logado. */
export const fetchDashboardActiveJobs = async () => {
  const response = await apiClient({
    method: "GET",
    url: "/dashboard/jobs",
    headers: { "Content-Type": "application/json" },
  });
  const responseBody = response.data || {};
  const envelope =
    responseBody != null &&
    responseBody.data != null &&
    typeof responseBody.data === "object"
      ? responseBody.data
      : responseBody;
  const jobs = Array.isArray(envelope?.jobs) ? envelope.jobs : [];
  return {
    jobs,
    pollAfterMs: Number(envelope?.pollAfterMs) || 3000,
  };
};

/** GET dashboard consolidado do representante com suporte a HTTP 202. */
export const fetchPartnerDashboardDeferred = async (scope, subpath, params = {}) => {
  const dominioIds = Array.isArray(params.dominio_ids)
    ? params.dominio_ids.filter((x) => x != null && String(x).trim() !== "")
    : [];
  const { dominio_ids: _drop, ...rest } = params || {};
  const query = { ...rest };
  if (dominioIds.length > 0) query.dominio_ids = dominioIds.map((x) => String(x)).join(",");
  const section = encodeURIComponent(String(scope || "").trim());
  const path = encodeURIComponent(String(subpath || "").trim());
  const response = await apiClient({
    method: "GET",
    url: `/billing/partner/dashboard/${section}/${path}`,
    params: query,
    headers: { "Content-Type": "application/json" },
    validateStatus: (status) => (status >= 200 && status < 300) || status === 202,
  });
  const responseBody = response.data || {};
  const envelope =
    responseBody != null &&
    responseBody.data != null &&
    typeof responseBody.data === "object"
      ? responseBody.data
      : responseBody;
  const preserveEnvelope = isDashboardJobOrDeferredEnvelope(envelope);
  const inner = preserveEnvelope ? envelope : envelope?.data ?? envelope ?? null;
  return {
    httpStatus: response.status,
    data: inner,
    message: responseBody.message,
  };
};

/** Lista jobs ativos do dashboard consolidado do representante. */
export const fetchPartnerDashboardActiveJobs = async () => {
  const response = await apiClient({
    method: "GET",
    url: "/billing/partner/dashboard/jobs",
    headers: { "Content-Type": "application/json" },
  });
  const responseBody = response.data || {};
  const envelope =
    responseBody != null &&
    responseBody.data != null &&
    typeof responseBody.data === "object"
      ? responseBody.data
      : responseBody;
  const jobs = Array.isArray(envelope?.jobs) ? envelope.jobs : [];
  return {
    jobs,
    pollAfterMs: Number(envelope?.pollAfterMs) || 3000,
  };
};

/** Status de um job do dashboard consolidado do representante. */
export const fetchPartnerDashboardJobStatus = async (jobId) => {
  const safe = encodeURIComponent(String(jobId || "").trim());
  const response = await apiClient({
    method: "GET",
    url: `/billing/partner/dashboard/jobs/${safe}`,
    headers: { "Content-Type": "application/json" },
    validateStatus: (status) => (status >= 200 && status < 300) || status === 202,
  });
  const responseBody = response.data || {};
  const envelope =
    responseBody != null &&
    responseBody.data != null &&
    typeof responseBody.data === "object"
      ? responseBody.data
      : responseBody;
  return {
    httpStatus: response.status,
    data: envelope,
    message: responseBody.message,
  };
};

/** POST /billing/partner/dashboard/presentations */
export async function createPartnerDashboardPresentation(payload) {
  const { data } = await apiClient.post("/billing/partner/dashboard/presentations", payload, {
    headers: { "Content-Type": "application/json" },
  });
  return data?.data ?? data;
}

/** Cancela um job de relatório do dashboard em processamento. */
export const cancelDashboardJob = async (jobId) => {
  const safe = encodeURIComponent(String(jobId || "").trim());
  const response = await apiClient.post(
    `/dashboard/jobs/${safe}/cancel`,
    {},
    {
      headers: { "Content-Type": "application/json" },
      validateStatus: (status) => status >= 200 && status < 500,
    }
  );
  const responseBody = response.data || {};
  return responseBody?.data ?? responseBody;
};

/** Cancela um job de relatório consolidado do representante. */
export const cancelPartnerDashboardJob = async (jobId) => {
  const safe = encodeURIComponent(String(jobId || "").trim());
  const response = await apiClient.post(
    `/billing/partner/dashboard/jobs/${safe}/cancel`,
    {},
    {
      headers: { "Content-Type": "application/json" },
      validateStatus: (status) => status >= 200 && status < 500,
    }
  );
  const responseBody = response.data || {};
  return responseBody?.data ?? responseBody;
};

/** Lista caches de relatório gerados pelo usuário logado. */
export const fetchDashboardCaches = async () => {
  const response = await apiClient.get("/dashboard/caches", {
    headers: { "Content-Type": "application/json" },
  });
  const responseBody = response.data || {};
  const envelope =
    responseBody?.data != null && typeof responseBody.data === "object"
      ? responseBody.data
      : responseBody;
  return {
    caches: Array.isArray(envelope?.caches) ? envelope.caches : [],
  };
};

/** Remove um cache de relatório do dashboard. */
export const deleteDashboardCache = async (entryId) => {
  const safe = encodeURIComponent(String(entryId || "").trim());
  const response = await apiClient.delete(`/dashboard/caches/${safe}`, {
    headers: { "Content-Type": "application/json" },
  });
  const responseBody = response.data || {};
  return responseBody?.data ?? responseBody;
};

/** Lista caches consolidados do representante gerados pelo usuário logado. */
export const fetchPartnerDashboardCaches = async () => {
  const response = await apiClient.get("/billing/partner/dashboard/caches", {
    headers: { "Content-Type": "application/json" },
  });
  const responseBody = response.data || {};
  const envelope =
    responseBody?.data != null && typeof responseBody.data === "object"
      ? responseBody.data
      : responseBody;
  return {
    caches: Array.isArray(envelope?.caches) ? envelope.caches : [],
  };
};

/** Remove um cache consolidado do representante. */
export const deletePartnerDashboardCache = async (entryId) => {
  const safe = encodeURIComponent(String(entryId || "").trim());
  const response = await apiClient.delete(`/billing/partner/dashboard/caches/${safe}`, {
    headers: { "Content-Type": "application/json" },
  });
  const responseBody = response.data || {};
  return responseBody?.data ?? responseBody;
};

/** GET dashboard consolidado da plataforma com suporte a HTTP 202. */
export const fetchPlatformDashboardDeferred = async (scope, subpath, params = {}) => {
  const dominioIds = Array.isArray(params.dominio_ids)
    ? params.dominio_ids.filter((x) => x != null && String(x).trim() !== "")
    : [];
  const { dominio_ids: _drop, ...rest } = params || {};
  const query = { ...rest };
  if (dominioIds.length > 0) query.dominio_ids = dominioIds.map((x) => String(x)).join(",");
  const section = encodeURIComponent(String(scope || "").trim());
  const path = encodeURIComponent(String(subpath || "").trim());
  const response = await apiClient({
    method: "GET",
    url: `/billing/admin/dashboard/${section}/${path}`,
    params: query,
    headers: { "Content-Type": "application/json" },
    validateStatus: (status) => (status >= 200 && status < 300) || status === 202,
  });
  const responseBody = response.data || {};
  const envelope =
    responseBody != null &&
    responseBody.data != null &&
    typeof responseBody.data === "object"
      ? responseBody.data
      : responseBody;
  const preserveEnvelope = isDashboardJobOrDeferredEnvelope(envelope);
  const inner = preserveEnvelope ? envelope : envelope?.data ?? envelope ?? null;
  return {
    httpStatus: response.status,
    data: inner,
    message: responseBody.message,
  };
};

export const fetchPlatformDashboardActiveJobs = async () => {
  const response = await apiClient({
    method: "GET",
    url: "/billing/admin/dashboard/jobs",
    headers: { "Content-Type": "application/json" },
  });
  const responseBody = response.data || {};
  const envelope =
    responseBody?.data != null && typeof responseBody.data === "object"
      ? responseBody.data
      : responseBody;
  const jobs = Array.isArray(envelope?.jobs) ? envelope.jobs : [];
  return {
    jobs,
    pollAfterMs: Number(envelope?.pollAfterMs) || 3000,
  };
};

export const fetchPlatformDashboardJobStatus = async (jobId) => {
  const safe = encodeURIComponent(String(jobId || "").trim());
  const response = await apiClient({
    method: "GET",
    url: `/billing/admin/dashboard/jobs/${safe}`,
    headers: { "Content-Type": "application/json" },
    validateStatus: (status) => (status >= 200 && status < 300) || status === 202,
  });
  const responseBody = response.data || {};
  const envelope =
    responseBody?.data != null && typeof responseBody.data === "object"
      ? responseBody.data
      : responseBody;
  return {
    httpStatus: response.status,
    data: envelope,
    message: responseBody.message,
  };
};

export const cancelPlatformDashboardJob = async (jobId) => {
  const safe = encodeURIComponent(String(jobId || "").trim());
  const response = await apiClient.post(
    `/billing/admin/dashboard/jobs/${safe}/cancel`,
    {},
    {
      headers: { "Content-Type": "application/json" },
      validateStatus: (status) => status >= 200 && status < 500,
    }
  );
  const responseBody = response.data || {};
  return responseBody?.data ?? responseBody;
};

export const fetchPlatformDashboardCaches = async () => {
  const response = await apiClient.get("/billing/admin/dashboard/caches", {
    headers: { "Content-Type": "application/json" },
  });
  const responseBody = response.data || {};
  const envelope =
    responseBody?.data != null && typeof responseBody.data === "object"
      ? responseBody.data
      : responseBody;
  return {
    caches: Array.isArray(envelope?.caches) ? envelope.caches : [],
  };
};

export const deletePlatformDashboardCache = async (entryId) => {
  const safe = encodeURIComponent(String(entryId || "").trim());
  const response = await apiClient.delete(`/billing/admin/dashboard/caches/${safe}`, {
    headers: { "Content-Type": "application/json" },
  });
  const responseBody = response.data || {};
  return responseBody?.data ?? responseBody;
};

/** Prefixo de IDs sintéticos de threads do WhatsApp pessoal (perfil) na lista do Chat. */
export const PERSONAL_WHATSAPP_CONVERSATION_PREFIX = "wa-personal:";

export function getPersonalChatKeyFromSyntheticConversationId(convId) {
  const s = String(convId || "");
  if (!s.startsWith(PERSONAL_WHATSAPP_CONVERSATION_PREFIX)) return null;
  try {
    return decodeURIComponent(s.slice(PERSONAL_WHATSAPP_CONVERSATION_PREFIX.length));
  } catch {
    return null;
  }
}

/** WhatsApp pessoal (perfil): sessão, lista Redis e promover para solicitação */
export async function getPersonalWhatsappStatus() {
  return fetchData("/users/me/whatsapp-personal", "GET");
}

export async function createPersonalWhatsappSession(payload = {}) {
  return fetchData("/users/me/whatsapp-personal/session", "POST", payload);
}

export async function getPersonalWhatsappQrcode() {
  return fetchData("/users/me/whatsapp-personal/qrcode", "GET");
}

export async function deletePersonalWhatsappSession() {
  return fetchData("/users/me/whatsapp-personal/session", "DELETE");
}

export async function getPersonalWhatsappChatsList() {
  const r = await fetchData("/users/me/whatsapp-personal/chats", "GET");
  const inner = r?.data;
  if (inner?.chats && Array.isArray(inner.chats)) return inner.chats;
  if (Array.isArray(inner)) return inner;
  return [];
}

export async function getPersonalWhatsappChatMessages(chatKey) {
  const enc = encodeURIComponent(String(chatKey || ""));
  const r = await fetchData(`/users/me/whatsapp-personal/chats/${enc}/messages`, "GET");
  return r?.data ?? {};
}

export async function sendPersonalWhatsappChatMessage(chatKey, textOrPayload) {
  const enc = encodeURIComponent(String(chatKey || ""));
  const payload =
    typeof textOrPayload === "string" || textOrPayload == null
      ? { text: String(textOrPayload || "").trim() }
      : textOrPayload;
  return fetchData(`/users/me/whatsapp-personal/chats/${enc}/send`, "POST", payload);
}

/** Remove histórico Redis desta conversa do WhatsApp pessoal (não apaga a sessão nem a instância). */
export async function deletePersonalWhatsappChat(chatKey) {
  const enc = encodeURIComponent(String(chatKey || ""));
  return fetchData(`/users/me/whatsapp-personal/chats/${enc}`, "DELETE");
}

export async function promotePersonalWhatsappToRequest(body) {
  return fetchData("/users/me/whatsapp-personal/promote-to-request", "POST", body);
}

/**
 * Formato normalizado de erro de login para a UI.
 * @typedef {{ message: string; status?: number; isNetwork?: boolean }} LoginError
 */

/**
 * Chama o endpoint de login. Em caso de erro, rejeita com um objeto normalizado
 * { message, status?, isNetwork? } para a página exibir mensagem adequada.
 */

/** Catálogo público de planos para landing/signup (sem login). */
export async function fetchPublicPlanCatalog() {
  try {
    const { data } = await apiClient.get("/public/plan-catalog");
    const inner = data?.data ?? data;
    return {
      items: Array.isArray(inner?.items) ? inner.items : [],
      meta: inner?.meta || {},
    };
  } catch {
    return { items: [], meta: {} };
  }
}

/** GET /public/signup — servidor indica se o fluxo de cadastro público está habilitado. */
export async function fetchPublicSignupAvailable() {
  try {
    const { data } = await apiClient.get("/public/signup");
    const inner = data?.data ?? data;
    return !!inner?.publicSignup;
  } catch {
    return false;
  }
}

export const getPublicWebsiteBootstrap = async (slug, company) => {
  const safeSlug = encodeURIComponent(String(slug || "").trim());
  const companyRaw = String(company || "").trim();
  if (!safeSlug) throw new Error("slug é obrigatório para carregar website público.");
  const endpoint = companyRaw
    ? `/public/websites/${safeSlug}/bootstrap?company=${encodeURIComponent(companyRaw)}`
    : `/public/websites/${safeSlug}/bootstrap`;
  const res = await fetchData(endpoint, "GET");
  return res?.data ?? res;
};

export const getPublicWebsiteBootstrapByHost = async (company = "") => {
  const companyRaw = String(company || "").trim();
  const endpoint = companyRaw
    ? `/public/websites/bootstrap?company=${encodeURIComponent(companyRaw)}`
    : "/public/websites/bootstrap";
  const res = await fetchData(endpoint, "GET");
  return res?.data ?? res;
};

/**
 * Cadastro público de workspace (API deve ter WORKSPACE_PUBLIC_SIGNUP + template DB).
 * @param {{ workspace, email, password, nome, locale_code?: string, plan?: string, planSku?: string, planVersion?: number, mode?: 'trial'|'pay', audience?: string, partnerToken?: string, acquisition?: object }} payload
 */
export const signupWorkspaceApi = async (payload) => {
  try {
    const { data } = await apiClient.request({
      method: "POST",
      url: "/public/signup",
      data: payload,
      headers: { "Content-Type": "application/json", Company: "__signup__" },
      // Clone do workspace no servidor pode levar vários minutos (offline/RDS).
      timeout: 600000,
    });
    const inner = data?.data ?? data;
    const token = inner?.token;
    const user = inner?.user;
    const company = inner?.company;
    const emailVerificationPending = !!inner?.emailVerificationPending;
    if (token) setBearerToken(token);
    return {
      data: {
        token,
        user,
        company,
        dominio: inner?.dominio,
        emailVerificationPending,
        billingTrial: inner?.billingTrial,
        billingPayFirst: inner?.billingPayFirst,
      },
    };
  } catch (err) {
    const status = err.response?.status;
    const body = err.response?.data;
    const rawTimeoutHint =
      (typeof body?.errorMessage === "string" && body.errorMessage) ||
      (typeof body?.message === "string" && body.message) ||
      "";
    const looksLikeTimeout =
      status === 504 ||
      status === 408 ||
      err.code === "ECONNABORTED" ||
      /timeout|timed?\s*out/i.test(String(rawTimeoutHint)) ||
      /LambdaTimeoutError/i.test(String(body?.errorType || ""));
    const message =
      (typeof body?.message === "string" && body.message) ||
      (Array.isArray(body?.message) && body.message[0]) ||
      err.message ||
      null;
    const isNetwork = !err.response && (err.code === "ECONNABORTED" || err.message === "Network Error");
    const fromCode = translateApiPayload({
      code: typeof body?.code === "string" ? body.code : null,
      params: body?.params,
    });
    const fromTimeout = looksLikeTimeout
      ? translateApiPayload({ code: "errors.signup.server_timeout" })
      : null;
    const fallback = isNetwork
      ? "Verifique sua conexão e tente novamente."
      : "Não foi possível concluir o cadastro.";
    const signupError = new Error(fromTimeout || fromCode || message || fallback);
    signupError.status = status ?? null;
    signupError.isNetwork = !!isNetwork;
    signupError.code =
      typeof body?.code === "string" ? body.code : looksLikeTimeout ? "errors.signup.server_timeout" : null;
    throw signupError;
  }
};

/**
 * Confirma e-mail após signup (token do link).
 * @param {string} token
 */
export const verifySignupEmailApi = async (token) => {
  try {
    const { data } = await apiClient.request({
      method: "POST",
      url: "/public/verify-email",
      data: { token },
      headers: { "Content-Type": "application/json", Company: "__signup__" },
    });
    const inner = data?.data ?? data;
    return { ok: true, company: inner?.company, message: data?.message };
  } catch (err) {
    const body = err.response?.data;
    const fromCode = translateApiPayload({
      code: typeof body?.code === "string" ? body.code : null,
      params: body?.params,
    });
    const message =
      fromCode ||
      (typeof body?.message === "string" && body.message) ||
      err.message ||
      "Não foi possível confirmar o e-mail.";
    const verifyError = new Error(message);
    verifyError.code = typeof body?.code === "string" ? body.code : null;
    throw verifyError;
  }
};

/**
 * Reenvia link de verificação (não revela se o e-mail existe).
 */
export const resendSignupVerificationApi = async ({ email, company }) => {
  try {
    await apiClient.request({
      method: "POST",
      url: "/public/resend-signup-verification",
      data: { email: String(email || "").trim(), company: String(company || "").trim().toLowerCase() },
      headers: { "Content-Type": "application/json", Company: "__signup__" },
    });
    return { ok: true };
  } catch (err) {
    const body = err.response?.data;
    const fromCode = translateApiPayload({
      code: typeof body?.code === "string" ? body.code : null,
      params: body?.params,
    });
    const message =
      fromCode ||
      (typeof body?.message === "string" && body.message) ||
      err.message ||
      "Não foi possível reenviar.";
    const resendError = new Error(message);
    resendError.code = typeof body?.code === "string" ? body.code : null;
    throw resendError;
  }
};

export const changeOwnPasswordApi = async (currentPassword, newPassword, options = {}) => {
  const { data } = await apiClient.request({
    method: "POST",
    url: "/users/me/password",
    data: { current_password: currentPassword, new_password: newPassword },
    headers: { "Content-Type": "application/json" },
    skipCompanyHeader: Boolean(options.skipCompanyHeader),
  });
  return data;
};

export const loginApi = async (email, password, options = {}) => {
  if (DEBUG_MODE) return { data: { token: "simulated-debug-token", user: {} } };
  try {
    const { data } = await apiClient.request({
      method: "POST",
      url: "/login",
      data: {
        email,
        password,
        ...(options.scope === "partner" || options.scope === "platform"
          ? { scope: options.scope }
          : {}),
      },
      headers: { "Content-Type": "application/json" },
      skipCompanyHeader: Boolean(options.skipCompanyHeader),
      skipAuthHeader: options.skipAuthHeader !== false,
    });
    const payload = data?.data ?? data;
    const token = payload?.token;
    const user = payload?.user;
    if (token && options.persistToken !== false) setBearerToken(token);
    return {
      data: {
        token,
        user,
        password_needs_upgrade: Boolean(payload?.password_needs_upgrade),
      },
    };
  } catch (err) {
    const status = err.response?.status;
    const body = err.response?.data;
    const message =
      (typeof body?.message === "string" && body.message) ||
      (Array.isArray(body?.message) && body.message[0]) ||
      (typeof body?.error === "string" && body.error) ||
      (typeof body?.msg === "string" && body.msg) ||
      null;
    const isNetwork = !err.response && (err.code === "ECONNABORTED" || err.message === "Network Error");
    const fallback = isNetwork
      ? "Verifique sua conexão e tente novamente."
      : status === 401
        ? "E-mail ou senha incorretos."
        : status === 403
          ? "Acesso negado para este workspace."
          : status === 404
            ? "Workspace ou serviço de login não encontrado."
            : status >= 500
              ? "Erro temporário no servidor. Tente novamente em instantes."
              : "Não foi possível entrar. Tente novamente.";
    const fromCode = translateApiPayload({
      code: typeof body?.code === "string" ? body.code : null,
      params: body?.params,
    });
    const normalized = {
      message: fromCode || message || fallback,
      status: status ?? null,
      isNetwork: !!isNetwork,
      code: typeof body?.code === "string" ? body.code : null,
    };
    throw normalized;
  }
};

/**
 * Busca mensagens de uma conversa com suporte a paginação.
 * - id: ID da conversa
 * - options:
 *   - limit: quantidade de mensagens por página (padrão backend: 20)
 *   - beforeMessageId: busca mensagens com id < beforeMessageId (para rolagem infinita)
 *   - returnRaw: se true, retorna o payload completo da API
 */
export const getConversationMessagesById = async (id, options = {}) => {
  const { limit, beforeMessageId, returnRaw } = options || {};

  const sp = new URLSearchParams();
  if (limit != null) sp.set("limit", String(limit));
  if (beforeMessageId != null) sp.set("beforeMessageId", String(beforeMessageId));

  const qs = sp.toString();
  const endpoint = `/conversations/${id}/messages${qs ? `?${qs}` : ""}`;
  const response = await fetchData(endpoint, "GET");
  const payload = response?.data ?? response ?? {};

  if (returnRaw) return payload;

  // Compatibilidade com chamadas antigas que esperam apenas o array de mensagens
  if (Array.isArray(payload.messages)) return payload.messages;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
};

export const recordConversationAccess = async (id, { origem = "chat" } = {}) => {
  const cid = encodeURIComponent(String(id));
  const response = await fetchData(`/conversations/${cid}/access`, "POST", { origem });
  return response?.data ?? response ?? {};
};

export const getConversationAccessLogs = async (id) => {
  const cid = encodeURIComponent(String(id));
  const response = await fetchData(`/conversations/${cid}/access-logs`, "GET");
  const payload = response?.data ?? response ?? {};
  if (Array.isArray(payload.messages)) return payload.messages;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
};

export const editConversationNote = async (conversationId, messageId, content) => {
  const cid = encodeURIComponent(String(conversationId));
  const mid = encodeURIComponent(String(messageId));
  const response = await fetchData(
    `/conversations/${cid}/messages/${mid}/note`,
    "PATCH",
    { content }
  );
  return response?.data ?? response ?? {};
};

export const deleteConversationNote = async (conversationId, messageId) => {
  const cid = encodeURIComponent(String(conversationId));
  const mid = encodeURIComponent(String(messageId));
  const response = await fetchData(
    `/conversations/${cid}/messages/${mid}/note`,
    "DELETE"
  );
  return response?.data ?? response ?? {};
};

/**
 * Auditoria de API parceira de uma mensagem (master only).
 * GET /conversations/:id/messages/:messageId/partner-audit
 * Retorna o objeto apiAudit ou null (404/sem payload não lança).
 */
export const getConversationMessagePartnerAudit = async (conversationId, messageId) => {
  const cid = encodeURIComponent(String(conversationId));
  const mid = encodeURIComponent(String(messageId));
  try {
    const response = await fetchData(`/conversations/${cid}/messages/${mid}/partner-audit`, "GET", null, {
      timeout: 12000,
    });
    const payload = response?.data ?? response ?? {};
    return payload?.apiAudit ?? payload?.data?.apiAudit ?? null;
  } catch (err) {
    const status = err?.response?.status ?? err?.status;
    if (status === 404) return null;
    throw err;
  }
};

/**
 * Billing - contexto atual do workspace (domínio + assinatura/entitlements).
 */
export const getBillingContext = async () => {
  const { data } = await apiClient.get("/billing/context");
  return data?.data ?? null;
};

/**
 * Billing - resumo de consumo no período.
 * @param {{from?: string, to?: string, includeOverage?: string|number}} params formato YYYY-MM-DD; includeOverage=0 desliga estimativa de excedentes.
 */
export const getBillingUsageSummary = async (params = {}) => {
  const { data } = await apiClient.get("/billing/usage/summary", { params });
  return data?.data ?? null;
};

/**
 * Billing Admin - catálogo de planos ativo.
 */
export const getBillingPlanCatalog = async () => {
  const { data } = await apiClient.get("/billing/admin/plans");
  return data?.data ?? { items: [] };
};

/** Catálogo de planos para o fluxo de criação de workspace (requer workspaces.create). */
export const getWorkspaceCreatePlanCatalog = async () => {
  const { data } = await apiClient.get("/billing/admin/workspaces/plan-catalog");
  return data?.data ?? { items: [] };
};

/** Master / billing admin — lista xprobase.dominios (painel plataforma). */
export const listPlatformDominios = async (params = {}) => {
  const merged = {
    ...params,
    tenantLegacyStats: params.tenantLegacyStats != null ? params.tenantLegacyStats : "1",
  };
  const { data } = await apiClient.get("/billing/admin/dominios", { params: merged });
  return data?.data ?? { items: [], meta: {} };
};

/** Master — provisiona workspace (clone do template + dominios + permissoes quando aplicável). */
export const postPlatformCreateWorkspace = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/dominios", payload || {});
  return data?.data ?? null;
};

/** Master — dispara auto-migrate em workspaces selecionados (ou todos). */
export const postPlatformAutoMigrate = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/auto-migrate", payload || {}, { timeout: 300000 });
  return data?.data ?? null;
};

/** Master — job assíncrono de migrate pesado (índices / utf8mb4 COPY). */
export const postPlatformMigrateJob = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/migrate-jobs", payload || {}, { timeout: 60000 });
  return data?.data ?? null;
};

export const getPlatformMigrateJob = async (jobId = "active") => {
  const id = encodeURIComponent(String(jobId || "active").trim() || "active");
  const { data } = await apiClient.get(`/billing/admin/migrate-jobs/${id}`, { timeout: 30000 });
  return data?.data ?? null;
};

export const postPlatformMigrateJobAction = async (jobId, action) => {
  const id = encodeURIComponent(String(jobId || "").trim());
  const act = String(action || "").trim();
  if (!id || !act) throw new Error("jobId e action são obrigatórios.");
  const { data } = await apiClient.post(`/billing/admin/migrate-jobs/${id}/${act}`, {}, { timeout: 30000 });
  return data?.data ?? null;
};

/** Master — SHOW FULL PROCESSLIST filtrado (DDL / MDL). */
export const getPlatformDbProcesslist = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/db/processlist", { params, timeout: 30000 });
  return data?.data ?? null;
};

/** Master — amostragem de pressão de conexões MySQL (cache Redis e/ou live). */
export const getPlatformDbConnectionPressure = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/db/connection-pressure", {
    params,
    timeout: 45000,
  });
  return data?.data ?? null;
};

/** Master — causa de carga MySQL (PROCESSLIST + digests + SQL lento da app). */
export const getPlatformDbLoadCause = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/db/load-cause", {
    params,
    timeout: 60000,
  });
  return data?.data ?? null;
};

/** Master — KILL QUERY / KILL connection. */
export const postPlatformDbKill = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/db/kill", payload || {}, { timeout: 30000 });
  return data?.data ?? null;
};

/** Master — mata Sleep ociosos em lote (KILL CONNECTION). */
export const postPlatformDbKillIdleSleep = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/db/kill-idle-sleep", payload || {}, {
    timeout: 120000,
  });
  return data?.data ?? null;
};

/** Master — mata queries ativas em lote (KILL QUERY por padrão). */
export const postPlatformDbKillActiveQueries = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/db/kill-active-queries", payload || {}, {
    timeout: 120000,
  });
  return data?.data ?? null;
};

/** Master — remove hosts/accounts ociosos do performance_schema no writer. */
export const postPlatformDbReleaseWriterHosts = async () => {
  const { data } = await apiClient.post(
    "/billing/admin/db/release-writer-hosts",
    { confirm: "LIMPAR_HOSTS_WRITER" },
    { timeout: 60000 }
  );
  return data?.data ?? null;
};

/** Master — alinha o 1º usuário do tenant ao usuário xprobase logado (mesmo e-mail e senha do login central). */
export const postPlatformDominioSyncTenantLogin = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.post(`/billing/admin/dominios/${id}/sync-tenant-login`, {});
  return data?.data ?? null;
};

/** Master — relatório completo de compatibilidade legado → Xpro 4. */
export const getPlatformDominioXpro4Compatibility = async (dominioId, { refresh = false } = {}) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const params = refresh ? { refresh: "1" } : {};
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/xpro4-compatibility`, {
    params,
    timeout: 120000,
  });
  return data?.data ?? null;
};

/** Master — aplica correção automática de compatibilidade Xpro 4 (check ou todas). */
export const postPlatformDominioXpro4CompatibilityFix = async (
  dominioId,
  { checkId, actionId, applyAll = false } = {}
) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const body = {};
  if (applyAll) body.applyAll = true;
  else if (checkId) body.checkId = String(checkId).trim();
  else if (actionId) body.actionId = String(actionId).trim();
  else throw new Error("Informe checkId, actionId ou applyAll.");
  const { data } = await apiClient.post(`/billing/admin/dominios/${id}/xpro4-compatibility/fix`, body, {
    timeout: 300000,
    validateStatus: (status) => (status >= 200 && status < 300) || status === 202,
  });
  return data?.data ?? null;
};

/** Master — summary batch de compatibilidade (lista de workspaces). */
export const getPlatformDominiosXpro4CompatibilitySummary = async (ids = [], { concurrency = 3 } = {}) => {
  const clean = [...new Set(ids.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0))];
  if (!clean.length) return { items: [], meta: { requested: 0, returned: 0 } };

  const chunkSize = 50;
  const items = [];
  for (let i = 0; i < clean.length; i += chunkSize) {
    const chunk = clean.slice(i, i + chunkSize);
    const { data } = await apiClient.get("/billing/admin/dominios/xpro4-compatibility-summary", {
      params: { ids: chunk.join(","), concurrency },
      timeout: 180000,
    });
    const payload = data?.data ?? { items: [], meta: {} };
    items.push(...(payload.items || []));
  }

  return {
    items,
    meta: { requested: clean.length, returned: items.length },
  };
};

/**
 * Master — prévia WA Service (mês ant.) em lote.
 * mode=aggregates: rápido (metering). mode=partner: Gupshup em lotes curtos (não trava a listagem).
 */
export const getPlatformDominiosWhatsappServicePassThroughBatch = async (
  ids = [],
  { mode = "aggregates", concurrency, onChunk, chunkDelayMs } = {}
) => {
  const clean = [...new Set(ids.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0))];
  if (!clean.length) return { items: [], period: null, meta: { requested: 0, returned: 0 } };

  const partnerMode = String(mode).toLowerCase() === "partner";
  const chunkSize = partnerMode ? 5 : 80;
  const delayMs =
    chunkDelayMs != null
      ? Math.max(0, Number(chunkDelayMs) || 0)
      : partnerMode
        ? 1500
        : 0;
  const items = [];
  let period = null;
  let metaAcc = { requested: clean.length, returned: 0, partner_ok: 0, partner_attempted: 0 };

  for (let i = 0; i < clean.length; i += chunkSize) {
    if (i > 0 && delayMs > 0) {
      await new Promise((r) => setTimeout(r, delayMs));
    }
    const chunk = clean.slice(i, i + chunkSize);
    const { data } = await apiClient.get("/billing/admin/dominios/whatsapp-service-passthrough-batch", {
      params: {
        ids: chunk.join(","),
        mode: partnerMode ? "partner" : "aggregates",
        ...(concurrency != null ? { concurrency } : {}),
      },
      timeout: partnerMode ? 90000 : 60000,
    });
    const payload = data?.data ?? { items: [], meta: {} };
    if (payload.period) period = payload.period;
    items.push(...(payload.items || []));
    metaAcc.returned = items.length;
    metaAcc.partner_ok += Number(payload.meta?.partner_ok) || 0;
    metaAcc.partner_attempted += Number(payload.meta?.partner_attempted) || 0;
    if (typeof onChunk === "function") {
      onChunk(payload.items || [], payload);
    }
  }

  return { items, period, meta: metaAcc };
};

/**
 * Master — consumo diário de Serviço e previsão do mês, em lotes curtos (Gupshup).
 * onChunk recebe os itens de cada lote para preencher a tabela aos poucos.
 * onChunkError, se informado, marca só o lote que falhou e segue os demais.
 */
export const getPlatformDominiosWhatsappServiceDailyBatch = async (
  ids = [],
  { onChunk, onChunkError, chunkDelayMs = 800, shouldStop } = {}
) => {
  const clean = [...new Set(ids.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0))];
  if (!clean.length) return { items: [], month: null, meta: { requested: 0, returned: 0 } };

  const chunkSize = 3;
  const delayMs = Math.max(0, Number(chunkDelayMs) || 0);
  const items = [];
  let month = null;
  let asOf = null;
  let referencePeriod = null;
  let metaAcc = { requested: clean.length, returned: 0, partner_ok: 0, unit_tariff_brl: 0.049, failed: 0 };

  for (let i = 0; i < clean.length; i += chunkSize) {
    if (typeof shouldStop === "function" && shouldStop()) break;
    if (i > 0 && delayMs > 0) {
      await new Promise((r) => setTimeout(r, delayMs));
    }
    if (typeof shouldStop === "function" && shouldStop()) break;
    const chunk = clean.slice(i, i + chunkSize);
    try {
      const { data } = await apiClient.get("/billing/admin/dominios/whatsapp-service-daily-batch", {
        params: { ids: chunk.join(",") },
        timeout: 90000,
      });
      const payload = data?.data ?? { items: [], meta: {} };
      if (payload.month) month = payload.month;
      if (payload.as_of) asOf = payload.as_of;
      if (payload.reference_period) referencePeriod = payload.reference_period;
      const chunkItems = payload.items || [];
      items.push(...chunkItems);
      metaAcc.returned = items.length;
      metaAcc.partner_ok += Number(payload.meta?.partner_ok) || 0;
      if (payload.meta?.unit_tariff_brl != null) metaAcc.unit_tariff_brl = payload.meta.unit_tariff_brl;
      if (typeof onChunk === "function") onChunk(chunkItems, payload, chunk);
    } catch (err) {
      metaAcc.failed += chunk.length;
      if (typeof onChunkError === "function") {
        onChunkError(chunk, err);
        continue;
      }
      throw err;
    }
  }

  return { items, month, as_of: asOf, reference_period: referencePeriod, meta: metaAcc };
};

/** Master — analytics agregados da base de workspaces (xprobase). */
export const getPlatformWorkspaceAnalytics = async () => {
  const { data } = await apiClient.get("/billing/admin/analytics/workspaces");
  return data?.data ?? null;
};

/** Ingestão de eventos de uso do produto (navegação SPA). */
export const postProductUsageEvents = async (events = []) => {
  const { data } = await apiClient.post("/usage/product/events", { events });
  return data?.data ?? null;
};

/** Heartbeat de ambiente do cliente para presença (independente do socket). */
export const postPresenceClientEnv = async (payload = {}) => {
  const { data } = await apiClient.post("/usage/product/presence-env", payload);
  return data?.data ?? null;
};

/** Master — ambiente (device/browser/versão) das abas online por workspace. */
export const getPlatformPresenceClientEnv = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/platform/presence-client-env", { params });
  return data?.data ?? { items: [] };
};

/** Master — resumo de uso do produto (telas, usuários, tempo). */
export const getPlatformProductUsageSummary = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/analytics/product-usage/summary", { params });
  return data?.data ?? null;
};

/** Master — Ask interno (código da plataforma). */
export const createInternalAskSession = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/internal-ask/session", payload);
  return data?.data ?? data;
};

export const chatInternalAsk = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/internal-ask/chat", payload);
  return data?.data ?? data;
};

export const getInternalAskStatus = async () => {
  const { data } = await apiClient.get("/billing/admin/internal-ask/status");
  return data?.data ?? data;
};

export const listInternalAskEvents = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/internal-ask/events", { params });
  return data?.data ?? { items: [], total: 0 };
};

export const getInternalAskEvent = async (id) => {
  const { data } = await apiClient.get(`/billing/admin/internal-ask/events/${encodeURIComponent(id)}`);
  return data?.data ?? data;
};

/** Master — distribuição de uso por hora do dia. */
export const getPlatformProductUsageHourly = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/analytics/product-usage/hourly", { params });
  return data?.data ?? null;
};

/** Master — drill-down de um horário de pico (tela, workspace ou usuário). */
export const getPlatformProductUsageHourBreakdown = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/analytics/product-usage/hourly/breakdown", { params });
  return data?.data ?? null;
};

/** Master — drill-down de uma tela (workspace, user ou referrer). */
export const getPlatformProductUsageBreakdown = async (screenKey, params = {}) => {
  const key = encodeURIComponent(String(screenKey || ""));
  const { data } = await apiClient.get(
    `/billing/admin/analytics/product-usage/screens/${key}/breakdown`,
    { params }
  );
  return data?.data ?? null;
};

/** Master — ranking de uso por workspace. */
export const getPlatformProductUsageWorkspaces = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/analytics/product-usage/workspaces", { params });
  return data?.data ?? { items: [] };
};

/** Master — ranking de uso por usuário. */
export const getPlatformProductUsageUsers = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/analytics/product-usage/users", { params });
  return data?.data ?? { items: [] };
};

/** Master — resumo correlacionado (acessos + interações + créditos IA). */
export const getPlatformProductEngagementSummary = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/analytics/product-engagement/summary", { params });
  return data?.data ?? null;
};

/** Master — série diária correlacionada. */
export const getPlatformProductEngagementDaily = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/analytics/product-engagement/daily", { params });
  return data?.data ?? { days: [] };
};

/** Master — ranking correlacionado por workspace. */
export const getPlatformProductEngagementWorkspaces = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/analytics/product-engagement/workspaces", { params });
  return data?.data ?? { items: [] };
};

/** Master — ranking correlacionado por usuário. */
export const getPlatformProductEngagementUsers = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/analytics/product-engagement/users", { params });
  return data?.data ?? { items: [] };
};

/** Master — breakdown de créditos IA por feature/source. */
export const getPlatformAiCreditsBreakdown = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/analytics/ai-credits/breakdown", { params });
  return data?.data ?? { items: [], total_credits: 0 };
};

/** Master — drill-down de uma feature de IA (workspace ou usuário). */
export const getPlatformAiCreditsFeatureBreakdown = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/analytics/ai-credits/feature-breakdown", { params });
  return data?.data ?? null;
};

/** Master — oportunidades de upsell (uso vs limites do plano). */
export const getPlatformWorkspaceOpportunities = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/analytics/opportunities", { params });
  return data?.data ?? { items: [] };
};

/** Master — lista representantes (resellers). */
export const listAdminResellers = async () => {
  const { data } = await apiClient.get("/billing/admin/resellers");
  return data?.data ?? { items: [] };
};

/** Master — cria representante. */
export const postAdminReseller = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/resellers", payload || {});
  return data?.data ?? null;
};

/** Master — vincula e-mail a portal do representante (reseller_users). */
export const postAdminResellerStaff = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/resellers/staff", payload || {});
  return data?.data ?? null;
};

/** Master — usuários xprobase (flags master / compartilhar domínios). */
export const listAdminPlatformUsers = async () => {
  const { data } = await apiClient.get("/billing/admin/platform-users");
  return data?.data ?? { items: [], meta: {} };
};

/** Master global — atualiza flags de usuário na base platform (xprobase). */
export const patchAdminPlatformUser = async (userId, payload = {}) => {
  const id = encodeURIComponent(String(userId || "").trim());
  if (!id) throw new Error("userId é obrigatório.");
  const { data } = await apiClient.patch(`/billing/admin/platform-users/${id}`, payload || {});
  return data?.data ?? null;
};

/** Master global — inclui usuário no cadastro central (xprobase). */
export const postAdminPlatformUser = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/platform-users", payload || {});
  return data?.data ?? null;
};

/** Master global — envia e-mail de redefinição de senha (console da plataforma). */
export const postAdminPlatformUserSendReset = async (userId) => {
  const id = encodeURIComponent(String(userId || "").trim());
  if (!id) throw new Error("userId é obrigatório.");
  const { data } = await apiClient.post(`/billing/admin/platform-users/${id}/send-reset`, {});
  return data?.data ?? null;
};

/** Master global — exclusão lógica do usuário no xprobase. */
export const deleteAdminPlatformUser = async (userId) => {
  const id = encodeURIComponent(String(userId || "").trim());
  if (!id) throw new Error("userId é obrigatório.");
  const { data } = await apiClient.delete(`/billing/admin/platform-users/${id}`);
  return data?.data ?? null;
};

/** Master global — dispara a sincronização de masters órfãos em todos os workspaces. */
export const postAdminReconcileTenantMasters = async () => {
  const { data } = await apiClient.post(
    "/billing/admin/platform-users/reconcile-tenant-masters",
    { apply: true },
    { timeout: 30000 }
  );
  return data?.data ?? null;
};

/** Master global — exclusão lógica de um master órfão em um workspace (linha com master=1). */
export const postAdminExcludeOrphanTenantMaster = async ({ dominioId, email, tenantUserId } = {}) => {
  const { data } = await apiClient.post(
    "/billing/admin/platform-users/reconcile-tenant-masters/exclude",
    {
      dominioId,
      email,
      ...(tenantUserId ? { tenantUserId } : {}),
    },
    { timeout: 30000 }
  );
  return data?.data ?? null;
};
export const getAdminReconcileTenantMastersStatus = async (runId) => {
  const id = String(runId || "").trim();
  if (!id) throw new Error("runId é obrigatório.");
  const { data } = await apiClient.get("/billing/admin/platform-users/reconcile-tenant-masters", {
    params: { runId: id },
    timeout: 30000,
  });
  return data?.data ?? null;
};

/** Master — IDs de domínio (xprobase) que outro usuário master pode operar (permissoes_dominios). */
export const getUserPlatformDomainsAcl = async (userId) => {
  const id = encodeURIComponent(String(userId || "").trim());
  if (!id) throw new Error("userId é obrigatório.");
  const { data } = await apiClient.get(`/users/${id}/acl`, { params: { context: "platform" } });
  return data?.data ?? { dominios: [] };
};

export const setUserPlatformDomainsAcl = async (userId, dominios = []) => {
  const id = encodeURIComponent(String(userId || "").trim());
  if (!id) throw new Error("userId é obrigatório.");
  const { data } = await apiClient.put(`/users/${id}/acl`, { dominios }, { params: { context: "platform" } });
  return data?.data ?? null;
};

/** Master — atualiza representante (nome, status, metadata). */
export const patchAdminReseller = async (resellerId, payload = {}) => {
  const id = encodeURIComponent(String(resellerId || "").trim());
  if (!id) throw new Error("resellerId é obrigatório.");
  const { data } = await apiClient.patch(`/billing/admin/resellers/${id}`, payload || {});
  return data?.data ?? null;
};

/** Master — exclusão lógica do representante (some da lista; não é só inativar). */
export const deleteAdminReseller = async (resellerId) => {
  const id = encodeURIComponent(String(resellerId || "").trim());
  if (!id) throw new Error("resellerId é obrigatório.");
  const { data } = await apiClient.delete(`/billing/admin/resellers/${id}`);
  return data?.data ?? null;
};

/** Master — lista e-mails com acesso ao portal do representante. */
export const listAdminResellerStaff = async (resellerId) => {
  const id = encodeURIComponent(String(resellerId || "").trim());
  if (!id) throw new Error("resellerId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/resellers/${id}/staff`);
  return data?.data ?? { items: [] };
};

/** Master — remove vínculo e-mail ↔ representante. */
export const deleteAdminResellerStaff = async (resellerId, staffId) => {
  const rid = encodeURIComponent(String(resellerId || "").trim());
  const sid = encodeURIComponent(String(staffId || "").trim());
  if (!rid || !sid) throw new Error("resellerId e staffId são obrigatórios.");
  const { data } = await apiClient.delete(`/billing/admin/resellers/${rid}/staff/${sid}`);
  return data?.data ?? null;
};

/** Master — atualiza nome/active do staff do representante. */
export const patchAdminResellerStaff = async (resellerId, staffId, payload = {}) => {
  const rid = encodeURIComponent(String(resellerId || "").trim());
  const sid = encodeURIComponent(String(staffId || "").trim());
  if (!rid || !sid) throw new Error("resellerId e staffId são obrigatórios.");
  const { data } = await apiClient.patch(`/billing/admin/resellers/${rid}/staff/${sid}`, payload || {});
  return data?.data ?? null;
};

/** Master — reenvia e-mail de convite (portal do representante). */
export const postAdminResellerStaffResendInvite = async (resellerId, staffId) => {
  const rid = encodeURIComponent(String(resellerId || "").trim());
  const sid = encodeURIComponent(String(staffId || "").trim());
  if (!rid || !sid) throw new Error("resellerId e staffId são obrigatórios.");
  const { data } = await apiClient.post(`/billing/admin/resellers/${rid}/staff/${sid}/resend-invite`, {});
  return data?.data ?? null;
};

/** Portal do representante — analytics consolidados. */
export const getPartnerWorkspaceAnalytics = async () => {
  const { data } = await apiClient.get("/billing/partner/analytics");
  return data?.data ?? null;
};

/** Portal do representante — painel ao vivo consolidado (fila, operadores, automação). */
export const getPartnerLiveDashboard = async (params = {}) => {
  const dominioIds = Array.isArray(params.dominio_ids)
    ? params.dominio_ids.filter((x) => x != null && String(x).trim() !== "")
    : [];
  const query =
    dominioIds.length > 0
      ? { dominio_ids: dominioIds.map((x) => String(x)).join(",") }
      : undefined;
  const { data } = await apiClient.get("/billing/partner/live-dashboard", { params: query });
  return data?.data ?? null;
};

/** Console da plataforma — painel ao vivo consolidado. */
export const getPlatformLiveDashboard = async (params = {}) => {
  const dominioIds = Array.isArray(params.dominio_ids)
    ? params.dominio_ids.filter((x) => x != null && String(x).trim() !== "")
    : [];
  const query =
    dominioIds.length > 0
      ? { dominio_ids: dominioIds.map((x) => String(x)).join(",") }
      : undefined;
  const { data } = await apiClient.get("/billing/admin/live-dashboard", { params: query });
  return data?.data ?? null;
};

/** Portal do representante — seção do dashboard consolidado (mesmas rotas /dashboard/:scope/:subpath). */
export const getPartnerDashboardSection = async (scope, subpath, params = {}) => {
  const dominioIds = Array.isArray(params.dominio_ids)
    ? params.dominio_ids.filter((x) => x != null && String(x).trim() !== "")
    : [];
  const { dominio_ids: _drop, ...rest } = params || {};
  const query = { ...rest };
  if (dominioIds.length > 0) query.dominio_ids = dominioIds.map((x) => String(x)).join(",");
  const section = encodeURIComponent(String(scope || "").trim());
  const path = encodeURIComponent(String(subpath || "").trim());
  const { data: body } = await apiClient.get(`/billing/partner/dashboard/${section}/${path}`, { params: query });
  return body?.data ?? body ?? null;
};

/** Portal do representante — pesquisas consolidadas dos workspaces. */
export const listPartnerSurveys = async (params = {}) => {
  const dominioIds = Array.isArray(params.dominio_ids)
    ? params.dominio_ids.filter((x) => x != null && String(x).trim() !== "")
    : [];
  const { dominio_ids: _drop, ...rest } = params || {};
  const query = { ...rest };
  if (dominioIds.length > 0) query.dominio_ids = dominioIds.map((x) => String(x)).join(",");
  const { data } = await apiClient.get("/billing/partner/surveys", { params: query });
  return data?.data ?? { data: [], pagination: { total: 0, page: 1, totalPages: 1 } };
};

/** Console da plataforma — pesquisas consolidadas. */
export const listPlatformSurveys = async (params = {}) => {
  const dominioIds = Array.isArray(params.dominio_ids)
    ? params.dominio_ids.filter((x) => x != null && String(x).trim() !== "")
    : [];
  const { dominio_ids: _drop, ...rest } = params || {};
  const query = { ...rest };
  if (dominioIds.length > 0) query.dominio_ids = dominioIds.map((x) => String(x)).join(",");
  const { data } = await apiClient.get("/billing/admin/surveys", { params: query });
  return data?.data ?? { data: [], pagination: { total: 0, page: 1, totalPages: 1 } };
};

/** Console da plataforma — seção do dashboard consolidado. */
export const getPlatformDashboardSection = async (scope, subpath, params = {}) => {
  const dominioIds = Array.isArray(params.dominio_ids)
    ? params.dominio_ids.filter((x) => x != null && String(x).trim() !== "")
    : [];
  const { dominio_ids: _drop, ...rest } = params || {};
  const query = { ...rest };
  if (dominioIds.length > 0) query.dominio_ids = dominioIds.map((x) => String(x)).join(",");
  const section = encodeURIComponent(String(scope || "").trim());
  const path = encodeURIComponent(String(subpath || "").trim());
  const { data: body } = await apiClient.get(`/billing/admin/dashboard/${section}/${path}`, { params: query });
  return body?.data ?? body ?? null;
};

/** Portal do representante — analytics de pesquisa com suporte a HTTP 202. */
export const fetchPartnerSurveyAnalyticsDeferred = async (partnerSurveyKey, params = {}) => {
  const key = encodeURIComponent(String(partnerSurveyKey || "").trim());
  const response = await apiClient({
    method: "GET",
    url: `/billing/partner/surveys/${key}/analytics`,
    params,
    headers: { "Content-Type": "application/json" },
    validateStatus: (status) => (status >= 200 && status < 300) || status === 202,
  });
  const responseBody = response.data || {};
  const envelope =
    responseBody != null &&
    responseBody.data != null &&
    typeof responseBody.data === "object"
      ? responseBody.data
      : responseBody;
  return {
    httpStatus: response.status,
    data: envelope,
    message: responseBody.message,
  };
};

/** Console da plataforma — analytics de pesquisa com suporte a HTTP 202. */
export const fetchPlatformSurveyAnalyticsDeferred = async (platformSurveyKey, params = {}) => {
  const key = encodeURIComponent(String(platformSurveyKey || "").trim());
  const response = await apiClient({
    method: "GET",
    url: `/billing/admin/surveys/${key}/analytics`,
    params,
    headers: { "Content-Type": "application/json" },
    validateStatus: (status) => (status >= 200 && status < 300) || status === 202,
  });
  const responseBody = response.data || {};
  const envelope =
    responseBody != null &&
    responseBody.data != null &&
    typeof responseBody.data === "object"
      ? responseBody.data
      : responseBody;
  return {
    httpStatus: response.status,
    data: envelope,
    message: responseBody.message,
  };
};

/** Portal do representante — analytics de uma pesquisa (chave dominio_id:survey_id). */
export const getPartnerSurveyAnalytics = async (partnerSurveyKey, params = {}) => {
  const res = await fetchPartnerSurveyAnalyticsDeferred(partnerSurveyKey, params);
  if (res.httpStatus === 202) return res.data;
  return res.data ?? null;
};

/** Portal do representante — contexto. */
export const getPartnerBillingContext = async () => {
  const { data } = await apiClient.get("/billing/partner/context");
  return data?.data ?? null;
};

/** Portal do representante — workspaces do representante (somente GET /billing/partner/dominios; sem fallback admin). */
export const listPartnerDominios = async (params = {}) => {
  const { data } = await apiClient.get("/billing/partner/dominios", { params });
  return data?.data ?? { items: [], meta: {} };
};

/** Portal do representante — detalhe workspace (mesmo payload admin com escopo). */
export const getPartnerDominioDetail = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/partner/dominios/${id}`);
  return data?.data ?? null;
};

/** Portal do representante — exclusão lógica (excluido=1 em xprobase.dominios), sem remover schema do tenant. */
export const excludePartnerDominio = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.post(`/billing/partner/dominios/${id}/exclude`, {});
  return data?.data ?? null;
};

/** Portal do representante — catálogo de preços referência. */
export const getPartnerCatalog = async () => {
  const { data } = await apiClient.get("/billing/partner/catalog");
  return data?.data ?? { items: [], price_overrides_for_customers: {} };
};

/** Portal do representante — grava preços sugeridos ao cliente final (metadata representante). */
export const putPartnerPricingOverrides = async (payload = {}) => {
  const { data } = await apiClient.put("/billing/partner/pricing", payload || {});
  return data?.data ?? null;
};

/* =====================================================================
 * Workspaces de apresentação (representante + master)
 * ===================================================================== */

/** Normaliza payload de criação (camelCase + compat com dominio_id numérico). */
function normalizeWorkspaceCreateApiData(raw) {
  if (raw == null || typeof raw !== "object") return raw;
  const fromId = raw.dominioId ?? raw.dominio_id;
  const n = Number(fromId);
  const dominioId = Number.isFinite(n) && n > 0 ? n : null;
  return dominioId != null && dominioId !== raw.dominioId ? { ...raw, dominioId } : raw;
}

/** Representante — cria workspace (mode = 'trial' | 'pay' | 'presentation'). */
export const createPartnerWorkspace = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/partner/dominios", payload || {});
  return normalizeWorkspaceCreateApiData(data?.data ?? null);
};

/** Representante — status do pipeline de apresentação (polling). */
export const getPartnerWorkspacePresentationStatus = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/partner/dominios/${id}/presentation/status`);
  return data?.data ?? null;
};

/** Representante — converte workspace de apresentação em trial/pago. */
export const convertPartnerWorkspacePresentation = async (dominioId, payload = {}) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.post(
    `/billing/partner/dominios/${id}/presentation/convert`,
    payload || {}
  );
  return data?.data ?? null;
};

/** Representante — descarta workspace de apresentação (exclusão lógica + status discarded; schema do tenant preservado). */
export const discardPartnerWorkspacePresentation = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.delete(`/billing/partner/dominios/${id}/presentation`);
  return data?.data ?? null;
};

/** Master — cria workspace via wizard (suporta mode = 'trial' | 'pay' | 'presentation'). */
/** Verifica se o identificador (subdomínio) está disponível na plataforma. */
export const checkPlatformWorkspaceSlugAvailability = async (slug) => {
  const s = String(slug || "").trim();
  if (!s) return { available: false, reason: "missing" };
  const { data } = await apiClient.get("/billing/admin/workspaces/slug-availability", {
    params: { slug: s },
  });
  return data?.data ?? data ?? { available: false };
};

/** Portal revenda — mesma checagem de identificador. */
export const checkPartnerWorkspaceSlugAvailability = async (slug) => {
  const s = String(slug || "").trim();
  if (!s) return { available: false, reason: "missing" };
  const { data } = await apiClient.get("/billing/partner/workspaces/slug-availability", {
    params: { slug: s },
  });
  return data?.data ?? data ?? { available: false };
};

export const createPlatformWorkspace = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/dominios", payload || {});
  return normalizeWorkspaceCreateApiData(data?.data ?? null);
};

/** Master — status do pipeline de apresentação. */
export const getPlatformWorkspacePresentationStatus = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/presentation/status`);
  return data?.data ?? null;
};

/** Master — converte apresentação em trial/pago. */
export const convertPlatformWorkspacePresentation = async (dominioId, payload = {}) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.post(
    `/billing/admin/dominios/${id}/presentation/convert`,
    payload || {}
  );
  return data?.data ?? null;
};

/** Master — descarta apresentação (exclusão lógica; schema do tenant preservado). */
export const discardPlatformWorkspacePresentation = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.delete(`/billing/admin/dominios/${id}/presentation`);
  return data?.data ?? null;
};

/** Master — operadores humanos “online” em todos os workspaces com schema resolvível (mesma regra que is_online na listagem de usuários). */
export const getPlatformOnlineOperators = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/platform/online-operators", { params });
  return data?.data ?? { items: [], meta: {} };
};

/** Master — detalhe admin: domínio + assinatura + sinais de uso. */
export const getPlatformDominioAdminDetail = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}`);
  return data?.data ?? null;
};

/** Master — snapshot operacional no tenant (Customer Health parcial): leads, canais, retenção de equipe, tempos. */
export const getPlatformDominioHealthSnapshot = async (dominioId, params = {}) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/health-snapshot`, {
    params,
    timeout: 120_000,
  });
  return data?.data ?? null;
};

/** Master — vínculo Superlógica: estado atual. */
export const getPlatformDominioSuperlogicaLink = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/superlogica/link`);
  return data?.data ?? null;
};

/** Master — tenta vincular por CPF/CNPJ (docValue) ou força por superlogicaClienteId. */
export const postPlatformDominioSuperlogicaLink = async (dominioId, payload = {}) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.post(`/billing/admin/dominios/${id}/superlogica/link`, payload || {});
  return data?.data ?? null;
};

/** Master — remove vínculo Superlógica. */
export const deletePlatformDominioSuperlogicaLink = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.delete(`/billing/admin/dominios/${id}/superlogica/link`);
  return data?.data ?? null;
};

/** Master — resumo financeiro/contratual (Superlógica) do cliente vinculado. */
export const getPlatformDominioSuperlogicaSummary = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/superlogica/summary`);
  return data?.data ?? null;
};

/** Master — cobranças Superlógica + NFs vinculadas (aba Faturas). */
export const getPlatformDominioSuperlogicaCobrancas = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/superlogica/cobrancas`);
  return data?.data ?? null;
};

/** Master — catálogo Superlógica (somente leitura): type=planos|produtos */
export const getBillingSuperlogicaCatalogAdmin = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/superlogica/catalog", { params });
  return data?.data ?? { items: [] };
};

/** Master — enfileira import Superlógica (job assíncrono; poll em getBillingSuperlogicaImportJobAdmin) */
export const postBillingSuperlogicaImportAdmin = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/superlogica/import", payload || {}, {
    timeout: 60000,
  });
  return data?.data ?? {};
};

/** Status do job de importação Superlógica */
export const getBillingSuperlogicaImportJobAdmin = async (jobId) => {
  const id = encodeURIComponent(String(jobId || "").trim());
  const { data } = await apiClient.get(`/billing/admin/superlogica/import/jobs/${id}`, {
    timeout: 30000,
  });
  return data?.data ?? null;
};

/**
 * Enfileira e aguarda conclusão do import Superlógica (poll Redis via API).
 * @returns {Promise<{ importedPlans: string[], importedProducts: string[], planItemsImported?: number, errors?: any[], job?: object }>}
 */
export const runBillingSuperlogicaImportAdmin = async (payload = {}, { onStatus } = {}) => {
  const started = await postBillingSuperlogicaImportAdmin(payload);
  if (!started?.accepted || !started?.jobId) {
    return started;
  }
  const jobId = started.jobId;
  const maxMs = 10 * 60 * 1000;
  const intervalMs = 2000;
  const t0 = Date.now();
  while (Date.now() - t0 < maxMs) {
    const res = await getBillingSuperlogicaImportJobAdmin(jobId);
    const job = res?.job || res;
    const status = String(job?.status || "").toLowerCase();
    onStatus?.(status, job);
    if (status === "done") {
      return { ...(job?.result || {}), job, accepted: true, jobId };
    }
    if (status === "failed" || status === "cancelled") {
      const err = new Error(job?.errorMessage || `Importação Superlógica: ${status}`);
      err.job = job;
      throw err;
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  const err = new Error("Importação Superlógica demorou mais que o esperado. Recarregue Planos em instantes.");
  err.code = "poll_timeout";
  err.jobId = jobId;
  throw err;
};

/** Catálogo comercial — produtos/serviços (base Superlógica) */
export const getBillingCatalogProductsAdmin = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/catalog/products", { params });
  return data?.data ?? { items: [], default_cmv_keys: [] };
};

export const putBillingCatalogProductAdmin = async (id, payload = {}) => {
  const { data } = await apiClient.put(`/billing/admin/catalog/products/${encodeURIComponent(id)}`, payload);
  return data?.data ?? null;
};

export const getBillingCatalogPlansAdmin = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/catalog/plans", { params });
  return data?.data ?? { items: [] };
};

export const putBillingCatalogPlanAdmin = async (id, payload = {}) => {
  const { data } = await apiClient.put(`/billing/admin/catalog/plans/${encodeURIComponent(id)}`, payload);
  return data?.data ?? null;
};

export const postBillingCatalogPlanRecalcAdmin = async (id) => {
  const { data } = await apiClient.post(`/billing/admin/catalog/plans/${encodeURIComponent(id)}/recalc`);
  return data?.data ?? null;
};

/** Offer Composer — parâmetros de custo versionados + publish de ofertas */
export const getBillingOfferCostParamsAdmin = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/offer-composer/cost-params", { params });
  return data?.data ?? { items: [], active: null };
};

export const putBillingOfferCostParamsAdmin = async (payload = {}) => {
  const { data } = await apiClient.put("/billing/admin/offer-composer/cost-params", payload);
  return data?.data ?? null;
};

export const getBillingOfferVersionsAdmin = async () => {
  const { data } = await apiClient.get("/billing/admin/offer-composer/offers");
  return data?.data ?? { items: [] };
};

export const putBillingOfferDraftAdmin = async (payload = {}) => {
  const { data } = await apiClient.put("/billing/admin/offer-composer/offers", payload);
  return data?.data ?? null;
};

export const postBillingOfferPublishAdmin = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/offer-composer/publish", payload);
  return data?.data ?? null;
};

export const getPlatformDominioSubscriptions = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/subscriptions`);
  return data?.data ?? { subscriptions: [], merged_limits: {}, extra_charges: [] };
};

export const postPlatformDominioSyncSubscriptions = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.post(`/billing/admin/dominios/${id}/superlogica/sync-subscriptions`);
  return data?.data ?? null;
};

export const getPlatformDominioExtraCharges = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/extra-charges`);
  return data?.data ?? { items: [] };
};

export const putPlatformDominioExtraCharge = async (dominioId, payload = {}) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.put(`/billing/admin/dominios/${id}/extra-charges`, payload);
  return data?.data ?? null;
};

/** Console Financeiro — sync Superlógica + métricas (assíncrono; poll sync-runs) */
export const postPlatformFinanceSync = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/finance/sync", payload || {}, {
    timeout: 60_000,
    // 202 Accepted é sucesso
    validateStatus: (status) => (status >= 200 && status < 300) || status === 202,
  });
  return data?.data ?? null;
};

export const getPlatformFinanceSyncRuns = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/finance/sync-runs", {
    params,
    timeout: 30_000,
  });
  return data?.data ?? { items: [] };
};

export const getPlatformFinanceSyncRun = async (runId) => {
  const { data } = await apiClient.get("/billing/admin/finance/sync-runs", {
    params: { id: runId },
    timeout: 30_000,
  });
  return data?.data ?? null;
};

export const getPlatformFinanceDre = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/finance/dre", { params });
  return data?.data ?? null;
};

export const getPlatformFinanceDreEntries = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/finance/dre/entries", { params });
  return data?.data ?? null;
};

export const getPlatformFinanceIndicators = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/finance/indicators", { params });
  return data?.data ?? null;
};

export const getPlatformFinanceClientMargins = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/finance/client-margins", { params });
  return data?.data ?? { items: [], totals: {} };
};

export const getPlatformFinanceGoals = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/finance/goals", { params });
  return data?.data ?? { items: [] };
};

export const putPlatformFinanceGoals = async (payload = {}) => {
  const { data } = await apiClient.put("/billing/admin/finance/goals", payload || {});
  return data?.data ?? null;
};

export const getPlatformFinanceHeadcount = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/finance/headcount", { params });
  return data?.data ?? null;
};

export const putPlatformFinanceHeadcount = async (payload = {}) => {
  const { data } = await apiClient.put("/billing/admin/finance/headcount", payload || {});
  return data?.data ?? null;
};

export const getPlatformFinanceIncentives = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/finance/incentives", { params });
  return data?.data ?? null;
};

export const putPlatformFinanceIncentiveCycle = async (payload = {}) => {
  const { data } = await apiClient.put("/billing/admin/finance/incentives/cycle", payload || {});
  return data?.data ?? null;
};

export const putPlatformFinanceCorporateGoals = async (payload = {}) => {
  const { data } = await apiClient.put("/billing/admin/finance/incentives/corporate", payload || {});
  return data?.data ?? null;
};

export const getPlatformFinancePeople = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/finance/incentives/people", { params });
  return data?.data ?? { items: [], org_units: [], role_bands: [] };
};

export const putPlatformFinancePeople = async (payload = {}) => {
  const { data } = await apiClient.put("/billing/admin/finance/incentives/people", payload || {});
  return data?.data ?? null;
};

export const putPlatformFinanceOrgUnits = async (payload = {}) => {
  const { data } = await apiClient.put("/billing/admin/finance/incentives/org-units", payload || {});
  return data?.data ?? null;
};

export const getPlatformFinanceGoalCards = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/finance/incentives/cards", { params });
  return data?.data ?? { cards: [], people: [], shared_groups: [] };
};

export const putPlatformFinanceGoalCards = async (payload = {}) => {
  const { data } = await apiClient.put("/billing/admin/finance/incentives/cards", payload || {});
  return data?.data ?? null;
};

export const putPlatformFinanceDiscretionary = async (payload = {}) => {
  const { data } = await apiClient.put("/billing/admin/finance/incentives/discretionary", payload || {});
  return data?.data ?? null;
};

export const getPlatformFinanceBonus = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/finance/incentives/bonus", { params });
  return data?.data ?? null;
};

export const postPlatformFinanceBonusClose = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/finance/incentives/bonus/close", payload || {});
  return data?.data ?? null;
};

export const getPlatformFinanceChannel = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/finance/commissions", { params });
  return data?.data ?? { items: [], plans: [], tracks: [] };
};

export const putPlatformFinanceChannelPlans = async (payload = {}) => {
  const { data } = await apiClient.put("/billing/admin/finance/commissions/plans", payload || {});
  return data?.data ?? null;
};

export const putPlatformFinanceChannelPayouts = async (payload = {}) => {
  const { data } = await apiClient.put("/billing/admin/finance/commissions/payouts", payload || {});
  return data?.data ?? null;
};

export const getPlatformFinanceCommissionsEligibility = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/finance/commissions/eligibility", { params });
  return data?.data ?? { defaults: [] };
};

export const putPlatformFinanceCommissionsEligibility = async (payload = {}) => {
  const { data } = await apiClient.put("/billing/admin/finance/commissions/eligibility", payload || {});
  return data?.data ?? null;
};

export const getPlatformFinanceCommissionsAttributions = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/finance/commissions/attributions", { params });
  return data?.data ?? { items: [], people: [] };
};

export const putPlatformFinanceCommissionsAttributions = async (payload = {}) => {
  const { data } = await apiClient.put("/billing/admin/finance/commissions/attributions", payload || {});
  return data?.data ?? null;
};

export const getPlatformFinanceCommissionsSimulate = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/finance/commissions/simulate", { params });
  return data?.data ?? null;
};

export const postPlatformFinanceCommissionsPayoutsClose = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/finance/commissions/payouts/close", payload || {});
  return data?.data ?? null;
};

export const getPlatformFinanceCommissionsMyResults = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/finance/commissions/my-results", { params });
  return data?.data ?? { person: null, items: [], totals: {} };
};

export const getPlatformFinanceCommissionsTeamDefaults = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/finance/commissions/team-defaults", { params });
  return data?.data ?? { defaults: {}, people: [] };
};

export const putPlatformFinanceCommissionsTeamDefaults = async (payload = {}) => {
  const { data } = await apiClient.put("/billing/admin/finance/commissions/team-defaults", payload || {});
  return data?.data ?? null;
};

export const getPlatformFinanceCommissionsSales = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/finance/commissions/sales", { params });
  return data?.data ?? { items: [], people: [], defaults: {} };
};

export const getPlatformFinanceCommissionsSalesPreview = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/finance/commissions/sales/preview", { params });
  return data?.data ?? null;
};

export const postPlatformFinanceCommissionsSales = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/finance/commissions/sales", payload || {});
  return data?.data ?? null;
};

export const postPlatformFinanceCommissionsSalesCancel = async (saleId) => {
  const { data } = await apiClient.post(
    `/billing/admin/finance/commissions/sales/${encodeURIComponent(saleId)}/cancel`,
    {}
  );
  return data?.data ?? null;
};

export const getPlatformFinanceCommissionsMonthReport = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/finance/commissions/month-report", { params });
  return data?.data ?? { people: [], totals: {}, period_ym: null };
};

export const getPlatformFinanceBalance = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/finance/balance", { params });
  return data?.data ?? null;
};

export const putPlatformFinanceBalance = async (payload = {}) => {
  const { data } = await apiClient.put("/billing/admin/finance/balance", payload || {});
  return data?.data ?? null;
};

export const deletePlatformFinanceBalance = async (params = {}) => {
  const { data } = await apiClient.delete("/billing/admin/finance/balance", { params });
  return data?.data ?? null;
};

/** Livro-caixa / conciliação bancária */
export const getPlatformFinanceBankAccounts = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/finance/bank-accounts", { params });
  return data?.data ?? { items: [], totals: {} };
};

export const postPlatformFinanceBankAccountsSync = async (payload = {}) => {
  const { data } = await apiClient.post(
    "/billing/admin/finance/bank-accounts/sync-from-superlogica",
    payload || {},
    { timeout: 120_000 }
  );
  return data?.data ?? null;
};

export const postPlatformFinanceBankAccount = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/finance/bank-accounts", payload || {});
  return data?.data ?? null;
};

export const putPlatformFinanceBankAccount = async (id, payload = {}) => {
  const { data } = await apiClient.put(`/billing/admin/finance/bank-accounts/${id}`, payload || {});
  return data?.data ?? null;
};

export const deletePlatformFinanceBankAccount = async (id) => {
  const { data } = await apiClient.delete(`/billing/admin/finance/bank-accounts/${id}`);
  return data?.data ?? null;
};

export const getPlatformFinanceLedgerEntries = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/finance/ledger/entries", {
    params,
    timeout: 60_000,
  });
  return data?.data ?? { items: [], total: 0 };
};

export const postPlatformFinanceLedgerEntry = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/finance/ledger/entries", payload || {});
  return data?.data ?? null;
};

export const getPlatformFinanceEntryAdjustment = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/finance/entry-adjustments", { params });
  return data?.data ?? { item: null };
};

export const putPlatformFinanceEntryAdjustment = async (payload = {}) => {
  const { data } = await apiClient.put("/billing/admin/finance/entry-adjustments", payload || {});
  return data?.data ?? null;
};

export const postPlatformFinanceEntryAdjustmentRevert = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/finance/entry-adjustments/revert", payload || {});
  return data?.data ?? null;
};

export const postPlatformFinanceLedgerSync = async (payload = {}) => {
  const yearScope = payload?.year != null && payload?.year !== "";
  const { data } = await apiClient.post(
    "/billing/admin/finance/ledger/sync-from-superlogica",
    payload || {},
    // Sync anual percorre vários meses (expansão de cobranças) — timeout maior.
    { timeout: yearScope ? 540_000 : 180_000 }
  );
  return data?.data ?? null;
};

export const getPlatformFinanceBankStatements = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/finance/bank-statements", { params });
  return data?.data ?? { items: [] };
};

export const getPlatformFinanceBankStatement = async (id) => {
  const { data } = await apiClient.get(`/billing/admin/finance/bank-statements/${id}`);
  return data?.data ?? null;
};

export const postPlatformFinanceBankStatementImport = async (payload = {}) => {
  const { data } = await apiClient.post(
    "/billing/admin/finance/bank-statements/import",
    payload || {},
    { timeout: 120_000 }
  );
  return data?.data ?? null;
};

export const postPlatformFinanceBankStatementAutoMatch = async (id) => {
  const { data } = await apiClient.post(
    `/billing/admin/finance/bank-statements/${id}/auto-match`,
    {},
    { timeout: 120_000 }
  );
  return data?.data ?? null;
};

export const postPlatformFinanceReconcileConfirm = async (payload = {}) => {
  const batch = Array.isArray(payload?.items);
  const { data } = await apiClient.post(
    "/billing/admin/finance/reconcile/confirm",
    payload || {},
    { timeout: batch ? 120_000 : 30_000 }
  );
  return data?.data ?? null;
};

export const postPlatformFinanceReconcileIgnore = async (payload = {}) => {
  const batch = Array.isArray(payload?.items);
  const { data } = await apiClient.post(
    "/billing/admin/finance/reconcile/ignore",
    payload || {},
    { timeout: batch ? 120_000 : 30_000 }
  );
  return data?.data ?? null;
};

export const postPlatformFinanceReconcileUnmatch = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/finance/reconcile/unmatch", payload || {});
  return data?.data ?? null;
};

export const getPlatformFinanceAccountMap = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/finance/account-map", {
    params,
    timeout: params?.rebuild ? 120_000 : 60_000,
  });
  return data?.data ?? { items: [], dre_lines: [], dre_line_labels: {} };
};

export const putPlatformFinanceAccountMap = async (payload = {}) => {
  const { data } = await apiClient.put("/billing/admin/finance/account-map", payload || {});
  return data?.data ?? null;
};

export const getPlatformFinanceChurnEvents = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/finance/churn-events", { params });
  return data?.data ?? { items: [] };
};

export const postPlatformFinanceChurnEvent = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/finance/churn-events", payload || {});
  return data?.data ?? null;
};

export const getPlatformFinanceAnnualEvolution = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/finance/annual-evolution", { params });
  return data?.data ?? null;
};

export const getPlatformFinanceCashFlow = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/finance/cash-flow", { params });
  return data?.data ?? null;
};

export const getPlatformFinanceValuation = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/finance/valuation", { params });
  return data?.data ?? data;
};

export const putPlatformFinanceValuationAssumptions = async (payload = {}) => {
  const { data } = await apiClient.put("/billing/admin/finance/valuation/assumptions", payload || {});
  return data?.data ?? data;
};

/** Master — exclusão lógica: excluido=1 em xprobase.dominios (legado Xpro). */
export const excludePlatformDominio = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.post(`/billing/admin/dominios/${id}/exclude`, {});
  return data?.data ?? null;
};

/** Master — resumo de uso agregado (usage_daily_aggregates) por domínio. */
export const getPlatformDominioUsageSummary = async (dominioId, params = {}) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/usage/summary`, { params });
  return data?.data ?? null;
};

/** Master — auditoria evento a evento por métrica de uso (metricKey obrigatório). */
export const listPlatformDominioUsageEvents = async (dominioId, params = {}) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/usage/events`, { params });
  return data?.data ?? null;
};

/** Master — alias créditos de IA. */
export const listPlatformDominioAiCreditEvents = async (dominioId, params = {}) =>
  listPlatformDominioUsageEvents(dominioId, { ...params, metricKey: params.metricKey || "ai_credits" });

/** App — auditoria de uso do workspace autenticado (metricKey obrigatório). */
export const getBillingUsageEvents = async (params = {}) => {
  const { data } = await apiClient.get("/billing/usage/events", { params });
  return data?.data ?? null;
};

/** App — alias créditos de IA. */
export const getBillingAiCreditEvents = async (params = {}) =>
  getBillingUsageEvents({ ...params, metricKey: params.metricKey || "ai_credits" });

/** Master — faturas xprobase do domínio. */
export const listPlatformDominioInvoices = async (dominioId, params = {}) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/invoices`, { params });
  return data?.data ?? { items: [] };
};

/** Master — analytics de ofertas (billing_offer_events) por domínio. */
export const getPlatformDominioOfferAnalytics = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/offers/analytics`);
  return data?.data ?? { items: [] };
};

/** Master — vendas de passagens / funil / pagamentos (tenant) para segmento rodoviário. */
export const getPlatformDominioPassengerSales = async (dominioId, params = {}) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/passenger-transport/sales`, { params });
  return data?.data ?? null;
};

/** Master — conciliação Gupshup (API parceiro) × mensagens no tenant × métricas billing. */
export const getPlatformDominioGupshupReconciliation = async (dominioId, params = {}) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/gupshup/reconciliation`, { params });
  return data?.data ?? null;
};

/** Master — impacto da virada Meta WhatsApp Out/2026 (CMV Xvision × cliente) por workspace. */
export const getPlatformDominioWhatsappPricingImpact = async (dominioId, params = {}) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/whatsapp-pricing-impact`, { params });
  return data?.data ?? null;
};

/** Master — PDF timbrado da prévia de repasse WhatsApp Service (Out/2026). */
export const downloadPlatformDominioWhatsappPricingImpactPdf = async (dominioId, params = {}) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/whatsapp-pricing-impact/pdf`, {
    params,
  });
  return data?.data ?? null;
};

/** Master — auditoria de billing por domínio. */
export const listPlatformBillingAudit = async (dominioId, params = {}) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get("/billing/admin/audit", { params: { dominioId: id, ...params } });
  return data?.data ?? { items: [] };
};

export const getBillingAddonCatalogAdmin = async () => {
  const { data } = await apiClient.get("/billing/admin/addons");
  return data?.data ?? { items: [] };
};

export const upsertBillingAddonPriceAdmin = async (sku, payload = {}) => {
  const s = encodeURIComponent(String(sku || "").trim().toLowerCase());
  if (!s) throw new Error("sku é obrigatório.");
  const { data } = await apiClient.put(`/billing/admin/addons/${s}/prices`, payload || {});
  return data?.data ?? null;
};

/** Master — lê `workspace.segment` no tenant vinculado ao domínio (xprobase.dominios.bd). */
export const getPlatformDominioWorkspaceSegment = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/workspace-segment`);
  return data?.data ?? null;
};

/** Master — grava `workspace.segment` no tenant (configuracoes). */
export const setPlatformDominioWorkspaceSegment = async (dominioId, segment) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.put(`/billing/admin/dominios/${id}/workspace-segment`, { segment });
  return data?.data ?? null;
};

/** Master — catálogo unificado de segmentos (setup + console). */
export const listPlatformWorkspaceSegmentCatalog = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/workspace-segments", { params });
  return data?.data ?? { items: [] };
};

export const createPlatformWorkspaceSegmentCatalog = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/workspace-segments", payload || {});
  return data?.data ?? null;
};

export const updatePlatformWorkspaceSegmentCatalog = async (segmentKey, payload = {}) => {
  const k = encodeURIComponent(String(segmentKey || "").trim());
  if (!k) throw new Error("segmentKey é obrigatório.");
  const { data } = await apiClient.put(`/billing/admin/workspace-segments/${k}`, payload || {});
  return data?.data ?? null;
};

export const deletePlatformWorkspaceSegmentCatalog = async (segmentKey) => {
  const k = encodeURIComponent(String(segmentKey || "").trim());
  if (!k) throw new Error("segmentKey é obrigatório.");
  const { data } = await apiClient.delete(`/billing/admin/workspace-segments/${k}`);
  return data?.data ?? null;
};

/** Workspaces modelo (templates clonáveis) por segmento. */
export const listPlatformWorkspaceModelTemplates = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/workspace-model-templates", { params });
  return data?.data ?? { items: [] };
};

export const listPartnerWorkspaceModelTemplates = async (params = {}) => {
  const { data } = await apiClient.get("/billing/partner/workspace-model-templates", { params });
  return data?.data ?? { items: [] };
};

export const createPlatformWorkspaceModelTemplate = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/workspace-model-templates", payload || {});
  return data?.data ?? null;
};

export const updatePlatformWorkspaceModelTemplate = async (id, payload = {}) => {
  const n = encodeURIComponent(String(id || "").trim());
  if (!n) throw new Error("id é obrigatório.");
  const { data } = await apiClient.put(`/billing/admin/workspace-model-templates/${n}`, payload || {});
  return data?.data ?? null;
};

export const publishPlatformWorkspaceModelTemplate = async (id) => {
  const n = encodeURIComponent(String(id || "").trim());
  if (!n) throw new Error("id é obrigatório.");
  const { data } = await apiClient.post(`/billing/admin/workspace-model-templates/${n}/publish`);
  return data?.data ?? null;
};

export const unpublishPlatformWorkspaceModelTemplate = async (id) => {
  const n = encodeURIComponent(String(id || "").trim());
  if (!n) throw new Error("id é obrigatório.");
  const { data } = await apiClient.post(`/billing/admin/workspace-model-templates/${n}/unpublish`);
  return data?.data ?? null;
};

export const archivePlatformWorkspaceModelTemplate = async (id) => {
  const n = encodeURIComponent(String(id || "").trim());
  if (!n) throw new Error("id é obrigatório.");
  const { data } = await apiClient.delete(`/billing/admin/workspace-model-templates/${n}`);
  return data?.data ?? null;
};

/** Master — catálogo de templates de fluxo de ChatBot (console plataforma). */
export const listPlatformChatbotFlowTemplates = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/chatbot-flow-templates", { params });
  return data?.data ?? { items: [] };
};

export const getPlatformChatbotFlowTemplate = async (id) => {
  const tplId = encodeURIComponent(String(id || "").trim());
  if (!tplId) throw new Error("id é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/chatbot-flow-templates/${tplId}`);
  return data?.data ?? null;
};

export const createPlatformChatbotFlowTemplate = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/chatbot-flow-templates", payload || {});
  return data?.data ?? null;
};

export const updatePlatformChatbotFlowTemplate = async (id, payload = {}) => {
  const tplId = encodeURIComponent(String(id || "").trim());
  if (!tplId) throw new Error("id é obrigatório.");
  const { data } = await apiClient.put(`/billing/admin/chatbot-flow-templates/${tplId}`, payload || {});
  return data?.data ?? null;
};

export const deletePlatformChatbotFlowTemplate = async (id) => {
  const tplId = encodeURIComponent(String(id || "").trim());
  if (!tplId) throw new Error("id é obrigatório.");
  const { data } = await apiClient.delete(`/billing/admin/chatbot-flow-templates/${tplId}`);
  return data?.data ?? null;
};

/** Master — lê `workspace.ai.context.v1` no tenant vinculado ao domínio. */
export const getPlatformDominioAiWorkspaceContext = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/ai-workspace-context`);
  return data?.data ?? null;
};

/** Master — merge parcial em `workspace.ai.context.v1` no tenant. */
export const setPlatformDominioAiWorkspaceContext = async (dominioId, patch) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.put(`/billing/admin/dominios/${id}/ai-workspace-context`, patch || {});
  return data?.data ?? null;
};

/** Billing — limites/preços do plano ativo (sem catálogo admin completo). */
export const getBillingPlanCurrentCatalog = async () => {
  const { data } = await apiClient.get("/billing/plan/current-catalog");
  return data?.data ?? { items: [] };
};

export const getBillingInvoices = async () => {
  const { data } = await apiClient.get("/billing/invoices");
  return data?.data ?? { items: [] };
};

export const postBillingCheckoutIntent = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/checkout/intent", payload || {});
  return data?.data ?? null;
};

export const getBillingOffers = async (params = {}) => {
  const { data } = await apiClient.get("/billing/offers", { params });
  return data?.data ?? { items: [] };
};

export const getBillingOfferAnalytics = async () => {
  const { data } = await apiClient.get("/billing/offers/analytics");
  return data?.data ?? { items: [] };
};

export const viewBillingOffer = async (offerId) => {
  const id = encodeURIComponent(String(offerId || "").trim());
  if (!id) throw new Error("offerId é obrigatório.");
  const { data } = await apiClient.post(`/billing/offers/${id}/view`, {});
  return data?.data ?? null;
};

export const clickBillingOffer = async (offerId) => {
  const id = encodeURIComponent(String(offerId || "").trim());
  if (!id) throw new Error("offerId é obrigatório.");
  const { data } = await apiClient.post(`/billing/offers/${id}/click`, {});
  return data?.data ?? null;
};

export const acceptBillingOffer = async (offerId) => {
  const id = encodeURIComponent(String(offerId || "").trim());
  if (!id) throw new Error("offerId é obrigatório.");
  const { data } = await apiClient.post(`/billing/offers/${id}/accept`, {});
  return data?.data ?? null;
};

export const seedOfficialBillingAddonPackages = async () => {
  const { data } = await apiClient.post("/billing/admin/addons/seed-official", {});
  return data?.data ?? null;
};

export const seedTelephonySmsAddonPrices = async () => {
  const { data } = await apiClient.post("/billing/admin/addons/seed-telephony-sms", {});
  return data?.data ?? null;
};

/**
 * Billing Admin - vincula assinatura de um domínio a um plano ou perfil sem cobrança.
 * payload esperado:
 * { billingProfile?: 'commercial'|'internal'|'development', planSku?, version?, dominioId? | company?, billingMode?, enforceLimits?, status?, resellerId?, billingAccountId? }
 * Perfil internal/development: cancela assinatura ativa; planSku não é obrigatório.
 */
export const assignBillingSubscription = async (payload) => {
  const { data } = await apiClient.post("/billing/admin/subscriptions/assign", payload || {});
  return data?.data ?? null;
};

export const createBillingPlan = async (payload) => {
  const { data } = await apiClient.post("/billing/admin/plans", payload || {});
  return data?.data ?? null;
};

export const createBillingPlanVersion = async (sku, payload) => {
  const { data } = await apiClient.post(`/billing/admin/plans/${encodeURIComponent(sku)}/versions`, payload || {});
  return data?.data ?? null;
};

export const updateBillingPlanLimits = async (sku, version, limits) => {
  const { data } = await apiClient.put(
    `/billing/admin/plans/${encodeURIComponent(sku)}/versions/${encodeURIComponent(version)}/limits`,
    { limits: Array.isArray(limits) ? limits : [] }
  );
  return data?.data ?? null;
};

/**
 * Billing Admin — MRR e setup (centavos) por audiência em plan_version_prices.
 * @param {string} sku
 * @param {number|string} version
 * @param {{ audience: 'end_customer'|'reseller', amountMrrCents: number, amountSetupCents: number, currencyCode?: string }} payload
 */
export const upsertBillingPlanVersionPrices = async (sku, version, payload) => {
  const { data } = await apiClient.put(
    `/billing/admin/plans/${encodeURIComponent(sku)}/versions/${encodeURIComponent(version)}/prices`,
    payload || {}
  );
  return data?.data ?? null;
};

export const consumeBillingMetric = async (payload) => {
  const { data } = await apiClient.post("/billing/usage/consume", payload || {});
  return data?.data ?? null;
};

/**
 * Apaga no provedor quando o canal suporta e marca a mensagem como excluída no histórico.
 */
export const deleteConversationMessage = async (conversationId, messageId) => {
  if (conversationId == null || messageId == null) {
    throw new Error("conversationId e messageId são obrigatórios.");
  }
  return fetchData(
    `/conversations/${encodeURIComponent(String(conversationId))}/messages/${encodeURIComponent(String(messageId))}`,
    "DELETE"
  );
};

export const getConversationInsights = async (id) => {
  const res = await fetchData(`/conversations/${id}/insights`, "GET");
  return res?.data ?? res;
};

/** Gera título de tarefa e itens de checklist a partir das mensagens da solicitação (IA). */
export const generateConversationTaskChecklist = async (conversationId, body = {}) => {
  if (!conversationId) {
    throw new Error("conversationId é obrigatório para gerar tarefas com IA.");
  }
  const res = await fetchData(
    `/conversations/${encodeURIComponent(String(conversationId))}/generate-task-checklist`,
    "POST",
    body
  );
  return res?.data ?? res;
};

/** Sessão do assistente "Tirar dúvidas" (RAG tipo/subtipo + contexto da solicitação). */
export const createConversationTipoAssistantSession = async (conversationId) => {
  const res = await fetchData(
    `/conversations/${encodeURIComponent(String(conversationId))}/assistant/tipo/session`,
    "POST",
    {}
  );
  return res?.data ?? res;
};

export const chatConversationTipoAssistant = async (conversationId, body) => {
  const res = await fetchData(
    `/conversations/${encodeURIComponent(String(conversationId))}/assistant/tipo/chat`,
    "POST",
    body
  );
  return res?.data ?? res;
};

/** Materiais das fontes de conhecimento (tipo/subtipos) para navegação no chat. */
export const fetchConversationKnowledgeMaterials = async (conversationId) => {
  const res = await fetchData(
    `/conversations/${encodeURIComponent(String(conversationId))}/knowledge-materials`,
    "GET"
  );
  return res?.data ?? res;
};

/** Analytics agregado de um atendente (solicitações com l.id_usuario = id). Query: start, end (YYYY-MM-DD). */
export const getUserOperatorAnalytics = async (userId, query = {}) => {
  const q = new URLSearchParams();
  if (query.start) q.set("start", query.start);
  if (query.end) q.set("end", query.end);
  if (query.detail) q.set("detail", query.detail);
  const qs = q.toString();
  const suffix = qs ? `?${qs}` : "";
  const res = await fetchData(`/users/${encodeURIComponent(String(userId))}/operator-analytics${suffix}`, "GET");
  return res?.data ?? res;
};

/** Presign S3 para upload de PDF (PDA/DISC etc.) vinculado ao usuário. */
export const presignUserOperatorDocument = async (userId, body) => {
  const res = await fetchData(
    `/users/${encodeURIComponent(String(userId))}/operator-documents/presign`,
    "POST",
    body
  );
  return res?.data ?? {};
};

/** Após PUT no S3, dispara extração, chunking e embeddings. */
export const ingestUserOperatorDocument = async (userId, body) => {
  const res = await fetchData(
    `/users/${encodeURIComponent(String(userId))}/operator-documents/ingest`,
    "POST",
    body
  );
  return res?.data ?? res;
};

export const listUserOperatorDocuments = async (userId) => {
  const res = await fetchData(`/users/${encodeURIComponent(String(userId))}/operator-documents`, "GET");
  const d = res?.data;
  if (d && Array.isArray(d.items)) return d.items;
  if (Array.isArray(d)) return d;
  return [];
};

export const deleteUserOperatorDocument = async (userId, docId) => {
  return fetchData(
    `/users/${encodeURIComponent(String(userId))}/operator-documents/${encodeURIComponent(String(docId))}`,
    "DELETE"
  );
};

/**
 * Gera explicação/insights de IA para métricas do dashboard.
 * @param {string} scope - ex.: "atendimento"
 * @param {object} payload - dados da métrica + contexto do período/filtros
 */
export const getDashboardMetricInsights = async (scope, payload = {}) => {
  if (!scope) throw new Error("scope é obrigatório para gerar insights de métrica.");
  const partner = getPartnerDashboardAiContext();
  if (partner.enabled) {
    const section = encodeURIComponent(String(scope || "").trim());
    const params =
      partner.dominioIds.length > 0
        ? { dominio_ids: partner.dominioIds.map((x) => String(x)).join(",") }
        : undefined;
    const { data } = await apiClient.post(
      `/billing/partner/dashboard/${section}/metric-insights`,
      payload,
      { params }
    );
    return data?.data ?? data;
  }
  const res = await fetchData(`/dashboard/${scope}/metric-insights`, "POST", payload);
  return res?.data ?? res;
};

/**
 * Chat com IA restrito a uma métrica do dashboard.
 * @param {string} scope - ex.: "atendimento" | "clientes" | "produtos" | "vendas"
 * @param {object} payload - basePayload + userMessage + history
 */
export const chatDashboardMetric = async (scope, payload = {}) => {
  if (!scope) throw new Error("scope é obrigatório para conversar sobre a métrica.");
  const partner = getPartnerDashboardAiContext();
  if (partner.enabled) {
    const section = encodeURIComponent(String(scope || "").trim());
    const params =
      partner.dominioIds.length > 0
        ? { dominio_ids: partner.dominioIds.map((x) => String(x)).join(",") }
        : undefined;
    const { data } = await apiClient.post(
      `/billing/partner/dashboard/${section}/metric-chat`,
      payload,
      { params }
    );
    return data?.data ?? data;
  }
  const res = await fetchData(`/dashboard/${scope}/metric-chat`, "POST", payload);
  return res?.data ?? res;
};

export const createAssistantSession = async (payload = {}) => {
  if (isPartnerAssistantContext()) {
    const partnerCtx = getPartnerDashboardAiContext();
    const body = {
      ...(payload || {}),
      ...(partnerCtx.dominioIds.length ? { dominio_ids: partnerCtx.dominioIds } : {}),
    };
    const { data: resp } = await apiClient.post("/billing/partner/assistant/session", body);
    return resp?.data ?? resp;
  }
  const res = await fetchData(`/assistant/session`, "POST", payload);
  return res?.data ?? res;
};

export const chatWithAssistant = async (payload = {}) => {
  if (isPartnerAssistantContext()) {
    const partnerCtx = getPartnerDashboardAiContext();
    const body = {
      ...(payload || {}),
      ...(partnerCtx.dominioIds.length ? { dominio_ids: partnerCtx.dominioIds } : {}),
    };
    const { data: resp } = await apiClient.post("/billing/partner/assistant/chat", body);
    return resp?.data ?? resp;
  }
  const res = await fetchData(`/assistant/chat`, "POST", payload);
  return res?.data ?? res;
};

/**
 * Estado de um turno agêntico. O POST /assistant/chat devolve `turnId` e a
 * resposta chega por aqui, junto com os passos já executados.
 */
export const getAssistantTurn = async (turnId) => {
  const res = await fetchData(`/assistant/turns/${encodeURIComponent(turnId)}`, "GET");
  return res?.data ?? res;
};

export const cancelAssistantTurn = async (turnId) => {
  const res = await fetchData(`/assistant/turns/${encodeURIComponent(turnId)}/cancel`, "POST", {});
  return res?.data ?? res;
};

export const confirmAssistantMutation = async (proposalId) => {
  const res = await fetchData(
    `/assistant/mutations/${encodeURIComponent(proposalId)}/confirm`,
    "POST",
    {}
  );
  return res?.data ?? res;
};

export const cancelAssistantMutation = async (proposalId) => {
  const res = await fetchData(
    `/assistant/mutations/${encodeURIComponent(proposalId)}/cancel`,
    "POST",
    {}
  );
  return res?.data ?? res;
};

export const generateBannerDraftWithAi = async (payload = {}, options = {}) => {
  const res = await fetchData(`/banners/ai-draft`, "POST", payload);
  const data = res?.data ?? res;

  if (data?.accepted && data?.jobId) {
    return pollBannerAiDraftJob(data.jobId, options);
  }

  return data;
};

const BANNER_AI_POLL_INTERVAL_MS = 2500;
const BANNER_AI_POLL_MAX_MS = 300000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function pollBannerAiDraftJob(jobId, { onStatus } = {}) {
  const started = Date.now();
  while (Date.now() - started < BANNER_AI_POLL_MAX_MS) {
    const job = await getBannerAiDraftJob(jobId);
    const status = String(job?.status || "").toLowerCase();
    onStatus?.(status, job);

    if (status === "completed") {
      return job?.result || job;
    }
    if (status === "failed" || status === "cancelled") {
      const err = new Error(
        job?.errorMessage || "Não foi possível gerar o banner com IA."
      );
      err.job = job;
      throw err;
    }

    await sleep(BANNER_AI_POLL_INTERVAL_MS);
  }

  const err = new Error(
    "A geração do banner está demorando mais que o esperado. Tente novamente em instantes."
  );
  err.code = "poll_timeout";
  throw err;
};

/** Acompanha job de geração de banner com IA (Redis). */
export const getBannerAiDraftJob = async (jobId) => {
  const id = encodeURIComponent(String(jobId || ""));
  const res = await fetchData(`/banners/ai-draft/jobs/${id}`, "GET");
  return res?.data ?? res;
};

const MOCK_TRAINING_HUB = [
  {
    id: 901,
    titulo: "Onboarding (mock CMS)",
    slug: "onboarding-mock-cms",
    ordem: 1,
    updated_at: "2026-03-01",
    training: {
      format: "training_kb.v1",
      intro: "Dados estáticos quando REACT_APP_MOCK_CMS=1.",
      modules: [
        {
          id: "mm1",
          title: "Links úteis",
          order: 0,
          items: [
            {
              id: "mi1",
              kind: "link",
              title: "Xpro",
              url: "https://xpro.me",
              notes: "",
              order: 0,
            },
          ],
        },
      ],
    },
  },
];

/** Lista treinamentos ativos (materiais de ajuda) para o hub. */
export const fetchTrainingHub = async () => {
  if (typeof process !== "undefined" && process.env.REACT_APP_MOCK_CMS === "1") {
    await new Promise((r) => setTimeout(r, 120));
    return MOCK_TRAINING_HUB;
  }
  const res = await fetchData("/training/hub", "GET");
  const raw = res?.data ?? res;
  return Array.isArray(raw) ? raw : [];
};

/** Sessão de IA escopada a um treinamento (conteúdo cadastrado). */
export const createTrainingAssistantSession = async (payload = {}) => {
  const res = await fetchData(`/assistant/training/session`, "POST", payload);
  return res?.data ?? res;
};

export const chatTrainingAssistant = async (payload = {}) => {
  const res = await fetchData(`/assistant/training/chat`, "POST", payload);
  return res?.data ?? res;
};

export const getConversationById = async (id) => {
  const res = await fetchData(`/conversations/${id}`, "GET");
  return res?.data ?? res;
};

export const getRequestDetails = async (requestId) => {
  const res = await fetchData(`/requests/${encodeURIComponent(String(requestId))}`, "GET");
  return res?.data ?? res;
};

/**
 * Verifica se existem conversas abertas (status Pendente ou Em Atendimento)
 * para um determinado telefone de cliente, opcionalmente filtrando por projeto.
 * Usamos a própria listagem de /conversations com filtros de status.
 */
export const checkOpenConversationsByPhone = async ({ phone, projectId } = {}) => {
  const sp = new URLSearchParams();
  if (phone) {
    sp.set("search", String(phone).trim());
  }
  // Foco em relacionamento (chat) e apenas status 0/1
  sp.set("tipo_scope", "relacionamento");
  sp.set("filters", ["Pendente", "Em Atendimento"].join(","));
  sp.set("limit", "10");
  if (projectId != null) {
    sp.set("ids_projetos", String(projectId));
  }
  const qs = sp.toString();
  const res = await fetchData(`/conversations${qs ? `?${qs}` : ""}`, "GET");
  const list = res?.data ?? res ?? [];
  return Array.isArray(list) ? list : [];
};

/**
 * Listagem de conversas com os mesmos query params da tela de chat.
 * Útil para painéis (ex.: dashboard ao vivo). `default_date_window_days=0` evita cortar por data.
 */
/** Séries diárias de solicitações (leads) atribuídas ao chatbot URA — GET /chatbot/analytics/conversation-series */
export const getChatbotConversationSeries = async (userId, opts = {}) => {
  const qs = new URLSearchParams();
  qs.set("user_id", String(userId));
  qs.set("days", String(Math.min(Math.max(Number(opts.days) || 30, 1), 90)));
  if (opts.tipo_scope) qs.set("tipo_scope", String(opts.tipo_scope));
  const res = await fetchData(`/chatbot/analytics/conversation-series?${qs.toString()}`, "GET");
  return res?.data ?? {};
};

/** Facetas do menu de filtros do atendimento (POST /conversations/filter-counts). */
export const fetchConversationFilterCounts = async ({
  advanced,
  filters,
  atendimentoLastSeen,
} = {}) => {
  const res = await fetchData("/conversations/filter-counts", "POST", {
    advanced: advanced && typeof advanced === "object" ? advanced : {},
    filters: Array.isArray(filters) ? filters.filter((f) => typeof f === "string") : [],
    atendimentoLastSeen:
      atendimentoLastSeen && typeof atendimentoLastSeen === "object"
        ? atendimentoLastSeen
        : {},
  });
  const data = res?.data ?? res ?? {};
  return {
    byLabel: data.byLabel && typeof data.byLabel === "object" ? data.byLabel : {},
    unreadCapped: Boolean(data.unreadCapped),
    timeout: Boolean(data.timeout),
    windowDays: Number(data.windowDays) || 30,
  };
};

export const getConversationsList = async (opts = {}) => {
  const qs = new URLSearchParams();
  const lim = Math.min(parseInt(opts.limit, 10) || 50, 50);
  qs.set("limit", String(lim));
  const ts = String(opts.tipo_scope || "relacionamento").trim().toLowerCase();
  const tipoScopeParam =
    ts === "comercial" || ts === "commercial" || ts === "0"
      ? "comercial"
      : ts === "todos" || ts === "ambos" || ts === "all"
        ? "todos"
        : "relacionamento";
  qs.set("tipo_scope", tipoScopeParam);
  qs.set("default_date_window_days", String(opts.default_date_window_days ?? 0));
  if (Array.isArray(opts.filters) && opts.filters.length) {
    qs.set("filters", opts.filters.join(","));
  }
  if (opts.tipo_responsavel) {
    qs.set("tipo_responsavel", String(opts.tipo_responsavel));
  }
  if (opts.data_inicio) qs.set("data_inicio", String(opts.data_inicio));
  if (opts.data_fim) qs.set("data_fim", String(opts.data_fim));
  if (opts.date_field) qs.set("date_field", String(opts.date_field));

  const setCsv = (key, value) => {
    if (value == null || value === "") return;
    if (Array.isArray(value)) {
      const arr = value.map((x) => String(x ?? "").trim()).filter(Boolean);
      if (arr.length) qs.set(key, arr.join(","));
      return;
    }
    const s = String(value).trim();
    if (s) qs.set(key, s);
  };

  setCsv("ids_usuarios", opts.ids_usuarios);
  setCsv("ids_agentes", opts.ids_agentes);
  setCsv("ids_usuarios_interacao", opts.ids_usuarios_interacao);
  setCsv("ids_agentes_interacao", opts.ids_agentes_interacao);
  setCsv("ids_origens", opts.ids_origens);
  setCsv("ids_tipos", opts.ids_tipos);
  setCsv("ids_subtipo1", opts.ids_subtipo1);
  setCsv("ids_subtipo2", opts.ids_subtipo2);
  setCsv("ids_subtipo3", opts.ids_subtipo3);
  setCsv("tags", opts.tags);
  setCsv("ids_produtos", opts.ids_produtos);
  setCsv("ids_campaigns", opts.ids_campaigns);
  setCsv("ids_whatsapp_campaigns", opts.ids_whatsapp_campaigns);
  setCsv("ids_motivos", opts.ids_motivos);
  setCsv("ids_potenciais", opts.ids_potenciais);
  if (opts.ops_preset) qs.set("ops_preset", String(opts.ops_preset));
  if (opts.ops_preset === "stale" && opts.stale_days != null) {
    qs.set("stale_days", String(opts.stale_days));
  }

  const query = qs.toString();
  const res = await fetchData(`/conversations${query ? `?${query}` : ""}`, "GET");
  const list = Array.isArray(res?.data) ? res.data : [];
  return {
    list,
    pagination: res?.pagination || { hasMore: false, nextCursor: null },
    timeout: Boolean(res?.pagination?.timeout),
    timeoutMessage: res?.pagination?.timeoutMessage || null,
  };
};

/** Usuários humanos (exclui chatbots), com flags `is_online` quando disponível no banco. */
export const getHumanOperators = async (limit = 200) => {
  const res = await fetchData(`/users?limit=${encodeURIComponent(String(limit))}&tipo_responsavel=humanos`, "GET");
  const rows = Array.isArray(res?.data) ? res.data : [];
  return { operators: rows, pagination: res?.pagination };
};

/** Encerra sessões JWT do usuário alvo (admin/master ou permissão de editar Usuários). */
export const postForceLogoutUser = async (userId) => {
  const res = await fetchData(`/users/${encodeURIComponent(String(userId))}/force-logout`, "POST", {});
  return res?.data ?? res;
};

export const generateSuggestedReply = async (conversationId, body = {}) => {
  if (!conversationId) {
    throw new Error(
      "conversationId é obrigatório para gerar sugestão de resposta."
    );
  }
  const res = await fetchData(
    `/conversations/${conversationId}/suggested-reply`,
    "POST",
    body
  );
  return res?.data ?? res;
};

export const getConversationMessageSuggestions = async (conversationId, params = {}) => {
  if (!conversationId) {
    throw new Error("conversationId é obrigatório para obter sugestões de mensagens.");
  }
  const qs = new URLSearchParams();
  if (params.originId != null) qs.set("originId", params.originId);
  if (params.typeId != null) qs.set("typeId", params.typeId);
  const query = qs.toString();
  const endpoint = `/conversations/${conversationId}/message-suggestions${query ? `?${query}` : ""}`;
  const res = await fetchData(endpoint, "GET");
  return res?.data ?? res;
};

/** Carrinho da solicitação (mesma tabela do e-commerce) */
export const putConversationCart = async (conversationId, items) => {
  if (!conversationId) throw new Error("conversationId obrigatório.");
  const res = await fetchData(
    `/conversations/${encodeURIComponent(String(conversationId))}/cart`,
    "PUT",
    { items: Array.isArray(items) ? items : [] }
  );
  return res?.data ?? res;
};

/** Intenção de pagamento para o atendente (não substitui /ecom/payments/initialize) */
export const postConversationPaymentIntent = async (conversationId, body) => {
  if (!conversationId) throw new Error("conversationId obrigatório.");
  const res = await fetchData(
    `/conversations/${encodeURIComponent(String(conversationId))}/payments/intent`,
    "POST",
    body || {}
  );
  return res?.data ?? res;
};

export const getConversationShippingOptions = async (conversationId, zip) => {
  if (!conversationId) throw new Error("conversationId obrigatório.");
  const cep = String(zip || "").replace(/\D/g, "");
  const res = await fetchData(
    `/conversations/${encodeURIComponent(String(conversationId))}/shipping-options?zip=${encodeURIComponent(cep)}`,
    "GET"
  );
  return res?.data ?? res;
};

export const putConversationShipping = async (conversationId, body) => {
  if (!conversationId) throw new Error("conversationId obrigatório.");
  const res = await fetchData(
    `/conversations/${encodeURIComponent(String(conversationId))}/shipping`,
    "PUT",
    body || {}
  );
  return res?.data ?? res;
};

export const getChannelEmailDomainStatus = async (channelId) => {
  if (!channelId) throw new Error("channelId é obrigatório para buscar status de domínio de e-mail.");
  const res = await fetchData(`/channels/${channelId}/email-domain/status`, "GET");
  return res?.data ?? res;
};

export const requestChannelEmailDomainSend = async (channelId) => {
  if (!channelId) throw new Error("channelId é obrigatório para solicitar autorização de envio.");
  const res = await fetchData(`/channels/${channelId}/email-domain/request-send`, "POST");
  return res?.data ?? res;
};

export const approveChannelEmailDomainSendGrant = async (channelId, requesterDatabaseName) => {
  if (!channelId || !requesterDatabaseName) {
    throw new Error("channelId e requesterDatabaseName são obrigatórios para aprovar envio.");
  }
  const requester = encodeURIComponent(String(requesterDatabaseName));
  const res = await fetchData(
    `/channels/${channelId}/email-domain/send-grants/${requester}/approve`,
    "POST"
  );
  return res?.data ?? res;
};

export const rejectChannelEmailDomainSendGrant = async (channelId, requesterDatabaseName) => {
  if (!channelId || !requesterDatabaseName) {
    throw new Error("channelId e requesterDatabaseName são obrigatórios para recusar envio.");
  }
  const requester = encodeURIComponent(String(requesterDatabaseName));
  const res = await fetchData(
    `/channels/${channelId}/email-domain/send-grants/${requester}/reject`,
    "POST"
  );
  return res?.data ?? res;
};

export const revokeChannelEmailDomainSendGrant = async (channelId, requesterDatabaseName) => {
  if (!channelId || !requesterDatabaseName) {
    throw new Error("channelId e requesterDatabaseName são obrigatórios para revogar envio.");
  }
  const requester = encodeURIComponent(String(requesterDatabaseName));
  const res = await fetchData(
    `/channels/${channelId}/email-domain/send-grants/${requester}`,
    "DELETE"
  );
  return res?.data ?? res;
};

export const sendChannelEmailDomainTest = async (channelId) => {
  if (!channelId) {
    throw new Error("channelId é obrigatório para enviar e-mail de teste do domínio de e-mail.");
  }
  const res = await fetchData(`/channels/${channelId}/email-domain/test-send`, "POST");
  return res?.data ?? res;
};

export const getChannelWebsiteConfig = async (channelId) => {
  if (!channelId) throw new Error("channelId é obrigatório para obter configuração do website.");
  const res = await fetchData(`/channels/${channelId}/website`, "GET");
  return res?.data ?? res;
};

export const updateChannelWebsiteConfig = async (channelId, body) => {
  if (!channelId) throw new Error("channelId é obrigatório para atualizar configuração do website.");
  const res = await fetchData(`/channels/${channelId}/website`, "PUT", body || {});
  return res?.data ?? res;
};

export const regenerateChannelWebsiteTempUrl = async (channelId) => {
  if (!channelId) throw new Error("channelId é obrigatório para regenerar URL temporária do website.");
  const res = await fetchData(`/channels/${channelId}/website/temp-url/regenerate`, "POST");
  return res?.data ?? res;
};

export const suggestChannelWebsiteTheme = async (channelId, prompt) => {
  if (!channelId) throw new Error("channelId é obrigatório para sugerir tema do website.");
  const res = await fetchData(`/channels/${channelId}/website/theme/suggest`, "POST", {
    prompt: String(prompt || "").trim(),
  });
  return res?.data ?? res;
};

export const getChannelWebsiteDomainStatus = async (channelId) => {
  if (!channelId) throw new Error("channelId é obrigatório para buscar status de domínio do website.");
  const res = await fetchData(`/channels/${channelId}/website/domain/status`, "GET");
  return res?.data ?? res;
};

export const setupChannelWebsiteDomain = async (channelId) => {
  if (!channelId) throw new Error("channelId é obrigatório para configurar domínio do website.");
  const res = await fetchData(`/channels/${channelId}/website/domain/setup`, "POST");
  return res?.data ?? res;
};

export const listChannelWebsiteAudit = async (channelId) => {
  if (!channelId) throw new Error("channelId é obrigatório para listar histórico do website.");
  const res = await fetchData(`/channels/${channelId}/website/audit`, "GET");
  return res?.data ?? res;
};

export const getChannelTemplates = async (channelId) => {
  const res = await fetchData(`/channels/${channelId}/templates`, "GET");
  return res?.data ?? res;
};

export const importGupshupTemplates = async (channelId) => {
  const res = await fetchData(`/channels/${channelId}/gupshup/import-templates`, "POST");
  return res?.data ?? res;
};

export const createGupshupTemplate = async (channelId, payload) => {
  const res = await fetchData(`/channels/${channelId}/gupshup/templates`, "POST", payload || {});
  return res?.data ?? res;
};

export const publishWhatsappTicketFlow = async (channelId, body) => {
  const res = await fetchData(`/channels/${channelId}/whatsapp-flows/ticket-sale`, "POST", body || {});
  return res?.data ?? res;
};

export const getWhatsappTicketFlowUsage = async (channelId) => {
  const res = await fetchData(`/channels/${channelId}/whatsapp-flows/ticket-sale`, "GET");
  return res?.data ?? res;
};

export const removeWhatsappTicketFlow = async (channelId) => {
  const res = await fetchData(`/channels/${channelId}/whatsapp-flows/ticket-sale`, "DELETE");
  return res?.data ?? res;
};

/** Valida credenciais Gupshup (mensagens e/ou Partner voz). Body: { which?: "messaging"|"voice"|"both", messaging?, voice? } */
export const testGupshupChannelIntegrations = async (channelId, body) => {
  if (!channelId) {
    throw new Error("ID do canal é obrigatório para testar integrações Gupshup.");
  }
  const res = await fetchData(`/channels/${channelId}/gupshup/test-integrations`, "POST", body || {});
  return res?.data ?? res;
};

/** Valida SMTP (envio) e IMAP (recebimento) de canal tipo 30. channelId opcional se credenciais vierem no body. */
export const testSmtpChannelCredentials = async (channelId, body) => {
  const path =
    channelId != null && String(channelId).trim() !== ""
      ? `/channels/${channelId}/smtp/test-credentials`
      : "/channels/smtp/test-credentials";
  const res = await fetchData(path, "POST", body || {});
  return res?.data ?? res;
};

// === WhatsApp QRCode ===
export const createApifacilInstance = async (channelId, body) => {
  if (!channelId) throw new Error("channelId é obrigatório para criar instância WhatsApp QRCode.");
  const res = await fetchData(`/channels/${channelId}/apifacil/instance`, "POST", body);
  return res?.data ?? res;
};

export const switchApifacilInstanceVersion = async (channelId, body) => {
  if (!channelId) throw new Error("channelId é obrigatório para trocar a versão da conexão WhatsApp.");
  const res = await fetchData(`/channels/${channelId}/apifacil/instance/switch-version`, "POST", body);
  return res?.data ?? res;
};

export const getApifacilInstanceQrCode = async (channelId) => {
  if (!channelId) throw new Error("channelId é obrigatório para obter QRCode da instância WhatsApp QRCode.");
  const res = await fetchData(`/channels/${channelId}/apifacil/instance/qrcode`, "GET");
  return res?.data ?? res;
};

export const getApifacilInstanceStatus = async (channelId) => {
  if (!channelId) {
    throw new Error("channelId é obrigatório para obter status da instância WhatsApp QRCode.");
  }
  const res = await fetchData(`/channels/${channelId}/apifacil/instance/status`, "GET");
  return res?.data ?? res;
};

export const getApifacilInstanceConfig = async (channelId) => {
  if (!channelId) {
    throw new Error("channelId é obrigatório para obter configuração da instância WhatsApp QRCode.");
  }
  const res = await fetchData(`/channels/${channelId}/apifacil/config`, "GET");
  return res?.data ?? res;
};

export const getApifacilGroupParticipants = async (channelId, groupId) => {
  if (!channelId) {
    throw new Error("channelId é obrigatório para listar participantes do grupo WhatsApp.");
  }
  const gid = String(groupId || "").trim();
  if (!gid) {
    throw new Error("groupId é obrigatório para listar participantes do grupo WhatsApp.");
  }
  const res = await fetchData(`/channels/${channelId}/apifacil/groups/participants`, "GET", null, {
    params: { groupId: gid },
  });
  const payload = res?.data ?? res;
  const participants = Array.isArray(payload?.participants)
    ? payload.participants
    : Array.isArray(payload)
      ? payload
      : [];
  return {
    groupId: payload?.groupId || gid,
    participants,
    self: payload?.self || null,
  };
};

export const updateApifacilInstanceConfig = async (channelId, body) => {
  if (!channelId) {
    throw new Error("channelId é obrigatório para atualizar configuração da instância WhatsApp QRCode.");
  }
  const res = await fetchData(`/channels/${channelId}/apifacil/config`, "PUT", body);
  return res?.data ?? res;
};

export const requestApifacilPairingCode = async (channelId, phone) => {
  const res = await fetchData(`/channels/${channelId}/apifacil/instance/pairing`, "POST", { phone });
  return res?.data ?? res;
};

export const getApifacilPairingCode = async (channelId) => {
  const res = await fetchData(`/channels/${channelId}/apifacil/instance/pairing`, "GET");
  return res?.data ?? res;
};

export const pauseApifacilInstance = async (channelId) => {
  const res = await fetchData(`/channels/${channelId}/apifacil/instance/pause`, "POST", {});
  return res?.data ?? res;
};

export const getApifacilAccountProfile = async (channelId) => {
  const res = await fetchData(`/channels/${channelId}/apifacil/profile`, "GET");
  return res?.data ?? res;
};

export const updateApifacilAccountProfile = async (channelId, body) => {
  const res = await fetchData(`/channels/${channelId}/apifacil/profile`, "PUT", body);
  return res?.data ?? res;
};

export const removeApifacilAccountPhoto = async (channelId) => {
  const res = await fetchData(`/channels/${channelId}/apifacil/profile/photo`, "DELETE");
  return res?.data ?? res;
};

export const apifacilGroupAdmin = async (channelId, action, body = {}) => {
  const res = await fetchData(`/channels/${channelId}/apifacil/groups/admin`, body && Object.keys(body).length ? "POST" : "GET", body, {
    params: { action },
  });
  return res?.data ?? res;
};

export const listApifacilCalls = async (channelId) => {
  const res = await fetchData(`/channels/${channelId}/apifacil/calls`, "GET");
  return res?.data ?? res;
};

export const rejectApifacilCall = async (channelId, callId) => {
  const res = await fetchData(`/channels/${channelId}/apifacil/calls/${encodeURIComponent(callId)}/reject`, "POST", {});
  return res?.data ?? res;
};

export const reactConversationMessage = async (conversationId, messageId, emoji) => {
  const res = await fetchData(`/conversations/${conversationId}/messages/${messageId}/react`, "POST", { emoji });
  return res?.data ?? res;
};

export const editConversationWhatsappMessage = async (conversationId, messageId, content) => {
  const res = await fetchData(`/conversations/${conversationId}/messages/${messageId}/edit`, "POST", { content });
  return res?.data ?? res;
};

export const forwardConversationWhatsappMessage = async (conversationId, messageId, to) => {
  const res = await fetchData(`/conversations/${conversationId}/messages/${messageId}/forward`, "POST", { to });
  return res?.data ?? res;
};

export const markConversationWhatsappRead = async (conversationId) => {
  const res = await fetchData(`/conversations/${conversationId}/whatsapp-qr/mark-read`, "POST", {});
  return res?.data ?? res;
};

// === Integrações Meta (Facebook / Instagram) ===
export const getMetaOauthUrl = async (channelType, { rerequest = false } = {}) => {
  const safeType = channelType || "generic";
  const qs = new URLSearchParams({ channelType: safeType });
  if (rerequest) qs.set("rerequest", "1");
  const res = await fetchData(`/channels/meta/oauth-url?${qs.toString()}`, "GET");
  // backend responde { url }
  return res?.data?.url ?? res?.url;
};

export const getMetaPages = async ({ accessToken, includeInstagram = true }) => {
  if (!accessToken) {
    throw new Error("accessToken é obrigatório para listar páginas da Meta.");
  }
  const res = await fetchData("/integrations/meta/pages", "POST", {
    accessToken,
    includeInstagram,
  });
  return res?.data ?? res;
};

// === Telefonia (PABX) - simulação para testes ===
/** Monta URL de telefonia com ?company= para o backend identificar o workspace e emitir no socket. */
function telephonyUrl(path) {
  const company =
    typeof localStorage !== "undefined" ? getWorkspaceDatabaseHeaderValue() : null;
  const qs = company ? `?company=${encodeURIComponent(company)}` : "";
  return `${path}${qs}`;
}

/** Simula chamada recebida (POST /telephony/incoming). Retorna { idlead, sid } em caso de sucesso. */
export const simulateTelephonyIncoming = async (payload) => {
  const res = await fetchData(telephonyUrl("/telephony/incoming"), "POST", payload);
  return res;
};

/** Simula chamada terminada (POST /telephony/end). Usar com o mesmo id_chamada (sid) da chamada simulada. */
export const simulateTelephonyEnd = async (payload) => {
  const res = await fetchData(telephonyUrl("/telephony/end"), "POST", payload);
  return res;
};

/** Finaliza chamada originada e contabiliza minutos outbound. */
export const simulateTelephonyEndOutbound = async (payload) => {
  const res = await fetchData(telephonyUrl("/telephony/end-outbound"), "POST", payload);
  return res;
};

export const listTwilioOwnedNumbers = async (channelId) => {
  const res = await fetchData(`/channels/${channelId}/twilio/owned-numbers`, "GET");
  return res?.data ?? res;
};

/** Lista números já na conta da plataforma (somente master). */
export const listTwilioOwnedNumbersPlatform = async () => {
  const res = await fetchData(`/channels/twilio/owned-numbers`, "GET");
  return res?.data ?? res;
};

export const listTwilioAvailableNumbers = async (channelId, params = {}) => {
  const qs = toQS(params);
  const res = await fetchData(`/channels/${channelId}/twilio/available-numbers${qs}`, "GET");
  return res?.data ?? res;
};

/** Busca números disponíveis na conta da plataforma (antes de criar o canal). */
export const listTwilioAvailableNumbersPlatform = async (params = {}) => {
  const qs = toQS(params);
  const res = await fetchData(`/channels/twilio/available-numbers${qs}`, "GET");
  return res?.data ?? res;
};

/** Países com inventário de números disponíveis na conta da plataforma. */
export const listTwilioAvailableCountries = async () => {
  const res = await fetchData(`/channels/twilio/available-countries`, "GET");
  return res?.data ?? res;
};

/** Preços vigentes de número (mensal) e minutos (por minuto) para Telefonia Xpro. */
export const getTwilioVoicePricing = async () => {
  const res = await fetchData(`/channels/twilio/pricing`, "GET");
  return res?.data ?? res;
};

/** Preços de SMS: número mensal (se comprar novo) + valor por mensagem. */
export const getTwilioSmsPricing = async () => {
  const res = await fetchData(`/channels/twilio/sms/pricing`, "GET");
  return res?.data ?? res;
};

/** Messaging Services RCS disponíveis na conta da plataforma. */
export const listTwilioRcsMessagingServices = async () => {
  const res = await fetchData(`/channels/twilio/rcs/messaging-services`, "GET");
  return res?.data ?? res;
};

/** Números da Telefonia Xpro do workspace reutilizáveis para SMS (com capability SMS). */
export const listWorkspaceSmsCapableNumbers = async (params = {}) => {
  const qs = toQS(params);
  const res = await fetchData(`/channels/twilio/workspace-sms-numbers${qs}`, "GET");
  return res?.data ?? res;
};

/** Preços vigentes de canal (mensal) e minutos (por minuto) para Vídeo Xpro. */
export const getTwilioVideoPricing = async () => {
  const res = await fetchData(`/channels/twilio/video/pricing`, "GET");
  return res?.data ?? res;
};

export const purchaseTwilioNumber = async (channelId, payload) => {
  const res = await fetchData(`/channels/${channelId}/twilio/purchase-number`, "POST", payload);
  return res?.data ?? res;
};

/** Origina chamada na API da central (canal Telefonia / PABX tipo 29). */
export const originatePabxCall = async (channelId, payload) => {
  const res = await fetchData(`/channels/${channelId}/pabx/originate`, "POST", payload);
  return res?.data ?? res;
};

/** Requisitos Address/Bundle Twilio por país/tipo. */
export const getTwilioRegulatoryRequirements = async (params = {}) => {
  const qs = toQS(params);
  const res = await fetchData(`/channels/twilio/regulatory/requirements${qs}`, "GET");
  return res?.data ?? res;
};

export const getChannelTwilioRegulatory = async (channelId) => {
  const res = await fetchData(`/channels/${channelId}/twilio/regulatory`, "GET");
  return res?.data ?? res;
};

export const presignTwilioRegulatoryDocument = async (payload) => {
  const res = await fetchData(`/channels/twilio/regulatory/presign`, "POST", payload);
  return res?.data ?? res;
};

export const submitTwilioRegulatory = async (payload, channelId = null) => {
  const path = channelId
    ? `/channels/${channelId}/twilio/regulatory`
    : `/channels/twilio/regulatory`;
  const res = await fetchData(path, "POST", payload);
  return res?.data ?? res;
};

export const listPlatformVoiceRegulatoryPending = async (params = {}) => {
  const qs = toQS(params);
  const res = await fetchData(`/billing/admin/voice-regulatory/pending${qs}`, "GET");
  return res?.data ?? res;
};

export const patchPlatformVoiceRegulatory = async (requestId, payload) => {
  const res = await fetchData(`/billing/admin/voice-regulatory/${requestId}`, "PATCH", payload);
  return res?.data ?? res;
};

export const downloadPlatformVoiceRegulatoryDocument = async (requestId, docId) => {
  const res = await fetchData(
    `/billing/admin/voice-regulatory/${requestId}/documents/${docId}/download`,
    "GET"
  );
  return res?.data ?? res;
};

export const listPlatformErrors = async (params = {}) => {
  const qs = toQS(params);
  const res = await fetchData(`/billing/admin/errors${qs}`, "GET");
  return res?.data ?? res;
};

export const getPlatformErrorsSummary = async (params = {}) => {
  const qs = toQS(params);
  const res = await fetchData(`/billing/admin/errors/summary${qs}`, "GET");
  return res?.data ?? res;
};

export const getPlatformError = async (errorId) => {
  const res = await fetchData(`/billing/admin/errors/${errorId}`, "GET");
  return res?.data ?? res;
};

export const patchPlatformErrorStatus = async (errorId, payload) => {
  const res = await fetchData(`/billing/admin/errors/${errorId}`, "PATCH", payload);
  return res?.data ?? res;
};

export const patchPlatformErrorsBulkStatus = async (payload) => {
  const res = await fetchData(`/billing/admin/errors/bulk`, "PATCH", payload);
  return res?.data ?? res;
};

export const listPlatformApifacilInstances = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/apifacil/instances", {
    params,
    timeout: 120000,
  });
  return data?.data ?? data;
};

export const getPlatformApifacilInstanceStatus = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/apifacil/instances/status", {
    params,
    timeout: 30000,
  });
  return data?.data ?? data;
};

export const postPlatformApifacilInstanceStatusBatch = async (items = []) => {
  const { data } = await apiClient.post(
    "/billing/admin/apifacil/instances/status-batch",
    { items },
    { timeout: 120000 }
  );
  return data?.data ?? data;
};

export const postPlatformApifacilInstanceDisconnect = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/apifacil/instances/disconnect", payload, {
    timeout: 30000,
  });
  return data?.data ?? data;
};

export const postPlatformApifacilInstanceDelete = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/apifacil/instances/delete", payload, {
    timeout: 30000,
  });
  return data?.data ?? data;
};

export const getTwilioVoiceToken = async (channelId, payload = {}) => {
  const res = await fetchData(`/channels/${channelId}/twilio/voice-token`, "POST", payload || {});
  return res?.data ?? res;
};

/** Softphone reporta available | on_call | unavailable. */
export const updateTwilioVoicePresence = async (channelId, payload = {}) => {
  const res = await fetchData(
    `/channels/${channelId}/twilio/voice-presence`,
    "POST",
    payload || {}
  );
  return res?.data ?? res;
};

/** Lista fila de transferência de voz do usuário logado. */
export const listVoiceTransferQueue = async (channelId = null) => {
  const path = channelId
    ? `/channels/${channelId}/voice-queue`
    : `/channels/voice-queue`;
  const res = await fetchData(path, "GET");
  return res?.data ?? res;
};

/** Claim manual da próxima ligação da fila geral do canal. */
export const claimVoiceTransferQueue = async (channelId, payload = {}) => {
  const res = await fetchData(
    `/channels/${channelId}/voice-queue/claim`,
    "POST",
    payload || {}
  );
  return res?.data ?? res;
};

/** Registra tentativa/erro de ligação na timeline da solicitação. */
export const recordVoiceCallLog = async (channelId, payload) => {
  const res = await fetchData(`/channels/${channelId}/voice/call-log`, "POST", payload || {});
  return res?.data ?? res;
};

export const createTwilioOutboundCall = async (channelId, payload) => {
  const res = await fetchData(`/channels/${channelId}/twilio/outbound-call`, "POST", payload);
  return res?.data ?? res;
};

/** Click-to-call: toca no telefone de origem e, ao atender, completa para o destino. */
export const createTwilioClickToCall = async (channelId, payload) => {
  const res = await fetchData(`/channels/${channelId}/twilio/click-to-call`, "POST", payload || {});
  return res?.data ?? res;
};

/** Encerra ligação Twilio por CallSid (click-to-call / REST). */
export const hangupTwilioCall = async (channelId, payload) => {
  const res = await fetchData(`/channels/${channelId}/twilio/hangup`, "POST", payload || {});
  return res?.data ?? res;
};

/** Entra numa ligação em andamento (conferência / monitoria). */
export const joinVoiceLiveCall = async (channelId, payload) => {
  const res = await fetchData(`/channels/${channelId}/voice/live/join`, "POST", payload || {});
  return res?.data ?? res;
};

/** Proxy para POST /{phone-number-id}/calls na Graph API (WhatsApp Calling). */
export const postWhatsappCloudCall = async (channelId, body) => {
  const res = await fetchData(`/channels/${channelId}/whatsapp-cloud/calls`, "POST", body);
  return res?.data ?? res;
};

/** Habilitar chamadas / horário comercial via POST …/settings na Graph API. */
export const postWhatsappCloudSettings = async (channelId, body) => {
  const res = await fetchData(`/channels/${channelId}/whatsapp-cloud/settings`, "POST", body);
  return res?.data ?? res;
};

export const getWhatsappCloudCallRecord = async (channelId, callId) => {
  const enc = encodeURIComponent(callId);
  const res = await fetchData(`/channels/${channelId}/whatsapp-cloud/calls/${enc}`, "GET");
  return res?.data ?? res;
};

/** Solicita permissão de chamada (interactive call_permission_request) via Partner API Gupshup. */
export const postGupshupVoiceCallPermissionRequest = async (channelId, body) => {
  const res = await fetchData(`/channels/${channelId}/gupshup-voice/call-permission-request`, "POST", body);
  return res?.data ?? res;
};

/** Registro/habilitação SIP na Partner API (URL em GUPSHUP_PARTNER_VOICE_REGISTER_URL ou body.registerUrl). */
export const postGupshupVoiceRegisterSip = async (channelId, body) => {
  const res = await fetchData(`/channels/${channelId}/gupshup-voice/register-sip`, "POST", body || {});
  return res?.data ?? res;
};

/** Sugestão de host/porta SIP (infra da plataforma) para metaData GS_SIP (Gupshup WABA). */
export const getGupshupTwilioSipPeerHints = async (channelId) => {
  const res = await fetchData(`/channels/${channelId}/gupshup-voice/twilio-sip-peer-hints`, "GET");
  return res?.data ?? res;
};

/** Retorna hints para o SBC originar INVITE TLS à borda Gupshup (após validar permissão). */
export const postGupshupSipOutboundHints = async (channelId, body) => {
  const res = await fetchData(`/channels/${channelId}/gupshup-voice/sip-outbound-hints`, "POST", body);
  return res?.data ?? res;
};

/**
 * Liga o agente (softphone/PSTN) e, ao atender, conecta SIP à borda Gupshup (WhatsApp).
 * Body: { to, agentTo, leadId?, sipRegion? }
 */
export const postGupshupTwilioWhatsappOutboundCall = async (channelId, body) => {
  const res = await fetchData(`/channels/${channelId}/gupshup-voice/twilio-outbound-call`, "POST", body);
  return res?.data ?? res;
};

export const getTypeQuickReplies = async (typeId) => {
  const res = await fetchData(`/types/${typeId}/quick-replies`, "GET");
  return res?.data ?? [];
};

export const createTypeQuickReply = async (typeId, body) => {
  const res = await fetchData(`/types/${typeId}/quick-replies`, "POST", body);
  return res?.data ?? res;
};

export const updateTypeQuickReply = async (typeId, replyId, body) => {
  const res = await fetchData(`/types/${typeId}/quick-replies/${replyId}`, "PUT", body);
  return res?.data ?? res;
};

export const deleteTypeQuickReply = async (typeId, replyId) => {
  await fetchData(`/types/${typeId}/quick-replies/${replyId}`, "DELETE");
};

export const testPassagensConnection = async (target) => {
  if (!target) throw new Error("target é obrigatório para testar conexão.");
  const res = await fetchData("/configurations/passagens/test", "POST", { target });
  return res?.data ?? res;
};

export const getPassagensTicketTemplateMeta = async () => {
  const res = await fetchData("/configurations/passagens/ticket-template/meta");
  return res?.data ?? res;
};

export const previewPassagensTicketTemplate = async (body = {}) => {
  const res = await fetchData("/configurations/passagens/ticket-template/preview", "POST", body);
  return res?.data ?? res;
};

export const validatePassagensTicketTemplate = async (body = {}) => {
  const res = await fetchData("/configurations/passagens/ticket-template/validate", "POST", body);
  return res?.data ?? res;
};

/** Inicia importação de trechos/passagens (sempre assíncrona: job + poll; sync só com body.mode=sync). */
export const importPassagensErp = async (body = {}) => {
  // Enfileirar deve responder rápido; o trabalho pesado roda no worker / inline em background.
  const res = await fetchData("/products/import-passagens-erp", "POST", body, { timeout: 45000 });
  return res?.data ?? res;
};

/** Inicia exportação de produtos (assíncrona com worker quando disponível). */
export const startProductsExportJob = async (body = {}) => {
  const res = await fetchData("/products/export/jobs", "POST", body);
  return res?.data ?? res;
};

/** Acompanha job de exportação de produtos (Redis). */
export const getProductsExportJob = async (jobId) => {
  const id = encodeURIComponent(String(jobId || ""));
  const res = await fetchData(`/products/export/jobs/${id}`, "GET");
  return res?.data ?? res;
};

/** Solicita cancelamento do job de exportação de produtos. */
export const cancelProductsExportJob = async (jobId) => {
  const id = encodeURIComponent(String(jobId || ""));
  const res = await fetchData(`/products/export/jobs/${id}/cancel`, "POST", {});
  return res?.data ?? res;
};

export const getActiveProductImageAuditJob = async () => {
  const res = await fetchData("/products/image-audit/jobs/active", "GET");
  return res?.data ?? res;
};

export const startProductImageAuditJob = async (body = {}) => {
  const res = await fetchData("/products/image-audit/jobs", "POST", body);
  return res?.data ?? res;
};

export const getProductImageAuditJob = async (jobId) => {
  const id = encodeURIComponent(String(jobId || ""));
  const res = await fetchData(`/products/image-audit/jobs/${id}`, "GET");
  return res?.data ?? res;
};

export const listProductImageAuditIssues = async (jobId, { offset = 0, limit = 50 } = {}) => {
  const id = encodeURIComponent(String(jobId || ""));
  const res = await fetchData(
    `/products/image-audit/jobs/${id}/issues?offset=${offset}&limit=${limit}`,
    "GET"
  );
  return res?.data ?? res;
};

export const continueProductImageAuditJob = async (jobId) => {
  const id = encodeURIComponent(String(jobId || ""));
  const res = await fetchData(`/products/image-audit/jobs/${id}/continue`, "POST", {});
  return res?.data ?? res;
};

export const cancelProductImageAuditJob = async (jobId) => {
  const id = encodeURIComponent(String(jobId || ""));
  const res = await fetchData(`/products/image-audit/jobs/${id}/cancel`, "POST", {});
  return res?.data ?? res;
};

export const startChannelAiMappingJob = async (body = {}) => {
  const res = await fetchData("/channels/ai-mapping/jobs", "POST", body);
  return res?.data ?? res;
};

export const getActiveChannelAiMappingJob = async () => {
  const res = await fetchData("/channels/ai-mapping/jobs/active", "GET");
  return res?.data ?? res;
};

export const getChannelAiMappingJob = async (jobId) => {
  const id = encodeURIComponent(String(jobId || ""));
  const res = await fetchData(`/channels/ai-mapping/jobs/${id}`, "GET");
  return res?.data ?? res;
};

export const cancelChannelAiMappingJob = async (jobId) => {
  const id = encodeURIComponent(String(jobId || ""));
  const res = await fetchData(`/channels/ai-mapping/jobs/${id}/cancel`, "POST", {});
  return res?.data ?? res;
};

export const getLatestChannelAiMapping = async (idsProjetos = []) => {
  const ids = Array.isArray(idsProjetos) ? idsProjetos.join(",") : String(idsProjetos || "");
  const qs = ids ? `?ids_projetos=${encodeURIComponent(ids)}` : "";
  const res = await fetchData(`/channels/ai-mapping/latest${qs}`, "GET");
  return res?.data ?? res;
};

const PRODUCTS_EXPORT_POLL_INTERVAL_MS = 2500;
const PRODUCTS_EXPORT_POLL_MAX_MS = 15 * 60 * 1000;

/**
 * Enfileira exportação e aguarda downloadUrl (ou usa resposta síncrona se worker indisponível).
 */
export const exportProductsSpreadsheet = async (body = {}, { onStatus } = {}) => {
  const data = await startProductsExportJob(body);

  if (data?.accepted && data?.jobId) {
    const started = Date.now();
    while (Date.now() - started < PRODUCTS_EXPORT_POLL_MAX_MS) {
      const job = await getProductsExportJob(data.jobId);
      const status = String(job?.status || "").toLowerCase();
      onStatus?.(status, job);

      if (status === "completed") {
        return {
          downloadUrl: job?.downloadUrl || job?.result?.downloadUrl || null,
          total: job?.total ?? job?.result?.total ?? 0,
          processed: job?.processed ?? job?.result?.processed ?? 0,
          job,
        };
      }
      if (status === "failed" || status === "cancelled") {
        const err = new Error(
          job?.errorMessage || "Não foi possível exportar a lista de produtos."
        );
        err.job = job;
        throw err;
      }

      await new Promise((resolve) => setTimeout(resolve, PRODUCTS_EXPORT_POLL_INTERVAL_MS));
    }
    const err = new Error(
      "A exportação está demorando mais que o esperado. Tente novamente em instantes."
    );
    err.code = "poll_timeout";
    throw err;
  }

  return {
    downloadUrl: data?.downloadUrl || null,
    total: data?.total ?? 0,
    processed: data?.processed ?? data?.total ?? 0,
    job: data,
  };
};

/** Acompanha job de importação (Redis), retornado pelo POST acima quando `accepted: true`. */
export const getImportPassagensErpJob = async (jobId) => {
  const id = encodeURIComponent(String(jobId || ""));
  const res = await fetchData(`/products/import-passagens-erp/jobs/${id}`, "GET");
  return res?.data ?? res;
};

/** Solicita cancelamento do job assíncrono de importação. */
export const cancelImportPassagensErpJob = async (jobId) => {
  const id = encodeURIComponent(String(jobId || ""));
  const res = await fetchData(`/products/import-passagens-erp/jobs/${id}/cancel`, "POST", {});
  return res?.data ?? res;
};

/** Inicia atualização assíncrona de workspaces vinculados ao cadastro central. */
export const startCentralLinkedImportJob = async (sourceProjectId, { syncMode = "full" } = {}) => {
  const qs =
    sourceProjectId != null && sourceProjectId !== ""
      ? `?ids_projetos=${encodeURIComponent(String(sourceProjectId))}`
      : "";
  const res = await fetchData(`/products/import-central-linked/jobs${qs}`, "POST", {
    syncMode,
  });
  return res?.data ?? res;
};

/** Job ativo no workspace (se houver). */
export const getActiveCentralLinkedImportJob = async ({ light = true } = {}) => {
  const suffix = light ? "?light=1" : "";
  const res = await fetchData(`/products/import-central-linked/jobs/active${suffix}`, "GET");
  return res?.data ?? res;
};

/** Acompanha job de atualização de workspaces vinculados. */
export const getCentralLinkedImportJob = async (jobId, { light = true } = {}) => {
  const id = encodeURIComponent(String(jobId || ""));
  const suffix = light ? "?light=1" : "";
  const res = await fetchData(`/products/import-central-linked/jobs/${id}${suffix}`, "GET");
  return res?.data ?? res;
};

/** Solicita cancelamento do job assíncrono de workspaces vinculados. */
export const cancelCentralLinkedImportJob = async (jobId) => {
  const id = encodeURIComponent(String(jobId || ""));
  const res = await fetchData(`/products/import-central-linked/jobs/${id}/cancel`, "POST", {});
  return res?.data ?? res;
};

/** Inicia importação de fluxo via PDF (assíncrona com worker quando disponível). */
export const importChatbotFlowFromPdf = async (body = {}, options) => {
  const res = await fetchData("/chatbot/slots/import-pdf", "POST", body, options);
  return res?.data ?? res;
};

/** Acompanha job de importação PDF do fluxo (Redis). */
export const getChatbotFlowImportJob = async (jobId) => {
  const id = encodeURIComponent(String(jobId || ""));
  const res = await fetchData(`/chatbot/slots/import-jobs/${id}`, "GET");
  return res?.data ?? res;
};

// ============================================================================
// AGENTES DE IA
// ============================================================================

const toQS = (obj = {}) => {
  const sp = new URLSearchParams();
  Object.entries(obj).forEach(([k, v]) => {
    if (v === null || v === undefined) return;
    const s = String(v).trim();
    if (s) sp.set(k, s);
  });
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
};

/** Lista cidades do cadastro de passagens (segmento transporte). */
export const getPassengerCities = async (params = {}) => {
  const { page = 1, limit = 50, q = "", id = null, ids = null } = params;
  const qs = toQS({
    page,
    limit,
    ...(q ? { q } : {}),
    ...(id != null && id !== "" ? { id } : {}),
    ...(ids != null && ids !== "" ? { ids } : {}),
  });
  const res = await fetchData(`/products/passenger-cities${qs}`, "GET");
  return res?.data ?? res;
};

export const createPassengerCity = async (body) => {
  const res = await fetchData(`/products/passenger-cities`, "POST", body);
  return res?.data ?? res;
};

export const updatePassengerCity = async (id, body) => {
  const res = await fetchData(`/products/passenger-cities/${id}`, "PUT", body);
  return res?.data ?? res;
};

export const deletePassengerCity = async (id) => {
  const res = await fetchData(`/products/passenger-cities/${id}`, "DELETE");
  return res?.data ?? res;
};

export const getAgents = async (params = {}) => {
  const { page = 1, limit = 20, search = "", skill_key: skillKey = "" } = params;
  const qs = toQS({ page, limit, search, skill_key: skillKey || undefined });
  return fetchData(`/agents${qs}`);
};

export const getAgentById = async (id) => {
  const res = await fetchData(`/agents/${id}`, "GET");
  return { data: res?.data ?? res };
};

export const getAgentRoutingCatalog = async (agentId) => {
  const qs = agentId ? `?agent_id=${encodeURIComponent(String(agentId))}` : "";
  const res = await fetchData(`/agents/routing-catalog${qs}`, "GET");
  return res?.data ?? res ?? {};
};

export const createAgent = async (data) => {
  return fetchData("/agents", "POST", data);
};

/** Analisa modelo/temperatura/prompt do agente à luz da skill e da config. */
export const reviewAgentAiSettings = async (payload = {}) => {
  const res = await fetchData("/agents/ai-settings/review", "POST", payload);
  return res;
};

/** Extrai e avalia o texto de um documento/URL (ou lote via `sources`). */
export const previewAgentKnowledge = async (payload = {}) => {
  const res = await fetchData("/agents/knowledge/extract-preview", "POST", payload);
  return res?.data ?? res;
};

/** Analisa a base e sugere termos equivalentes + assuntos de encaminhamento. */
export const suggestAgentKnowledgeSearchHints = async (payload = {}) => {
  const res = await fetchData("/agents/knowledge/suggest-search-hints", "POST", payload);
  return res?.data ?? res;
};

export const updateAgent = async (id, data) => {
  return fetchData(`/agents/${id}`, "PUT", data);
};

export const deleteAgent = async (id) => {
  return fetchData(`/agents/${id}`, "DELETE");
};

export const getAgentLogs = async (id, params = {}) => {
  const { page = 1, limit = 50 } = params;
  const qs = toQS({ page, limit });
  const res = await fetchData(`/agents/${id}/logs${qs}`);
  return { data: res?.data ?? res ?? [], pagination: res?.pagination };
};

export const getAgentAnalytics = async (id) => {
  const res = await fetchData(`/agents/${id}/analytics`, "GET");
  return { data: res?.data ?? res };
};

export const getPassagemRecoveryAnalytics = async (id, params = {}) => {
  const qs = params.days != null ? `?days=${encodeURIComponent(params.days)}` : "";
  const res = await fetchData(`/agents/${id}/recovery-analytics${qs}`, "GET");
  return res?.data ?? res;
};

export const triggerPassagemRecovery = async (agentId, leadId) => {
  return fetchData(`/agents/${agentId}/recovery/trigger`, "POST", { leadId });
};

export const estimateOpsIntelligence = async (payload = {}) => {
  return fetchData(`/agents/ops-intelligence/estimate`, "POST", payload);
};

export const triggerOpsIntelligence = async (agentId, payload = {}) => {
  try {
    const response = await apiClient.request({
      method: "POST",
      url: `/agents/${agentId}/ops-intelligence/trigger`,
      data: payload,
      // Testes com mídia/LLM podem passar de 30s no fallback sync.
      timeout: 120000,
      validateStatus: (status) =>
        (status >= 200 && status < 300) || status === 202 || status === 409,
    });
    const data = response?.data ?? response;
    if (response?.status === 409) {
      const err = new Error(data?.message || "Relatório já em andamento");
      err.code = "OPS_RUN_IN_PROGRESS";
      err.activeRunId = data?.data?.activeRunId;
      throw err;
    }
    return {
      ...(typeof data === "object" && data ? data : {}),
      status: response?.status,
      async: Boolean(data?.data?.async ?? data?.async),
      runId: data?.data?.runId ?? data?.runId,
      message: data?.message,
    };
  } catch (error) {
    const body = error?.response?.data;
    const detail =
      (typeof body?.data?.error === "string" && body.data.error) ||
      (typeof body?.error === "string" && body.error) ||
      (typeof body?.message === "string" && body.message) ||
      error?.message ||
      "Falha ao gerar relatório";
    const err = new Error(detail);
    err.status = error?.response?.status ?? null;
    err.code = body?.data?.code || body?.code || error?.code || null;
    err.activeRunId = body?.data?.activeRunId;
    throw err;
  }
};

export const getOpsIntelligenceRuns = async (agentId, params = {}) => {
  const qs = params.limit != null ? `?limit=${encodeURIComponent(params.limit)}` : "";
  return fetchData(`/agents/${agentId}/ops-intelligence/runs${qs}`, "GET");
};

export const getOpsIntelligenceRun = async (agentId, runId) => {
  return fetchData(`/agents/${agentId}/ops-intelligence/runs/${runId}`, "GET");
};

/** Envia documentos (Word, planilhas, PDF) para o agente. FormData com campo "documents". */
export const uploadAgentDocuments = async (id, files) => {
  if (!id || !files?.length) return { data: null };
  const formData = new FormData();
  files.forEach((file) => formData.append("documents", file));
  const response = await apiClient.request({
    method: "POST",
    url: `/agents/${id}/documents`,
    data: formData,
  });
  return response?.data ?? response;
};

// ============================================================================
// EXPORTAÇÃO DO DASHBOARD (CSV)
// ============================================================================

/** Escapa um campo para formato CSV (delimitador ;) */
const __csvEscape = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (/[;"\n\r,]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

/** Monta o CSV a partir de linhas e cabeçalhos fixos (separador ;) */
function __buildCsv(rows, headers) {
  const BOM = "\uFEFF"; // compat Excel
  const head = headers.join(";");
  const body = rows
    .map((r) => headers.map((h) => __csvEscape(r[h])).join(";"))
    .join("\n");
  return `${BOM}${head}\n${body}\n`;
}

/** Normaliza o payload dos endpoints de dashboard em linhas "long format". */
function __dashboardExportError(code, fallback) {
  const err = new Error(fallback);
  err.code = code;
  return err;
}

function __toDashboardExportQs(params = {}) {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === null || v === undefined || v === false) return;
    if (Array.isArray(v)) {
      const arr = v.map((x) => (x ?? "").toString().trim()).filter((x) => x !== "");
      if (arr.length) sp.set(k, arr.join(","));
      return;
    }
    if (typeof v === "object") return;
    const s = String(v).trim();
    if (s) sp.set(k, s);
  });
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
}

function __addExportRow(out, section, period, metric, dim1, dim2, value) {
  if (value === undefined || value === null || value === "") return;
  if (typeof value === "boolean") return;
  if (typeof value === "object") return;
  out.push({
    secao: section,
    metrica: metric,
    dim1: dim1 ?? "",
    dim2: dim2 ?? "",
    valor: value,
    inicio: period.start || "",
    fim: period.end || "",
  });
}

function __addNumericFields(out, section, period, metric, obj, dim1 = "") {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return;
  Object.entries(obj).forEach(([k, v]) => {
    if (typeof v === "number") __addExportRow(out, section, period, metric, dim1 || k, dim1 ? k : "", v);
  });
}

function __rowDim(r) {
  if (!r || typeof r !== "object") return "";
  return (
    r.name ??
    r.path ??
    r.originName ??
    r.label ??
    r.stage ??
    r.category ??
    r.operador ??
    r.route ??
    r.day ??
    r.date ??
    r.month ??
    r.cargo ??
    r.key ??
    ""
  );
}

function __rowValue(r) {
  if (!r || typeof r !== "object") return 0;
  if (typeof r.value === "number") return r.value;
  if (typeof r.y === "number") return r.y;
  if (typeof r.qtd === "number") return r.qtd;
  if (typeof r.qty === "number") return r.qty;
  if (typeof r.total === "number") return r.total;
  if (typeof r.conversations === "number") return r.conversations;
  if (typeof r.revenue === "number") return r.revenue;
  if (typeof r.custo === "number") return r.custo;
  if (typeof r.custoAtendimentos === "number") return r.custoAtendimentos;
  if (typeof r.percentage === "number") return r.percentage;
  if (typeof r.count === "number") return r.count;
  return r.qtd ?? r.count ?? r.total ?? r.y ?? r.value ?? 0;
}

function __addArrayRows(out, section, period, metric, arr, extraValKeys = []) {
  if (!Array.isArray(arr)) return;
  arr.forEach((r) => {
    if (!r || typeof r !== "object") return;
    const dim = __rowDim(r);
    extraValKeys.forEach((key) => {
      if (typeof r[key] === "number") {
        __addExportRow(out, section, period, metric, dim, key, r[key]);
      }
    });
    if (!extraValKeys.length) {
      __addExportRow(out, section, period, metric, dim, "", __rowValue(r));
    }
  });
}

function __addOperadoresRows(out, section, period, operadores) {
  const rows = Array.isArray(operadores?.data)
    ? operadores.data
    : Array.isArray(operadores?.rows)
      ? operadores.rows
      : Array.isArray(operadores)
        ? operadores
        : [];
  rows.forEach((r) => {
    const name = r.operador || r.nome || "";
    ["total", "pendentes", "emAtendimento", "contatoFuturo", "concluidas", "perdidas", "pessoasContatadas", "avgTMAsec", "avgTMEsec", "custoAtendimentos", "custoOcioso"].forEach(
      (key) => {
        const v = r[key];
        if (typeof v === "number") __addExportRow(out, section, period, "operadores", name, key, v);
      }
    );
  });
}

function __addChartsGeneric(out, section, period, charts) {
  if (!charts || typeof charts !== "object") return;
  Object.entries(charts).forEach(([metric, val]) => {
    if (Array.isArray(val)) {
      const extraKeys = [];
      const sample = val.find((x) => x && typeof x === "object") || {};
      ["revenue", "cumul", "quantity", "conv", "orders", "tickets", "gmv", "custo", "custoAtendimentos", "custoOcioso", "volume", "conversations", "cost", "sales", "avgTMAsec", "avgTMEsec"].forEach(
        (k) => {
          if (typeof sample[k] === "number" && k !== "value" && k !== "y" && k !== "qtd") extraKeys.push(k);
        }
      );
      if (extraKeys.length > 1 || (extraKeys.length === 1 && sample.value == null && sample.y == null && sample.qtd == null)) {
        __addArrayRows(out, section, period, metric, val, extraKeys);
      } else {
        __addArrayRows(out, section, period, metric, val);
      }
    } else if (val && typeof val === "object") {
      Object.entries(val).forEach(([k, inner]) => {
        if (typeof inner === "number") {
          __addExportRow(out, section, period, metric, k, "", inner);
        } else if (inner && typeof inner === "object") {
          __addNumericFields(out, section, period, metric, inner, k);
        }
      });
    }
  });
}

function __normalizeDashboardToRows(section, payload) {
  const out = [];
  if (!payload || typeof payload !== "object") return out;

  const period = payload.period || {};
  const addCards = (cards) => {
    if (!cards || typeof cards !== "object") return;
    Object.entries(cards).forEach(([k, v]) => {
      if (typeof v === "number") __addExportRow(out, section, period, "cards", k, "", v);
      else if (k === "byStatus" && v && typeof v === "object") {
        __addNumericFields(out, section, period, "byStatus", v);
      }
    });
  };

  addCards(payload.cards);
  if (payload.totals && typeof payload.totals === "object") {
    __addNumericFields(out, section, period, "totals", payload.totals);
  }
  __addOperadoresRows(out, section, period, payload.operadores);

  const ch = payload.charts || {};

  if (section === "atendimento") {
    __addArrayRows(out, section, period, "emProcessamento", ch.emProcessamento);
    __addArrayRows(out, section, period, "finalizados", ch.finalizados);
    __addArrayRows(out, section, period, "funil", ch.funil);
    __addArrayRows(out, section, period, "serieTemporal", ch.serieTemporal);
    __addArrayRows(out, section, period, "motivos", ch.motivos);
    __addArrayRows(out, section, period, "motivosTop", ch.motivosTop);
  } else if (section === "produtos") {
    __addArrayRows(out, section, period, "abc", ch.abc, ["revenue", "cumul"]);
    __addArrayRows(out, section, period, "convByCat", ch.convByCat);
    __addArrayRows(out, section, period, "oportunidades", ch.oportunidades, ["gapScore"]);
    __addArrayRows(out, section, period, "mixCat", ch.mixCat);
    __addArrayRows(out, section, period, "mixDisp", ch.mixDisp);
    __addArrayRows(out, section, period, "monthlyAgg", ch.monthlyAgg, ["quantity", "revenue", "conv"]);
  } else if (section === "vendas") {
    __addArrayRows(out, section, period, "revenueByCategory", ch.revenueByCategory, ["revenue"]);
    __addArrayRows(out, section, period, "salesByChannel", ch.salesByChannel);
    __addArrayRows(out, section, period, "monthlyOverview", ch.monthlyOverview, ["orders", "revenue"]);
    __addArrayRows(out, section, period, "salesTable", ch.salesTable, ["orders", "revenue", "qty"]);
  } else if (section === "venda-passagens") {
    __addArrayRows(out, section, period, "origins", ch.origins, ["qty"]);
    __addArrayRows(out, section, period, "destinations", ch.destinations, ["qty"]);
    __addArrayRows(out, section, period, "topRoutes", ch.topRoutes, ["qty"]);
    __addArrayRows(out, section, period, "dailySeries", ch.dailySeries, ["tickets", "gmv"]);
  } else if (section === "financeiro-atendimento") {
    __addArrayRows(out, section, period, "serieTemporal", ch.serieTemporal, ["custoAtendimentos", "custo"]);
    __addChartsGeneric(out, section, period, {
      byTipoResponsavel: ch.byTipoResponsavel,
      byCargo: ch.byCargo,
    });
  } else if (section === "origem" || section === "ctwa" || section === "rota-regiao" || section === "campanhas-vendas") {
    __addChartsGeneric(out, section, period, payload.charts ? payload.charts : payload);
    __addArrayRows(out, section, period, "byChannel", payload.byChannel, ["conversations"]);
    __addArrayRows(out, section, period, "rows", payload.rows, ["conversations", "cost", "revenue", "sales"]);
    __addArrayRows(out, section, period, "bySource", payload.bySource, ["revenue", "cost", "sales"]);
    __addArrayRows(out, section, period, "byCampaign", payload.byCampaign, ["revenue", "cost", "sales"]);
  } else {
    __addChartsGeneric(out, section, period, ch);
  }

  return out;
}

const DASHBOARD_EXPORT_ROUTE = {
  atendimento: { subpath: "summary" },
  "financeiro-atendimento": { subpath: "summary" },
  clientes: { subpath: "charts", extras: { type: "all" } },
  produtos: { subpath: "charts" },
  vendas: { subpath: "charts" },
  "venda-passagens": { subpath: "charts" },
  origem: { subpath: "charts", bundle: true },
};

const ORIGEM_BUNDLE = [
  { scope: "origem", subpath: "charts" },
  { scope: "ctwa", subpath: "charts" },
  { scope: "rota-regiao", subpath: "charts", extras: { group_by: "uf" } },
  { scope: "campanhas-vendas", subpath: "charts" },
];

function __assertExportPayloadReady(resp) {
  if (resp?.httpStatus === 202 || isDashboardJobOrDeferredEnvelope(resp?.data)) {
    throw __dashboardExportError(
      "DASHBOARD_EXPORT_STILL_PROCESSING",
      "O relatório ainda está sendo gerado. Aguarde a notificação e tente exportar de novo."
    );
  }
  return resp?.data || {};
}

function __triggerCsvDownload(csv, fileName) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function __dashboardExportFileName(section, params, fileName) {
  const stamp =
    String(params?.start || "").replace(/-/g, "") +
    "_" +
    String(params?.end || "").replace(/-/g, "");
  return fileName || `dashboard_${section}_${stamp}.csv`;
}

function __finishDashboardCsv(section, params, fileName, rows) {
  if (!rows.length) {
    throw __dashboardExportError(
      "DASHBOARD_EXPORT_EMPTY",
      "Sem dados para exportar nesse período."
    );
  }
  const headers = ["secao", "metrica", "dim1", "dim2", "valor", "inicio", "fim"];
  __triggerCsvDownload(__buildCsv(rows, headers), __dashboardExportFileName(section, params, fileName));
}

async function __fetchWorkspaceExportPayload(scope, subpath, params) {
  const endpoint = `/dashboard/${scope}/${subpath}${__toDashboardExportQs(params)}`;
  const resp = await fetchDashboardDeferred(endpoint);
  return __assertExportPayloadReady(resp);
}

async function __fetchPartnerExportPayload(scope, subpath, params) {
  const resp = await fetchPartnerDashboardDeferred(scope, subpath, params);
  return __assertExportPayloadReady(resp);
}

async function __fetchPlatformExportPayload(scope, subpath, params) {
  const resp = await fetchPlatformDashboardDeferred(scope, subpath, params);
  return __assertExportPayloadReady(resp);
}

async function __exportDashboardByFetcher(section, params, fileName, fetcher) {
  const spec = DASHBOARD_EXPORT_ROUTE[section];
  if (!spec) {
    throw __dashboardExportError(
      "DASHBOARD_EXPORT_UNSUPPORTED",
      "Esta aba não tem exportação de CSV."
    );
  }

  const query = { ...params, ...(spec.extras || {}) };
  if (!query.start && params.start) query.start = params.start;
  if (!query.end && params.end) query.end = params.end;
  const withPeriod = (payload) => {
    if (!payload || typeof payload !== "object") return payload;
    if (payload.period?.start || payload.period?.end) return payload;
    return { ...payload, period: { start: params.start || "", end: params.end || "" } };
  };
  let rows = [];

  if (spec.bundle) {
    const parts = await Promise.all(
      ORIGEM_BUNDLE.map(async (item) => {
        const payload = await fetcher(item.scope, item.subpath, { ...query, ...(item.extras || {}) });
        return __normalizeDashboardToRows(item.scope, withPeriod(payload || {}));
      })
    );
    rows = parts.flat();
  } else {
    const payload = await fetcher(section, spec.subpath, query);
    rows = __normalizeDashboardToRows(section, withPeriod(payload || {}));
  }

  __finishDashboardCsv(section, params, fileName, rows);
}

/**
 * Faz o download de um CSV do dashboard.
 * Usa a mesma rota da aba (summary/charts + cache/job 202) para não recalcular do zero.
 */
export const exportDashboardCsv = async (section, params = {}, fileName) => {
  await __exportDashboardByFetcher(section, params, fileName, __fetchWorkspaceExportPayload);
};

/** Exporta CSV do dashboard consolidado do representante (mesmas seções do workspace). */
export const exportPartnerDashboardCsv = async (section, params = {}, fileName) => {
  await __exportDashboardByFetcher(section, params, fileName, __fetchPartnerExportPayload);
};

/** Exporta CSV do dashboard consolidado da plataforma. */
export const exportPlatformDashboardCsv = async (section, params = {}, fileName) => {
  await __exportDashboardByFetcher(section, params, fileName, __fetchPlatformExportPayload);
};

export const fetchPublicSurvey = async (slug, company, token = null) => {
  const query = new URLSearchParams();
  query.set("company", company);
  if (token) query.set("token", token);
  const { data } = await apiClient.get(`/public/surveys/${slug}?${query.toString()}`, {
    headers: { Company: company },
  });
  const payload = data?.data ?? data;
  return { data: payload ?? null };
};

export const startPublicSurveySession = async (slug, company, payload = {}) => {
  const query = new URLSearchParams({ company }).toString();
  const { data } = await apiClient.post(`/public/surveys/${slug}/sessions?${query}`, payload, {
    headers: { Company: company, "Content-Type": "application/json" },
  });
  const inner = data?.data ?? data;
  return { data: inner ?? null };
};

export const submitPublicSurveyAnswers = async (slug, company, token, answers = []) => {
  const query = new URLSearchParams({ company }).toString();
  const { data } = await apiClient.post(
    `/public/surveys/${slug}/sessions/${token}/complete?${query}`,
    { answers },
    {
      headers: { Company: company, "Content-Type": "application/json" },
    }
  );
  const inner = data?.data ?? data;
  return { data: inner ?? null };
};

/** POST /dashboard/presentations — link público de apresentação do painel. */
export async function createDashboardPresentation(payload) {
  const { data } = await apiClient.post("/dashboard/presentations", payload, {
    headers: { "Content-Type": "application/json" },
  });
  return data?.data ?? data;
}

/** GET /public/dashboard/presentations/:token — visualização sem autenticação. */
export async function fetchPublicDashboardPresentation(token) {
  const safe = encodeURIComponent(String(token || "").trim());
  const { data } = await apiClient.get(`/public/dashboard/presentations/${safe}`, {
    skipAuthHeader: true,
    skipCompanyHeader: true,
  });
  return data?.data ?? data;
}

export const exportSurveyResponsesCsv = async (surveyId, filters = {}, fileName = "") => {
  const params = new URLSearchParams();
  if (filters.startDate) params.set("startDate", filters.startDate);
  if (filters.endDate) params.set("endDate", filters.endDate);
  if (filters.sourceLabel) params.set("sourceLabel", filters.sourceLabel);
  if (filters.channel) params.set("channel", filters.channel);
  const query = params.toString();
  const response = await apiClient.get(
    `/surveys/${surveyId}/responses/export${query ? `?${query}` : ""}`,
    { responseType: "text" }
  );
  const csvText = typeof response.data === "string" ? response.data : "";
  const rowCount = Number(response.headers?.["x-export-row-count"] || 0);
  const totalRows = Number(response.headers?.["x-export-total-rows"] || rowCount);
  const truncated = String(response.headers?.["x-export-truncated"] || "").toLowerCase() === "true";
  const blob = new Blob([csvText], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName || `pesquisa-${surveyId}-respostas.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return { rowCount, totalRows, truncated };
};

/** @returns {Promise<Array>} */
export const fetchOperatorNotifications = async (limit = 50) => {
  const res = await fetchData(
    `/operator-notifications?limit=${encodeURIComponent(String(limit))}`,
    "GET"
  );
  const d = res?.data;
  return Array.isArray(d) ? d : [];
};

export const appendOperatorNotification = async (payload) => {
  return fetchData("/operator-notifications", "POST", payload);
};

export const markOperatorNotificationsRead = async (ids) => {
  return fetchData("/operator-notifications/mark-read", "POST", { ids });
};

export const markOperatorNotificationsReadByDeepLink = async (deepLink) => {
  return fetchData("/operator-notifications/mark-read-by-link", "POST", {
    deep_link: deepLink,
  });
};

/** @returns {Promise<Array>} prompts pendentes de pesquisa (automação) */
export const fetchPendingAutomationSurveyPrompts = async (leadId = null) => {
  const params = new URLSearchParams();
  if (leadId != null) params.set("leadId", String(leadId));
  const query = params.toString();
  const res = await fetchData(
    `/automation-survey-prompts/pending${query ? `?${query}` : ""}`,
    "GET"
  );
  const list = res?.data?.prompts;
  return Array.isArray(list) ? list : [];
};

export const startAutomationSurveyPrompt = async (promptId) => {
  const res = await fetchData(`/automation-survey-prompts/${promptId}/start`, "POST");
  return res?.data || null;
};

export const completeAutomationSurveyPrompt = async (promptId, answers = []) => {
  const res = await fetchData(`/automation-survey-prompts/${promptId}/complete`, "POST", {
    answers,
  });
  return res?.data || null;
};

export const dismissAutomationSurveyPrompt = async (promptId) => {
  const res = await fetchData(`/automation-survey-prompts/${promptId}/dismiss`, "POST");
  return res?.data || null;
};

export const fetchSurveySessionDetail = async (surveyId, sessionId, leadId = null) => {
  const params = new URLSearchParams();
  if (leadId != null && leadId !== "") params.set("leadId", String(leadId));
  const query = params.toString();
  const res = await fetchData(
    `/surveys/${surveyId}/sessions/${sessionId}${query ? `?${query}` : ""}`
  );
  return res?.data ?? res;
};

export const fetchPushVapidPublicKey = async () => {
  const res = await fetchData("/push/vapid-public-key", "GET");
  const d = res?.data;
  if (d && typeof d === "object" && d.publicKey) return d.publicKey;
  return null;
};

export const subscribeWebPush = async (subscriptionObject, meta = {}) => {
  return fetchData("/push/subscribe", "POST", {
    subscription: subscriptionObject,
    device_label: meta.deviceLabel || meta.device_label || null,
    user_agent: meta.userAgent || meta.user_agent || null,
  });
};

export const fetchPushSubscriptions = async () => {
  const res = await fetchData("/push/subscriptions", "GET");
  const d = res?.data;
  return Array.isArray(d) ? d : [];
};

export const unsubscribeWebPush = async ({ id, endpoint } = {}) => {
  return fetchData("/push/unsubscribe", "POST", { id, endpoint });
};

/** Opções de tenant para APIs de equipe (console plataforma / portal revenda). */
const teamCompanyOpts = (company) => (company ? { company: String(company).trim() } : {});

/** Gera título de tarefa e itens de checklist a partir das mensagens da conversa interna (IA). */
export const generateTeamSpaceTaskChecklist = async (spaceId, body = {}, company) => {
  if (!spaceId) {
    throw new Error("spaceId é obrigatório para gerar tarefas com IA.");
  }
  const res = await fetchData(
    `/team-spaces/${encodeURIComponent(String(spaceId))}/generate-task-checklist`,
    "POST",
    body,
    teamCompanyOpts(company)
  );
  return res?.data ?? res;
};

/** Comunicação interna de equipe */
export const fetchTeamSpaces = async (company) => {
  const res = await fetchData("/team-spaces", "GET", null, teamCompanyOpts(company));
  return res?.data?.spaces || res?.spaces || [];
};

export const fetchTeamSpace = async (spaceId, company) => {
  const res = await fetchData(`/team-spaces/${spaceId}`, "GET", null, teamCompanyOpts(company));
  return {
    space: res?.data?.space || res?.space || null,
    members: res?.data?.members || res?.members || [],
  };
};

export const createTeamChannel = async (payload, company) => {
  const res = await fetchData("/team-spaces", "POST", payload, teamCompanyOpts(company));
  return res?.data || res;
};

export const updateTeamSpace = async (spaceId, payload, company) => {
  return fetchData(`/team-spaces/${spaceId}`, "PATCH", payload, teamCompanyOpts(company));
};

export const createTeamDm = async (payload, company) => {
  const body =
    typeof payload === "number" || typeof payload === "string"
      ? { userId: Number(payload) }
      : { ...(payload || {}) };
  const res = await fetchData("/team-spaces/dm", "POST", body, teamCompanyOpts(company));
  return res?.data || res;
};

export const getTeamSpaceForLead = async (leadId, company) => {
  const res = await fetchData(
    `/team-spaces/for-lead/${leadId}`,
    "GET",
    null,
    teamCompanyOpts(company)
  );
  return res?.data || res;
};

export const createTeamSpaceForLead = async (leadId, company) => {
  const res = await fetchData("/team-spaces/for-lead", "POST", { leadId }, teamCompanyOpts(company));
  return res?.data || res;
};

export const fetchTeamMessages = async (spaceId, { before, after, limit = 50, company } = {}) => {
  const qs = new URLSearchParams();
  if (before) qs.set("before", String(before));
  if (after) qs.set("after", String(after));
  if (limit) qs.set("limit", String(limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const res = await fetchData(
    `/team-spaces/${spaceId}/messages${suffix}`,
    "GET",
    null,
    teamCompanyOpts(company)
  );
  return {
    messages: res?.data?.messages || res?.messages || [],
    nextBefore: res?.data?.nextBefore ?? res?.nextBefore ?? null,
    hasMore: Boolean(res?.data?.hasMore ?? res?.hasMore),
  };
};

export const sendTeamMessage = async (payload, company) => {
  const res = await fetchData("/team-messages", "POST", payload, teamCompanyOpts(company));
  return res?.data || res;
};

export const editTeamMessage = async (messageId, payload, company) => {
  const res = await fetchData(
    `/team-messages/${messageId}`,
    "PATCH",
    payload,
    teamCompanyOpts(company)
  );
  return res?.data || res;
};

export const deleteTeamMessage = async (messageId, company) => {
  const res = await fetchData(
    `/team-messages/${messageId}`,
    "DELETE",
    null,
    teamCompanyOpts(company)
  );
  return res?.data || res;
};

export const toggleTeamMessageReaction = async (messageId, emoji, company) => {
  const res = await fetchData(
    `/team-messages/${messageId}/reactions`,
    "POST",
    { emoji },
    teamCompanyOpts(company)
  );
  return res?.data || res;
};

export const markTeamMessagePlayed = async (messageId, company) => {
  const res = await fetchData(
    `/team-messages/${messageId}/played`,
    "POST",
    {},
    teamCompanyOpts(company)
  );
  return res?.data || res;
};

export const markTeamSpaceRead = async (spaceId, company) => {
  return fetchData(`/team-spaces/${spaceId}/read`, "PATCH", {}, teamCompanyOpts(company));
};

export const sendTeamSpaceTyping = async (spaceId, isTyping, company) => {
  if (!spaceId) return null;
  return fetchData(
    `/team-spaces/${spaceId}/typing`,
    "POST",
    { isTyping: Boolean(isTyping) },
    teamCompanyOpts(company)
  );
};

export const addTeamSpaceMember = async (spaceId, payload, company) => {
  return fetchData(`/team-spaces/${spaceId}/members`, "POST", payload, teamCompanyOpts(company));
};

export const removeTeamSpaceMember = async (spaceId, memberId, company) => {
  return fetchData(
    `/team-spaces/${spaceId}/members/${memberId}`,
    "DELETE",
    null,
    teamCompanyOpts(company)
  );
};

export const deleteTeamSpace = async (spaceId, company) => {
  return fetchData(`/team-spaces/${spaceId}`, "DELETE", null, teamCompanyOpts(company));
};

/**
 * Console plataforma: master com JWT de workspace mantém header Company (tenant) como demais rotas
 * billing/admin; login scope=platform segue sem Company e o API roteia via JWT escopado.
 */
function platformPortalTeamRequestOpts() {
  const company = getWorkspaceDatabaseHeaderValue();
  const slug = company != null ? String(company).trim() : "";
  if (slug && slug.toLowerCase() !== "xprobase") {
    return { company: slug };
  }
  return { skipCompanyHeader: true };
}

/** Busca contatos de equipe em todos os workspaces acessíveis (console plataforma). */
export const searchPlatformPortalTeamContacts = async (q) => {
  const res = await fetchData(
    `/billing/admin/portal-team/contacts?q=${encodeURIComponent(String(q || "").trim())}`,
    "GET",
    null,
    platformPortalTeamRequestOpts()
  );
  return res?.data?.contacts || res?.contacts || [];
};

export const listPlatformPortalTeamWorkspaces = async () => {
  const res = await fetchData(
    "/billing/admin/portal-team/workspaces",
    "GET",
    null,
    platformPortalTeamRequestOpts()
  );
  return res?.data?.workspaces || res?.workspaces || [];
};

/** Diretório agrupado por workspace: DMs existentes + usuários online/ausentes. */
export const fetchPlatformPortalTeamDirectory = async (workspaceSlug) => {
  const slug = workspaceSlug != null ? String(workspaceSlug).trim() : "";
  const qs = slug ? `?workspace=${encodeURIComponent(slug)}` : "";
  const res = await fetchData(
    `/billing/admin/portal-team/directory${qs}`,
    "GET",
    null,
    platformPortalTeamRequestOpts()
  );
  return res?.data?.workspaces || res?.workspaces || [];
};

/** Busca contatos de equipe nos workspaces do representante. */
export const searchPartnerPortalTeamContacts = async (q) => {
  const res = await fetchData(
    `/billing/partner/portal-team/contacts?q=${encodeURIComponent(String(q || "").trim())}`,
    "GET",
    null,
    { skipCompanyHeader: true }
  );
  return res?.data?.contacts || res?.contacts || [];
};

export const listPartnerPortalTeamWorkspaces = async () => {
  const res = await fetchData("/billing/partner/portal-team/workspaces", "GET", null, {
    skipCompanyHeader: true,
  });
  return res?.data?.workspaces || res?.workspaces || [];
};

export const fetchPartnerPortalTeamDirectory = async (workspaceSlug) => {
  const slug = workspaceSlug != null ? String(workspaceSlug).trim() : "";
  const qs = slug ? `?workspace=${encodeURIComponent(slug)}` : "";
  const res = await fetchData(`/billing/partner/portal-team/directory${qs}`, "GET", null, {
    skipCompanyHeader: true,
  });
  return res?.data?.workspaces || res?.workspaces || [];
};

/** Tarefas da equipe (painéis) */
const unwrapTeamBoardPayload = (res) => res?.data ?? res ?? {};

export const fetchTeamBoards = async ({ spaceId, leadId, workspace } = {}) => {
  const qs = new URLSearchParams();
  if (spaceId != null) qs.set("spaceId", String(spaceId));
  if (leadId != null) qs.set("leadId", String(leadId));
  if (workspace) qs.set("workspace", "1");
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const res = await fetchData(`/team-boards${suffix}`, "GET");
  const payload = unwrapTeamBoardPayload(res);
  return {
    boards: payload.boards || [],
    clientTasksAggregate: payload.clientTasksAggregate || null,
  };
};

export const fetchOrCreateTeamBoardForClient = async (clientId) => {
  const res = await fetchData(`/team-boards/for-client/${clientId}`, "POST", {});
  return unwrapTeamBoardPayload(res);
};

export const fetchClientBoardTasks = async ({ status = "open", clientId, limit, offset } = {}) => {
  const qs = new URLSearchParams();
  if (status) qs.set("status", String(status));
  if (clientId != null && clientId !== "") qs.set("clientId", String(clientId));
  if (limit != null) qs.set("limit", String(limit));
  if (offset != null) qs.set("offset", String(offset));
  const res = await fetchData(`/team-boards/client-tasks?${qs.toString()}`, "GET");
  return unwrapTeamBoardPayload(res);
};

export const fetchTeamBoard = async (boardId, { contextLeadId } = {}) => {
  const qs =
    contextLeadId != null && Number(contextLeadId) > 0
      ? `?contextLeadId=${encodeURIComponent(contextLeadId)}`
      : "";
  const res = await fetchData(`/team-boards/${boardId}${qs}`, "GET");
  return unwrapTeamBoardPayload(res);
};

export const fetchWorkspaceTaskAnalytics = async (params = {}) => {
  const qs = new URLSearchParams();
  if (params.start) qs.set("start", params.start);
  if (params.end) qs.set("end", params.end);
  if (params.userId != null && params.userId !== "") qs.set("userId", String(params.userId));
  const res = await fetchData(`/team-boards/analytics?${qs.toString()}`, "GET");
  return unwrapTeamBoardPayload(res);
};

export const fetchTeamBoardAnalytics = async (boardId, params = {}) => {
  const qs = new URLSearchParams();
  if (params.start) qs.set("start", params.start);
  if (params.end) qs.set("end", params.end);
  if (params.userId != null && params.userId !== "") qs.set("userId", String(params.userId));
  if (params.clientId != null && params.clientId !== "") qs.set("clientId", String(params.clientId));
  if (params.columnId != null && params.columnId !== "") qs.set("columnId", String(params.columnId));
  if (params.required != null && params.required !== "") qs.set("required", String(params.required));
  const res = await fetchData(`/team-boards/${boardId}/analytics?${qs.toString()}`, "GET");
  return unwrapTeamBoardPayload(res);
};

export const fetchTeamBoardTasks = async ({
  scope = "me",
  summary,
  assigneeId,
  status = "open",
  overdue,
  due,
  required,
  clientId,
  limit,
  offset,
} = {}) => {
  const qs = new URLSearchParams();
  if (summary) qs.set("summary", String(summary));
  else qs.set("scope", scope);
  if (assigneeId != null && String(assigneeId).trim() !== "") {
    qs.set("assigneeId", String(assigneeId));
  }
  if (!summary && status) qs.set("status", status);
  if (overdue) qs.set("overdue", "1");
  if (due) qs.set("due", String(due));
  if (required) qs.set("required", "1");
  if (clientId != null && String(clientId).trim() !== "") {
    qs.set("clientId", String(clientId));
  }
  if (limit != null) qs.set("limit", String(limit));
  if (offset != null) qs.set("offset", String(offset));
  const res = await fetchData(`/team-boards/tasks?${qs.toString()}`, "GET");
  return unwrapTeamBoardPayload(res);
};

/** Contagem de tarefas abertas por responsável (painel Ao vivo). */
export const fetchTeamTaskWorkload = async () => {
  const res = await fetchData("/team-boards/workload", "GET");
  const data = unwrapTeamBoardPayload(res);
  return {
    visible: Boolean(data.visible),
    counts: Array.isArray(data.counts) ? data.counts : [],
  };
};

export const createTeamBoard = async (payload) => {
  const res = await fetchData("/team-boards", "POST", payload);
  return unwrapTeamBoardPayload(res);
};

export const updateTeamBoard = async (boardId, payload) => {
  const res = await fetchData(`/team-boards/${boardId}`, "PATCH", payload);
  return unwrapTeamBoardPayload(res);
};

export const deleteTeamBoard = async (boardId) => {
  return fetchData(`/team-boards/${boardId}`, "DELETE");
};

export const fetchOrCreateTeamBoardForSpace = async (spaceId) => {
  const res = await fetchData(`/team-boards/for-space/${spaceId}`, "POST", {});
  return unwrapTeamBoardPayload(res);
};

export const fetchOrCreateTeamBoardForLead = async (leadId) => {
  const res = await fetchData(`/team-boards/for-lead/${leadId}`, "POST", {});
  return unwrapTeamBoardPayload(res);
};

export const setLeadTaskBoard = async (leadId, boardId) => {
  const res = await fetchData(`/team-boards/for-lead/${leadId}`, "PATCH", {
    boardId: boardId == null ? null : Number(boardId),
  });
  return unwrapTeamBoardPayload(res);
};

export const createTeamBoardColumn = async (boardId, payload) => {
  const res = await fetchData(`/team-boards/${boardId}/columns`, "POST", payload);
  return unwrapTeamBoardPayload(res);
};

export const updateTeamBoardColumn = async (boardId, columnId, payload) => {
  const res = await fetchData(`/team-boards/${boardId}/columns/${columnId}`, "PATCH", payload);
  return unwrapTeamBoardPayload(res);
};

export const deleteTeamBoardColumn = async (boardId, columnId) => {
  const res = await fetchData(`/team-boards/${boardId}/columns/${columnId}`, "DELETE");
  return unwrapTeamBoardPayload(res);
};

export const reorderTeamBoardColumns = async (boardId, columnIds) => {
  const res = await fetchData(`/team-boards/${boardId}/columns/reorder`, "PATCH", { columnIds });
  return unwrapTeamBoardPayload(res);
};

export const createTeamBoardCard = async (boardId, payload) => {
  const res = await fetchData(`/team-boards/${boardId}/cards`, "POST", payload);
  return unwrapTeamBoardPayload(res);
};

export const createTeamBoardLinkedCard = async (boardId, cardId, payload) => {
  const res = await fetchData(
    `/team-boards/${boardId}/cards/${cardId}/linked-cards`,
    "POST",
    payload
  );
  return unwrapTeamBoardPayload(res);
};

export const fetchLeadTasks = async (leadId) => {
  const res = await fetchData(`/leads/${leadId}/tasks`, "GET");
  return unwrapTeamBoardPayload(res);
};

export const updateTeamBoardCard = async (boardId, cardId, payload) => {
  const res = await fetchData(`/team-boards/${boardId}/cards/${cardId}`, "PATCH", payload);
  return unwrapTeamBoardPayload(res);
};

export const moveTeamBoardCard = async (boardId, cardId, payload) => {
  const res = await fetchData(`/team-boards/${boardId}/cards/${cardId}/move`, "PATCH", payload);
  return unwrapTeamBoardPayload(res);
};

export const deleteTeamBoardCard = async (boardId, cardId, payload) => {
  const res = await fetchData(
    `/team-boards/${boardId}/cards/${cardId}`,
    "DELETE",
    payload || {}
  );
  return unwrapTeamBoardPayload(res);
};

export const createTeamBoardLabel = async (boardId, payload) => {
  const res = await fetchData(`/team-boards/${boardId}/labels`, "POST", payload);
  return unwrapTeamBoardPayload(res);
};

export const createTeamBoardCardChecklist = async (boardId, cardId, payload) => {
  const res = await fetchData(`/team-boards/${boardId}/cards/${cardId}/checklists`, "POST", payload);
  return unwrapTeamBoardPayload(res);
};

export const updateTeamBoardCardChecklist = async (boardId, cardId, checklistId, payload) => {
  const res = await fetchData(
    `/team-boards/${boardId}/cards/${cardId}/checklists/${checklistId}`,
    "PATCH",
    payload
  );
  return unwrapTeamBoardPayload(res);
};

export const deleteTeamBoardCardChecklist = async (boardId, cardId, checklistId) => {
  const res = await fetchData(
    `/team-boards/${boardId}/cards/${cardId}/checklists/${checklistId}`,
    "DELETE"
  );
  return unwrapTeamBoardPayload(res);
};

export const createTeamBoardChecklistItem = async (boardId, cardId, checklistId, payload) => {
  const res = await fetchData(
    `/team-boards/${boardId}/cards/${cardId}/checklists/${checklistId}/items`,
    "POST",
    payload
  );
  return unwrapTeamBoardPayload(res);
};

export const updateTeamBoardChecklistItem = async (boardId, cardId, itemId, payload) => {
  const res = await fetchData(
    `/team-boards/${boardId}/cards/${cardId}/checklist-items/${itemId}`,
    "PATCH",
    payload
  );
  return unwrapTeamBoardPayload(res);
};

export const deleteTeamBoardChecklistItem = async (boardId, cardId, itemId) => {
  const res = await fetchData(
    `/team-boards/${boardId}/cards/${cardId}/checklist-items/${itemId}`,
    "DELETE"
  );
  return unwrapTeamBoardPayload(res);
};

export const createTeamBoardCardAttachment = async (boardId, cardId, payload) => {
  const res = await fetchData(`/team-boards/${boardId}/cards/${cardId}/attachments`, "POST", payload);
  return unwrapTeamBoardPayload(res);
};

export const deleteTeamBoardCardAttachment = async (boardId, cardId, attachmentId) => {
  const res = await fetchData(
    `/team-boards/${boardId}/cards/${cardId}/attachments/${attachmentId}`,
    "DELETE"
  );
  return unwrapTeamBoardPayload(res);
};

export const createTeamBoardCardComment = async (boardId, cardId, payload) => {
  const res = await fetchData(`/team-boards/${boardId}/cards/${cardId}/comments`, "POST", payload);
  return unwrapTeamBoardPayload(res);
};

export const fetchTeamBoardCardLocation = async (cardId) => {
  const res = await fetchData(`/team-boards/cards/${cardId}/location`, "GET");
  return unwrapTeamBoardPayload(res);
};

export const updateTeamBoardCardComment = async (boardId, cardId, commentId, payload) => {
  const res = await fetchData(
    `/team-boards/${boardId}/cards/${cardId}/comments/${commentId}`,
    "PATCH",
    payload
  );
  return unwrapTeamBoardPayload(res);
};

export const deleteTeamBoardCardComment = async (boardId, cardId, commentId) => {
  const res = await fetchData(
    `/team-boards/${boardId}/cards/${cardId}/comments/${commentId}`,
    "DELETE"
  );
  return unwrapTeamBoardPayload(res);
};

export const addTeamBoardMember = async (boardId, payload) => {
  const res = await fetchData(`/team-boards/${boardId}/members`, "POST", payload);
  return unwrapTeamBoardPayload(res).members || [];
};

export const updateTeamBoardMemberRole = async (boardId, memberId, role) => {
  const res = await fetchData(`/team-boards/${boardId}/members/${memberId}`, "PATCH", { role });
  return unwrapTeamBoardPayload(res).members || [];
};

export const removeTeamBoardMember = async (boardId, memberId) => {
  const res = await fetchData(`/team-boards/${boardId}/members/${memberId}`, "DELETE");
  return unwrapTeamBoardPayload(res).members || [];
};

export const fetchLeadRequiredTasks = async (leadId) => {
  const res = await fetchData(`/leads/${leadId}/required-tasks`, "GET");
  const data = unwrapTeamBoardPayload(res);
  return data.pending || [];
};

export const fetchTeamTaskTemplates = async (tipoId) => {
  const res = await fetchData(`/team-task-templates?tipoId=${encodeURIComponent(tipoId)}`, "GET");
  const data = unwrapTeamBoardPayload(res);
  return {
    templates: data.templates || [],
    boardId: data.boardId != null ? Number(data.boardId) : null,
  };
};

export const saveTeamTaskTemplates = async (tipoId, templates, { boardId } = {}) => {
  const body = { tipoId, templates };
  if (boardId !== undefined) {
    body.boardId = boardId == null ? null : Number(boardId);
  }
  const res = await fetchData("/team-task-templates", "POST", body);
  const data = unwrapTeamBoardPayload(res);
  return {
    templates: data.templates || [],
    boardId: data.boardId != null ? Number(data.boardId) : null,
  };
};

export { apiClient };

// --- Workspace rail counts ---

function unwrapWorkspaceRailCounts(res) {
  if (res && typeof res === "object" && "data" in res) return res.data;
  return res;
}

/** Contadores da barra lateral do workspace (respeitam filtros persistidos do atendimento). */
export const fetchWorkspaceRailCounts = async ({
  atendimentoLastSeen,
  agendaDismissed,
  atendimentoFilters,
} = {}) => {
  const res = await fetchData("/users/me/workspace-rail-counts", "POST", {
    atendimentoLastSeen: atendimentoLastSeen || {},
    agendaDismissed: Array.isArray(agendaDismissed) ? agendaDismissed : [],
    atendimentoFilters: atendimentoFilters || null,
  });
  const data = unwrapWorkspaceRailCounts(res) || {};
  return {
    atendimentoUnread: Number(data.atendimentoUnread ?? 0),
    teamChatUnread: Number(data.teamChatUnread ?? 0),
    tasksOpen: Number(data.tasksOpen ?? 0),
    agendaActive: Number(data.agendaActive ?? 0),
  };
};

// --- User Agenda ---

function unwrapAgendaPayload(res) {
  if (res && typeof res === "object" && "data" in res) return res.data;
  return res;
}

export const fetchUserAgenda = async ({ from, to } = {}) => {
  const qs = new URLSearchParams();
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return fetchData(`/users/me/agenda${suffix}`, "GET");
};

/** Lembretes de agenda (manual + Google) e tarefas com prazo. */
export const fetchScheduleReminders = async () => {
  const res = await fetchData("/users/me/agenda/reminders", "GET");
  return unwrapAgendaPayload(res);
};

export const createUserAgendaEvent = async (payload) => {
  const res = await fetchData("/users/me/agenda/events", "POST", payload);
  return unwrapAgendaPayload(res);
};

export const updateUserAgendaEvent = async (eventId, payload) => {
  const res = await fetchData(`/users/me/agenda/events/${eventId}`, "PATCH", payload);
  return unwrapAgendaPayload(res);
};

export const deleteUserAgendaEvent = async (eventId) => {
  return fetchData(`/users/me/agenda/events/${eventId}`, "DELETE");
};

export const fetchUserAgendaCalendars = async () => {
  const res = await fetchData("/users/me/agenda/calendars", "GET");
  return unwrapAgendaPayload(res);
};

export const createUserAgendaCalendar = async (payload) => {
  const res = await fetchData("/users/me/agenda/calendars", "POST", payload);
  return unwrapAgendaPayload(res);
};

export const updateUserAgendaCalendar = async (calendarId, payload) => {
  const res = await fetchData(`/users/me/agenda/calendars/${calendarId}`, "PATCH", payload);
  return unwrapAgendaPayload(res);
};

export const deleteUserAgendaCalendar = async (calendarId) => {
  return fetchData(`/users/me/agenda/calendars/${calendarId}`, "DELETE");
};

export const getGoogleCalendarOauthUrl = async () => {
  const res = await fetchData("/users/me/google-calendar/oauth-url", "GET");
  const payload = unwrapAgendaPayload(res);
  if (payload?.url) return payload;
  const url = res?.data?.url || res?.url;
  return url ? { url } : payload;
};

export const getGoogleCalendarStatus = async () => {
  const res = await fetchData("/users/me/google-calendar/status", "GET");
  return unwrapAgendaPayload(res);
};

export const fetchGoogleCalendarList = async () => {
  const res = await fetchData("/users/me/google-calendar/calendars", "GET");
  return unwrapAgendaPayload(res);
};

export const fetchAgendaAvailability = async (payload) => {
  const res = await fetchData("/users/me/agenda/availability", "POST", payload);
  return unwrapAgendaPayload(res);
};

export const fetchAgendaConflicts = async (payload) => {
  const res = await fetchData("/users/me/agenda/conflicts", "POST", payload);
  return unwrapAgendaPayload(res);
};

export const fetchAgendaAttendeeSchedule = async (payload) => {
  const res = await fetchData("/users/me/agenda/attendee-schedule", "POST", payload);
  return unwrapAgendaPayload(res);
};

export const disconnectGoogleCalendar = async ({ removeImported = false } = {}) => {
  return fetchData("/users/me/google-calendar", "DELETE", { removeImported: Boolean(removeImported) });
};

export const patchGoogleCalendarSettings = async (payload) => {
  return fetchData("/users/me/google-calendar", "PATCH", payload);
};

export const syncGoogleCalendar = async ({ forceFullSync = false } = {}) => {
  const res = await fetchData(
    "/users/me/google-calendar/sync",
    "POST",
    forceFullSync ? { forceFullSync: true } : {},
    { timeout: 60_000 }
  );
  return unwrapAgendaPayload(res);
};

/** —— Twilio Video / Google Meet —— */

function unwrapVideoPayload(res) {
  if (res == null) return null;
  if (res.data != null && typeof res.data === "object" && !Array.isArray(res.data)) {
    return res.data;
  }
  return res;
}

export async function fetchPublicVideoRoom(token, company, { live } = {}) {
  const safe = encodeURIComponent(String(token || "").trim());
  const params = new URLSearchParams({ company: String(company || "").trim() });
  if (live === false) params.set("live", "0");
  else if (live === true) params.set("live", "1");
  const { data } = await apiClient.get(`/public/video/${safe}?${params}`, {
    headers: { Company: company },
  });
  return unwrapVideoPayload(data) ?? data?.data ?? data;
}

export async function fetchPublicVideoAccessToken(token, company, payload = {}) {
  const safe = encodeURIComponent(String(token || "").trim());
  const query = new URLSearchParams({ company: String(company || "").trim() }).toString();
  const { data } = await apiClient.post(`/public/video/${safe}/access-token?${query}`, payload, {
    headers: { Company: company, "Content-Type": "application/json" },
  });
  return unwrapVideoPayload(data) ?? data?.data ?? data;
}

export async function fetchPublicVideoLobby(lobbyToken, company) {
  const safe = encodeURIComponent(String(lobbyToken || "").trim());
  const query = new URLSearchParams({ company: String(company || "").trim() }).toString();
  const { data } = await apiClient.get(`/public/video/lobby/${safe}?${query}`, {
    headers: { Company: company },
  });
  return unwrapVideoPayload(data) ?? data?.data ?? data;
}

export async function startPublicVideoLobby(lobbyToken, company, payload = {}) {
  const safe = encodeURIComponent(String(lobbyToken || "").trim());
  const query = new URLSearchParams({ company: String(company || "").trim() }).toString();
  const { data } = await apiClient.post(`/public/video/lobby/${safe}/start?${query}`, payload, {
    headers: { Company: company, "Content-Type": "application/json" },
  });
  return unwrapVideoPayload(data) ?? data?.data ?? data;
}

export async function postPublicVideoTranscript(token, company, payload = {}) {
  const safe = encodeURIComponent(String(token || "").trim());
  const query = new URLSearchParams({ company: String(company || "").trim() }).toString();
  const { data } = await apiClient.post(`/public/video/${safe}/transcript?${query}`, payload, {
    headers: { Company: company, "Content-Type": "application/json" },
  });
  return unwrapVideoPayload(data) ?? data?.data ?? data;
}

export async function postPublicVideoCaptions(token, company, payload = {}) {
  const safe = encodeURIComponent(String(token || "").trim());
  const query = new URLSearchParams({ company: String(company || "").trim() }).toString();
  const { data } = await apiClient.post(`/public/video/${safe}/captions?${query}`, payload, {
    headers: { Company: company, "Content-Type": "application/json" },
  });
  return unwrapVideoPayload(data) ?? data?.data ?? data;
}

function publicVideoLeaveBody(payload = {}) {
  if (!payload || typeof payload !== "object") return {};
  return {
    ...(payload.role ? { role: String(payload.role) } : {}),
    ...(payload.displayName ? { displayName: String(payload.displayName) } : {}),
    ...(payload.identity ? { identity: String(payload.identity) } : {}),
  };
}

export async function postPublicVideoLeave(token, company, payload = {}) {
  const safe = encodeURIComponent(String(token || "").trim());
  const query = new URLSearchParams({ company: String(company || "").trim() }).toString();
  const body = publicVideoLeaveBody(payload);
  const { data } = await apiClient.post(`/public/video/${safe}/leave?${query}`, body, {
    headers: { Company: company, "Content-Type": "application/json" },
  });
  return unwrapVideoPayload(data) ?? data?.data ?? data;
}

/**
 * Saída best-effort ao voltar para o app (navegação que desmonta a sala).
 * fetch keepalive sobrevive ao location.assign; sendBeacon não envia Authorization.
 */
export function postPublicVideoLeaveKeepalive(token, company, payload = {}) {
  const safe = encodeURIComponent(String(token || "").trim());
  const companySlug = String(company || "").trim();
  if (!safe || !companySlug) return;
  try {
    const bearer = readTabBearerToken();
    const base = String(getApiBaseURL() || "").replace(/\/+$/, "");
    if (!base) return;
    const query = new URLSearchParams({ company: companySlug }).toString();
    const headers = { "Content-Type": "application/json", Company: companySlug };
    if (bearer) headers.Authorization = `Bearer ${bearer}`;
    void fetch(`${base}/public/video/${safe}/leave?${query}`, {
      method: "POST",
      headers,
      body: JSON.stringify(publicVideoLeaveBody(payload)),
      keepalive: true,
      credentials: "omit",
    });
  } catch (err) {
    console.warn("[PublicVideo] leave keepalive:", err?.message || err);
  }
}

export async function postPublicVideoEnd(token, company, payload = {}) {
  const safe = encodeURIComponent(String(token || "").trim());
  const query = new URLSearchParams({ company: String(company || "").trim() }).toString();
  const body =
    payload && typeof payload === "object"
      ? {
          ...(payload.endedByRole ? { endedByRole: String(payload.endedByRole) } : {}),
          ...(payload.role ? { role: String(payload.role) } : {}),
        }
      : {};
  const { data } = await apiClient.post(`/public/video/${safe}/end?${query}`, body, {
    headers: { Company: company, "Content-Type": "application/json" },
  });
  return unwrapVideoPayload(data) ?? data?.data ?? data;
}

export async function fetchPublicVideoMessages(token, company, { afterId, limit } = {}) {
  const safe = encodeURIComponent(String(token || "").trim());
  const params = new URLSearchParams({ company: String(company || "").trim() });
  if (afterId != null && Number(afterId) > 0) params.set("afterId", String(Number(afterId)));
  if (limit != null && Number(limit) > 0) params.set("limit", String(Number(limit)));
  const { data } = await apiClient.get(`/public/video/${safe}/messages?${params}`, {
    headers: { Company: company },
  });
  return unwrapVideoPayload(data) ?? data?.data ?? data;
}

export async function postPublicVideoMessage(token, company, payload = {}) {
  const safe = encodeURIComponent(String(token || "").trim());
  const query = new URLSearchParams({ company: String(company || "").trim() }).toString();
  const { data } = await apiClient.post(`/public/video/${safe}/messages?${query}`, payload, {
    headers: { Company: company, "Content-Type": "application/json" },
  });
  return unwrapVideoPayload(data) ?? data?.data ?? data;
}

export async function signPublicVideoUpload(token, company, payload = {}) {
  const safe = encodeURIComponent(String(token || "").trim());
  const query = new URLSearchParams({ company: String(company || "").trim() }).toString();
  const { data } = await apiClient.post(`/public/video/${safe}/uploads/sign?${query}`, payload, {
    headers: { Company: company, "Content-Type": "application/json" },
  });
  return unwrapVideoPayload(data) ?? data?.data ?? data;
}

const VIDEO_CHAT_CACHE_CONTROL = "public, max-age=31536000, immutable";

export async function uploadPublicVideoFile(token, company, file) {
  const signed = await signPublicVideoUpload(token, company, {
    filename: file?.name || "arquivo",
    contentType: file?.type || "application/octet-stream",
  });
  const uploadUrl = signed?.uploadUrl;
  const fileUrl = signed?.fileUrl;
  if (!uploadUrl || !fileUrl) {
    throw new Error("Falha ao assinar upload.");
  }
  const res = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": file?.type || "application/octet-stream",
      "Cache-Control": VIDEO_CHAT_CACHE_CONTROL,
    },
    body: file,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`PUT S3 falhou (${res.status}) ${text}`.trim());
  }
  return { url: fileUrl, key: signed?.key || "" };
}

export async function postPublicVideoReact(token, company, messageId, payload = {}) {
  const safe = encodeURIComponent(String(token || "").trim());
  const mid = encodeURIComponent(String(messageId || "").trim());
  const query = new URLSearchParams({ company: String(company || "").trim() }).toString();
  const { data } = await apiClient.post(`/public/video/${safe}/messages/${mid}/react?${query}`, payload, {
    headers: { Company: company, "Content-Type": "application/json" },
  });
  return unwrapVideoPayload(data) ?? data?.data ?? data;
}

export async function createVideoRoom(payload) {
  const res = await fetchData("/video/rooms", "POST", payload);
  return unwrapVideoPayload(res) ?? res;
}

export async function getVideoRoomByLead(leadId) {
  const res = await fetchData(`/video/rooms/by-lead/${Number(leadId)}`, "GET");
  return unwrapVideoPayload(res) ?? res;
}

export async function getVideoRoomByTeamSpace(spaceId) {
  const res = await fetchData(`/video/rooms/by-team-space/${Number(spaceId)}`, "GET");
  return unwrapVideoPayload(res) ?? res;
}

export async function fetchActiveVideoRooms() {
  const res = await fetchData("/video/rooms/active", "GET");
  const payload = unwrapVideoPayload(res) ?? res;
  const rooms = Array.isArray(payload?.rooms)
    ? payload.rooms
    : Array.isArray(payload)
      ? payload
      : [];
  return rooms;
}

/** Escritório Virtual */
export async function listVirtualOfficeRooms() {
  return fetchData("/virtual-office/rooms", "GET");
}

export async function createVirtualOfficeRoom(payload) {
  return fetchData("/virtual-office/rooms", "POST", payload);
}

export async function updateVirtualOfficeRoom(id, payload) {
  return fetchData(`/virtual-office/rooms/${Number(id)}`, "PUT", payload);
}

export async function deleteVirtualOfficeRoom(id) {
  return fetchData(`/virtual-office/rooms/${Number(id)}`, "DELETE");
}

export async function joinVirtualOfficeRoom(id, payload = {}) {
  return fetchData(`/virtual-office/rooms/${Number(id)}/join`, "POST", payload);
}

export async function leaveVirtualOfficeRoom(id, payload = {}) {
  return fetchData(`/virtual-office/rooms/${Number(id)}/leave`, "POST", payload);
}

/**
 * Saída best-effort ao fechar a aba (pagehide/unload).
 * Usa fetch keepalive + Bearer da aba (sendBeacon não envia Authorization).
 */
export function leaveVirtualOfficeRoomKeepalive(id, payload = {}) {
  const roomId = Number(id);
  if (!Number.isFinite(roomId) || roomId <= 0) return;
  try {
    const token = readTabBearerToken();
    const company = getActiveWorkspaceSlug() || null;
    const base = String(getApiBaseURL() || "").replace(/\/+$/, "");
    if (!base) return;
    const headers = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (company) headers.Company = company;
    void fetch(`${base}/virtual-office/rooms/${roomId}/leave`, {
      method: "POST",
      headers,
      body: JSON.stringify(payload || {}),
      keepalive: true,
      credentials: "omit",
    });
  } catch (err) {
    console.warn("[virtualOffice] leave keepalive:", err?.message || err);
  }
}

export async function fetchVirtualOfficePresence(id) {
  return fetchData(`/virtual-office/rooms/${Number(id)}/presence`, "GET");
}

export async function patchVirtualOfficePresence(payload) {
  return fetchData("/virtual-office/presence", "PATCH", payload);
}

/** Best-effort ao fechar a aba da call: o PATCH normal some no unload. */
export function patchVirtualOfficePresenceKeepalive(payload = {}) {
  try {
    const token = readTabBearerToken();
    const company = getActiveWorkspaceSlug() || null;
    const base = String(getApiBaseURL() || "").replace(/\/+$/, "");
    if (!base) return;
    const headers = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (company) headers.Company = company;
    void fetch(`${base}/virtual-office/presence`, {
      method: "PATCH",
      headers,
      body: JSON.stringify(payload || {}),
      keepalive: true,
      credentials: "omit",
    });
  } catch (err) {
    console.warn("[virtualOffice] presence keepalive:", err?.message || err);
  }
}

/** Heartbeat/status 404: a sessão do piso foi fechada; reabre e aplica o status. */
export async function patchVirtualOfficePresenceOrRejoin(roomId, payload) {
  try {
    return await patchVirtualOfficePresence(payload);
  } catch (err) {
    const status = Number(err?.status || err?.response?.status);
    const voId = Number(roomId);
    if ((status !== 404 && status !== 400) || !(voId > 0)) throw err;
    if (!shouldRejoinVirtualOfficePresence(payload)) throw err;
    await joinVirtualOfficeRoom(voId, {
      deviceInfo: {
        ua: typeof navigator !== "undefined" ? navigator.userAgent : "",
        source: "virtual_office_other_meeting_rejoin",
      },
    });
    return patchVirtualOfficePresence(payload);
  }
}

export async function signalVirtualOfficeFloor(roomId, payload = {}) {
  return fetchData(`/virtual-office/rooms/${Number(roomId)}/floor-signal`, "POST", payload);
}

export async function fetchVirtualOfficeMySession() {
  return fetchData("/virtual-office/me/session", "GET");
}

export async function walkUpVirtualOffice(roomId, payload = {}) {
  return fetchData(`/virtual-office/rooms/${Number(roomId)}/walk-up`, "POST", payload);
}

export async function startVirtualOfficePrivateConversation(roomId, payload = {}) {
  return fetchData(
    `/virtual-office/rooms/${Number(roomId)}/private-conversation`,
    "POST",
    payload
  );
}

export async function admitVirtualOfficePrivateRoom(videoRoomId, payload = {}) {
  return fetchData(
    `/virtual-office/private-rooms/${Number(videoRoomId)}/admit`,
    "POST",
    payload
  );
}

export async function listVirtualOfficeBirthdayWishes(roomId) {
  return fetchData(`/virtual-office/rooms/${Number(roomId)}/birthday-wishes`, "GET");
}

export async function sendVirtualOfficeBirthdayWish(roomId, payload = {}) {
  return fetchData(`/virtual-office/rooms/${Number(roomId)}/birthday-wishes`, "POST", payload);
}

export async function listVirtualOfficeHighlights(roomId) {
  return fetchData(`/virtual-office/rooms/${Number(roomId)}/highlights`, "GET");
}

export async function listVirtualOfficeSparks(roomId) {
  return fetchData(`/virtual-office/rooms/${Number(roomId)}/sparks`, "GET");
}

export async function listVirtualOfficeHuddles(roomId) {
  return fetchData(`/virtual-office/rooms/${Number(roomId)}/huddles`, "GET");
}

export async function createVirtualOfficeHuddle(roomId, payload = {}) {
  return fetchData(`/virtual-office/rooms/${Number(roomId)}/huddles`, "POST", payload);
}

export async function joinVirtualOfficeHuddle(videoRoomId) {
  return fetchData(`/virtual-office/huddles/${Number(videoRoomId)}/join`, "POST");
}

export async function listVirtualOfficeHuddleJoinRequests(videoRoomId) {
  return fetchData(`/virtual-office/huddles/${Number(videoRoomId)}/join-requests`, "GET");
}

export async function resolveVirtualOfficeHuddleJoinRequest(videoRoomId, requestId, action) {
  return fetchData(
    `/virtual-office/huddles/${Number(videoRoomId)}/join-requests/${Number(requestId)}/resolve`,
    "POST",
    { action }
  );
}

export async function fetchVirtualOfficeAnalyticsLive() {
  return fetchData("/virtual-office/analytics/live", "GET");
}

export async function fetchVirtualOfficeFloorTime({ from, to, userId, roomId } = {}) {
  const qs = new URLSearchParams();
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  if (userId != null) qs.set("userId", String(userId));
  if (roomId != null) qs.set("roomId", String(roomId));
  return fetchData(`/virtual-office/analytics/floor-time?${qs}`, "GET");
}

export async function downloadVirtualOfficeFloorTimeCsv({ from, to, userId, roomId } = {}) {
  const qs = new URLSearchParams();
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  if (userId != null) qs.set("userId", String(userId));
  if (roomId != null) qs.set("roomId", String(roomId));
  return fetchData(`/virtual-office/analytics/export.csv?${qs}`, "GET", null, {
    responseType: "text",
  });
}

/** Controle de ponto */
export async function fetchTimeClockSettings() {
  return fetchData("/time-clock/settings", "GET");
}

export async function updateTimeClockSettings(payload) {
  return fetchData("/time-clock/settings", "PUT", payload);
}

export async function fetchTimeClockMirror({ userId, start, end }) {
  const qs = new URLSearchParams({ start, end });
  if (userId != null) qs.set("userId", String(userId));
  return fetchData(`/time-clock/mirror?${qs}`, "GET");
}

export async function fetchTimeClockMarks({ userId, start, end } = {}) {
  const qs = new URLSearchParams();
  if (userId != null) qs.set("userId", String(userId));
  if (start) qs.set("start", start);
  if (end) qs.set("end", end);
  return fetchData(`/time-clock/marks?${qs}`, "GET");
}

export async function createTimeClockMark(payload) {
  return fetchData("/time-clock/marks", "POST", payload);
}

export async function fetchTimeClockAdjustments({ userId, status } = {}) {
  const qs = new URLSearchParams();
  if (userId != null) qs.set("userId", String(userId));
  if (status) qs.set("status", String(status));
  const q = qs.toString();
  return fetchData(`/time-clock/adjustments${q ? `?${q}` : ""}`, "GET");
}

export async function createTimeClockAdjustment(payload) {
  return fetchData("/time-clock/adjustments", "POST", payload);
}

export async function reviewTimeClockAdjustment(id, payload) {
  return fetchData(`/time-clock/adjustments/${Number(id)}/review`, "POST", payload);
}

export async function consentTimeClock(payload = {}) {
  return fetchData("/time-clock/consent", "POST", payload);
}

export async function fetchTimeClockPolicy(userId) {
  const qs = new URLSearchParams();
  if (userId != null) qs.set("userId", String(userId));
  const q = qs.toString();
  return fetchData(`/time-clock/policy${q ? `?${q}` : ""}`, "GET");
}

export async function fetchTimeClockScheduleStatus({ userId, present } = {}) {
  const qs = new URLSearchParams();
  if (userId != null) qs.set("userId", String(userId));
  if (present != null) qs.set("present", String(present));
  const q = qs.toString();
  return fetchData(`/time-clock/schedule-status${q ? `?${q}` : ""}`, "GET");
}

export async function acknowledgeTimeClockLate(payload = {}) {
  return fetchData("/time-clock/schedule-status/acknowledge-late", "POST", payload);
}

export async function justifyTimeClockLate(payload) {
  return fetchData("/time-clock/schedule-status/justify-late", "POST", payload);
}

export async function justifyTimeClockStay(payload) {
  return fetchData("/time-clock/schedule-status/justify-stay", "POST", payload);
}

export async function justifyTimeClockBreak(payload) {
  return fetchData("/time-clock/schedule-status/justify-break", "POST", payload);
}

export async function fetchTimeClockHoursBank({ userId, start, end } = {}) {
  const qs = new URLSearchParams();
  if (userId != null) qs.set("userId", String(userId));
  if (start) qs.set("start", start);
  if (end) qs.set("end", end);
  const q = qs.toString();
  return fetchData(`/time-clock/hours-bank${q ? `?${q}` : ""}`, "GET");
}

export async function fetchTimeClockEmployee(userId) {
  return fetchData(`/time-clock/employees/${Number(userId)}`, "GET");
}

export async function updateTimeClockEmployee(userId, payload) {
  return fetchData(`/time-clock/employees/${Number(userId)}`, "PUT", payload);
}

export async function fetchTimeClockDayLocks({ userId, start, end } = {}) {
  const qs = new URLSearchParams();
  if (userId != null) qs.set("userId", String(userId));
  if (start) qs.set("start", start);
  if (end) qs.set("end", end);
  const q = qs.toString();
  return fetchData(`/time-clock/day-locks${q ? `?${q}` : ""}`, "GET");
}

export async function lockTimeClockDay(payload) {
  return fetchData("/time-clock/day-locks", "POST", payload);
}

export async function unlockTimeClockDay({ userId, workDate }) {
  const qs = new URLSearchParams({
    userId: String(userId),
    workDate: String(workDate),
  });
  return fetchData(`/time-clock/day-locks?${qs}`, "DELETE");
}

export async function downloadTimeClockExport(kind, { userId, start, end }) {
  const qs = new URLSearchParams({ start, end });
  if (userId != null) qs.set("userId", String(userId));

  if (kind === "payroll-pdf" || kind === "pdf") {
    const res = await fetchData(`/time-clock/export/payroll-pdf?${qs}`, "GET");
    const payload = res?.data?.data || res?.data || {};
    const base64 = payload.base64;
    if (!base64) throw new Error("PDF não retornado pela API.");
    const binary = atob(String(base64));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    const filename = payload.filename || `ponto_${userId || "user"}_${start}_${end}.pdf`;
    const blob = new Blob([bytes], { type: payload.contentType || "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return { filename, bytes: bytes.length };
  }

  let path;
  let filename;
  if (kind === "afd") {
    path = `/time-clock/export/afd?${qs}`;
    filename = `afd_${userId || "user"}_${start}_${end}.txt`;
  } else if (kind === "aej") {
    path = `/time-clock/export/aej?${qs}`;
    filename = `aej_${userId || "user"}_${start}_${end}.txt`;
  } else if (kind === "afd-bulk") {
    path = `/time-clock/export/afd-bulk?${qs}`;
    filename = `afd_bulk_${start}_${end}.txt`;
  } else {
    path = `/time-clock/export/payroll?${qs}`;
    filename = `ponto_${userId || "user"}_${start}_${end}.txt`;
  }
  const response = await apiClient.get(path, { responseType: "text" });
  const data = response.data;
  const text = typeof data === "string" ? data : String(data || "");
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return { filename, bytes: text.length };
}

export async function setVideoRoomPrivacy(roomId, { isPrivate } = {}) {
  const res = await fetchData(`/video/rooms/${Number(roomId)}/privacy`, "PATCH", {
    isPrivate: Boolean(isPrivate),
  });
  return unwrapVideoPayload(res) ?? res;
}

export async function setPublicVideoRoomPrivacy(token, company, payload = {}) {
  const safe = encodeURIComponent(String(token || "").trim());
  const query = new URLSearchParams({ company: String(company || "").trim() }).toString();
  const { data } = await apiClient.patch(`/public/video/${safe}/privacy?${query}`, payload, {
    headers: { Company: company, "Content-Type": "application/json" },
  });
  return unwrapVideoPayload(data) ?? data?.data ?? data;
}

export async function endVideoRoom(roomId) {
  return fetchData(`/video/rooms/${Number(roomId)}/end`, "POST", {});
}

export async function handoffVideoRoom(roomId, { toUserId } = {}) {
  const res = await fetchData(`/video/rooms/${Number(roomId)}/handoff`, "POST", {
    toUserId: Number(toUserId),
  });
  return unwrapVideoPayload(res) ?? res;
}

export async function createGoogleMeetLink(payload) {
  const res = await fetchData("/video/google-meet", "POST", payload);
  return unwrapVideoPayload(res) ?? res;
}
